/**
 * Optimizations Analytics Tab — R1 (Workbench Foundation)
 *
 * Visual language ported from Claude Design (claude.ai/design) bundle:
 *   maghaweer/project/optimizations/Optimizations List.html
 *
 * Two views:
 *   1. List — Hero + sticky filter bar + By Run / By Combo scope tabs +
 *             paginated run cards or combo table + floating bulk-save bar.
 *   2. Detail — single run, with metric/parameter filter panel, combo table,
 *               multi-select bulk save to Analytics. (R1 styling kept;
 *               full design treatment deferred to R2.)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Plot from 'react-plotly.js';
import { tradingService } from '../../../services/tradingService';
import ChartsPanel from './ChartsPanel';
import ComputeQueuePanel from './ComputeQueuePanel';

// ─────────────────────────────────────────────────────────────────────────
// FORMATTERS
// ─────────────────────────────────────────────────────────────────────────

const fmtNum = (n, d = 2) => (n == null || isNaN(n) ? '—' : Number(n).toFixed(d));
const fmtPct = (n, d = 1) => (n == null || isNaN(n) ? '—' : `${n >= 0 ? '+' : ''}${Number(n).toFixed(d)}%`);
// Compact combo count for grid-tree headers: 11880000 → "11.88M", 240000 → "240K".
const fmtCombos = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${Math.round(v / 1e3)}K`;
  return String(v);
};
const valColor = (n) => (n == null || isNaN(n) ? 'text-gray-400' : n > 0 ? 'text-green-400' : n < 0 ? 'text-red-400' : 'text-gray-400');
const fmtAge = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
};

// ─────────────────────────────────────────────────────────────────────────
// VISUAL PRIMITIVES (ported from design components.jsx)
// ─────────────────────────────────────────────────────────────────────────

const RunKindIcon = ({ kind, className = 'w-3.5 h-3.5' }) => {
  const sw = 1.5;
  const stroke = 'currentColor';
  switch (kind) {
    case 'standard':
      return (
        <svg viewBox="0 0 16 16" className={className} fill="none" stroke={stroke} strokeWidth={sw}>
          <rect x="2" y="2" width="4" height="4" /><rect x="10" y="2" width="4" height="4" />
          <rect x="2" y="10" width="4" height="4" /><rect x="10" y="10" width="4" height="4" />
        </svg>
      );
    case 'random':
      return (
        <svg viewBox="0 0 16 16" className={className} fill="none" stroke={stroke} strokeWidth={sw}>
          <rect x="2" y="2" width="12" height="12" rx="2" />
          <circle cx="5" cy="5" r="0.8" fill={stroke} stroke="none" />
          <circle cx="8" cy="8" r="0.8" fill={stroke} stroke="none" />
          <circle cx="11" cy="11" r="0.8" fill={stroke} stroke="none" />
        </svg>
      );
    case 'focused':
      return (
        <svg viewBox="0 0 16 16" className={className} fill="none" stroke={stroke} strokeWidth={sw}>
          <circle cx="8" cy="8" r="6" /><circle cx="8" cy="8" r="3.5" />
          <circle cx="8" cy="8" r="1" fill={stroke} stroke="none" />
        </svg>
      );
    case 'walkforward':
      return (
        <svg viewBox="0 0 16 16" className={className} fill="none" stroke={stroke} strokeWidth={sw}>
          <circle cx="8" cy="8" r="6" /><path d="M8 4.5V8l2.5 1.5" strokeLinecap="round" />
        </svg>
      );
    case 'nsr':
      return (
        <svg viewBox="0 0 16 16" className={className} fill="none" stroke={stroke} strokeWidth={sw}>
          <circle cx="7" cy="7" r="4.5" /><path d="M11 11l3 3" strokeLinecap="round" />
        </svg>
      );
    case 'bayesian':
      return (
        <svg viewBox="0 0 16 16" className={className} fill="none" stroke={stroke} strokeWidth={sw}>
          <rect x="3" y="5" width="10" height="8" rx="1.5" />
          <path d="M8 2v3M5 2.5v0M11 2.5v0" strokeLinecap="round" />
          <circle cx="6.5" cy="9" r="0.7" fill={stroke} stroke="none" />
          <circle cx="9.5" cy="9" r="0.7" fill={stroke} stroke="none" />
        </svg>
      );
    default:
      return <span className="inline-block w-3.5 h-3.5" />;
  }
};

const Glyph = ({ name, className = 'w-4 h-4' }) => {
  const sw = 1.5;
  switch (name) {
    case 'plus':       return <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth={sw}><path d="M8 3v10M3 8h10" strokeLinecap="round"/></svg>;
    case 'chevron':    return <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth={sw}><path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case 'chevron-d':  return <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth={sw}><path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case 'sort':       return <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth={sw}><path d="M5 4v8m0 0l-2-2m2 2l2-2M11 12V4m0 0l-2 2m2-2l2 2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case 'info':       return <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth={sw}><circle cx="8" cy="8" r="6"/><path d="M8 7v4M8 5v0" strokeLinecap="round"/></svg>;
    case 'spinner':    return <svg viewBox="0 0 16 16" className={`${className} animate-spin`} fill="none" stroke="currentColor" strokeWidth={sw}><path d="M8 2a6 6 0 1 1-6 6" strokeLinecap="round"/></svg>;
    case 'check':      return <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth={sw}><path d="M3 8.5l3 3 7-7" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case 'x':          return <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth={sw}><path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round"/></svg>;
    case 'refresh':    return <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth={sw}><path d="M13 8a5 5 0 1 1-1.46-3.54M13 2v3h-3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    default:           return null;
  }
};

const STATUS_CLS = {
  complete: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30',
  running:  'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30',
  failed:   'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/30',
  pending:  'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30',
};
const STATUS_DOT = {
  complete: 'bg-green-400',
  running:  'bg-yellow-400 animate-pulse',
  failed:   'bg-gray-400',
  pending:  'bg-blue-400',
};

const StatusBadge = ({ status, label }) => {
  const cls = STATUS_CLS[status] || STATUS_CLS.failed;
  const dot = STATUS_DOT[status] || STATUS_DOT.failed;
  return (
    <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label || status}
    </span>
  );
};

// The raw `status` field only tracks the IS (in-sample) grid. A run can be
// IS-complete while OOS or NSR are still running, queued, or FAILED — e.g. a
// worker death mid-OOS leaves status='complete' + oos_status='running'/'failed'.
// Rendering a flat green "COMPLETE" then is misleading. Derive the controlling
// pipeline stage so the badge names exactly where the run actually is.
// Returns { state, label } where state keys the colour (complete/running/
// pending/failed) and label is the specific text shown.
function pipelineStage(run) {
  const { status, oos_status, nsr_status, oos_split_pct, is_end_date } = run;
  // IS grid not done → it controls the badge (running / pending / failed).
  if (status !== 'complete') return { state: status, label: status };
  // IS complete — walk the downstream pipeline (OOS, then NSR).
  // OOS is only EXPECTED when a split is configured AND the IS/OOS boundary was
  // actually set (is_end_date). A degenerate <30-bar IS window leaves is_end_date
  // unset and the run chains NSR directly — mirrors backend _opt_status_bucket.
  const oosExpected = (oos_split_pct || 0) > 0 && !!is_end_date;
  if (oosExpected) {
    if (oos_status === 'running')  return { state: 'running',  label: 'OOS running' };
    if (oos_status === 'pending')  return { state: 'pending',  label: 'OOS queued' };
    if (oos_status === 'failed')   return { state: 'failed',   label: 'OOS failed' };
    // pending / None / never-ran: OOS configured but NOT complete. The pipeline
    // is unfinished — must NOT read as the green "complete/IS complete" badge or
    // an IS-only run masquerades as fully validated. Show it as awaiting OOS.
    if (oos_status !== 'complete') return { state: 'pending', label: 'OOS pending' };
  }
  if (nsr_status === 'running')    return { state: 'running',  label: 'NSR running' };
  if (nsr_status === 'pending')    return { state: 'pending',  label: 'NSR queued' };
  if (nsr_status === 'failed')     return { state: 'failed',   label: 'NSR failed' };
  return { state: 'complete', label: 'complete' };
}

// Collapse the rich pipeline stage into the three buckets the by-run tree
// groups by (Completed / Failed / Running). Reuses pipelineStage so the bucket
// a run lands in always matches the status badge rendered on its card —
// e.g. a run that's IS-complete but OOS-failed buckets as 'failed', not
// 'completed'. 'running' absorbs both running + pending (queued/awaiting worker).
const statusBucketOf = (run) => {
  const st = pipelineStage(run).state;
  if (st === 'complete') return 'completed';
  if (st === 'failed')   return 'failed';
  return 'running';
};

// Presentation for the status level (Running / Completed / Failed status tiles).
const STATUS_GROUP_META = {
  completed: { label: 'Completed', glyph: '✓', tone: 'text-green-600 dark:text-green-400' },
  failed:    { label: 'Failed',    glyph: '✗', tone: 'text-red-600 dark:text-red-400' },
  running:   { label: 'Running',   glyph: '⏳', tone: 'text-yellow-600 dark:text-yellow-500' },
};

const IdBadge = ({ id, kind }) => (
  <div className="flex items-center gap-2 shrink-0">
    <div className="font-mono text-xs text-gray-500 dark:text-gray-500 tabular-nums">#{id}</div>
    <div className="flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 dark:border-gray-700/70 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/40">
      <RunKindIcon kind={kind} />
    </div>
  </div>
);

const ParamChip = ({ name, value }) => (
  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10.5px] text-gray-600 dark:text-gray-400 bg-gray-100/80 dark:bg-gray-800/60 whitespace-nowrap">
    <span className="text-gray-500 dark:text-gray-500">{name}</span>
    <span className="text-gray-400 dark:text-gray-600">=</span>
    <span className="text-gray-800 dark:text-gray-200 tabular-nums">{String(value)}</span>
  </span>
);

const ParamChips = ({ params, max }) => {
  if (!params) return null;
  const entries = Object.entries(params);
  const shown = max ? entries.slice(0, max) : entries;
  const overflow = entries.length - shown.length;
  return (
    <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
      {shown.map(([k, v]) => <ParamChip key={k} name={k} value={v} />)}
      {overflow > 0 && (
        <span className="px-1.5 py-0.5 text-[10.5px] text-gray-400 dark:text-gray-500 font-mono whitespace-nowrap">+{overflow} more</span>
      )}
    </div>
  );
};

const SharpeMiniBar = ({ value }) => {
  if (value == null || isNaN(value)) return <span className="text-gray-500 text-xs">—</span>;
  const pct = Math.max(0, Math.min(1, value / 3));
  const barColor = value >= 1 ? 'bg-green-400/80' : value >= 0 ? 'bg-gray-400/60' : 'bg-red-400/80';
  const txtColor = value > 1 ? 'text-green-400' : value > 0 ? 'text-gray-700 dark:text-gray-300' : 'text-red-400';
  return (
    <div className="flex items-center gap-2">
      <span className={`font-mono tabular-nums text-sm ${txtColor}`}>{value.toFixed(2)}</span>
      <div className="relative w-14 h-1 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${barColor}`} style={{ width: `${pct * 100}%` }} />
        <div className="absolute top-0 bottom-0 w-px bg-gray-400/40 dark:bg-gray-600/60" style={{ left: `${(1/3)*100}%` }} />
      </div>
    </div>
  );
};

const BlendedNote = ({ symbol, sharpe, baseSharpe }) => {
  if (!symbol || sharpe == null) return null;
  let delta = null;
  if (baseSharpe != null && baseSharpe !== 0) delta = (sharpe - baseSharpe) / Math.abs(baseSharpe);
  const sign = delta != null && delta > 0 ? '+' : '';
  const color = delta == null ? 'text-gray-400' : delta > 0 ? 'text-green-400' : delta < 0 ? 'text-red-400' : 'text-gray-400';
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-mono">
      <span className="text-blue-500 dark:text-blue-400/90">with {symbol}:</span>
      <span className="text-gray-700 dark:text-gray-300 tabular-nums">Sharpe {sharpe.toFixed(2)}</span>
      {delta != null && (
        <span className={`${color} tabular-nums`}>({sign}{(delta * 100).toFixed(0)}%)</span>
      )}
    </div>
  );
};

const IdleTag = ({ idle }) => {
  if (!idle || idle === 'cash') {
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-gray-100 dark:bg-gray-800/60 text-gray-500 dark:text-gray-500 border border-gray-200/60 dark:border-gray-700/40 whitespace-nowrap">idle: cash</span>;
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/30 whitespace-nowrap">
      idle: {idle}
    </span>
  );
};

const OosTag = ({ oosSplitPct, isEndDate }) => {
  if (!oosSplitPct) return null;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/30 whitespace-nowrap">
      OOS {oosSplitPct}%{isEndDate ? ` · IS≤${isEndDate}` : ''}
    </span>
  );
};

const FilterChip = ({ label, value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  // Click-outside via document listener — using a `position: fixed` backdrop
  // breaks here because the filter bar above is `sticky z-10`, which creates
  // a stacking context that traps the absolute dropdown panel. A fixed
  // backdrop escapes that context and ends up rendering ON TOP of the panel,
  // making the dropdown appear blank when clicked.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="group inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-gray-200 dark:border-gray-700/80 text-xs text-gray-600 dark:text-gray-300 hover:border-brand-gold/60 hover:text-brand-gold transition-colors whitespace-nowrap bg-white dark:bg-[#0d1117]"
      >
        <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-500 group-hover:text-brand-gold/70">{label}</span>
        <span className="text-gray-800 dark:text-gray-200 group-hover:text-brand-gold">{value || 'All'}</span>
        <Glyph name="chevron-d" className="w-3 h-3 text-gray-400" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 min-w-[180px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#161b22] shadow-lg py-1 max-h-64 overflow-y-auto scrollbar-thin">
          {options.map(opt => (
            <button
              key={String(opt.value)}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/40 ${opt.value === value ? 'text-brand-gold' : 'text-gray-700 dark:text-gray-300'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// HERO + ABOUT PANEL
// ─────────────────────────────────────────────────────────────────────────

const RUN_KINDS = [
  { kind: 'standard',    label: 'Standard Run',    available: true },
  { kind: 'random',      label: 'Random Search',   available: false },
  { kind: 'focused',     label: 'Focused Grid',    available: false },
  { kind: 'walkforward', label: 'Walk-Forward',    available: false },
  { kind: 'nsr',         label: 'NSR Scan',        available: false },
  { kind: 'bayesian',    label: 'Bayesian',        available: false },
];

// Universe shortcut chips for a Standard Run targeting a whole basket (sweep).
// Keys must match the backend FILTER_SHORTCUTS. 'us50' is derived (top-50 by
// live mcap over the S&P 500), realised server-side — kept last + distinct.
const UNIVERSE_SHORTCUTS = [
  { key: 'sp500', label: 'S&P 500' },
  { key: 'nasdaq100', label: 'Nasdaq 100' },
  { key: 'dow30', label: 'Dow 30' },
  { key: 'tech', label: 'Technology' },
  { key: 'finance', label: 'Financials' },
  { key: 'healthcare', label: 'Healthcare' },
  { key: 'energy', label: 'Energy' },
  { key: 'industrials', label: 'Industrials' },
  { key: 'consumer', label: 'Consumer Cyc.' },
  { key: 'etfs', label: 'ETFs' },
  { key: 'us50', label: 'US 50', derived: true },
];

const Hero = ({ onLaunch, onAbout, aboutOpen }) => (
  <div className="px-3 md:px-5 pt-5 pb-4 border-b border-gray-200 dark:border-gray-800">
    <div className="flex items-baseline gap-3 flex-wrap">
      <h1 className="font-serif text-3xl md:text-4xl text-gray-900 dark:text-gray-50 leading-none">Optimizations</h1>
      <span className="text-[10px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-500">
        Parameter Grid Exploration · Research Workbench
      </span>
    </div>
    <div className="mt-4 flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap">
        {RUN_KINDS.map(k => (
          <button
            key={k.kind}
            onClick={() => onLaunch(k.kind, k.available)}
            disabled={!k.available}
            title={k.available ? `Launch ${k.label}` : 'Coming soon — start from chat for now'}
            className={`group inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs transition-colors whitespace-nowrap ${
              k.available
                ? 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-gold/60 hover:text-brand-gold hover:bg-brand-gold/5 cursor-pointer'
                : 'border-gray-200/60 dark:border-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60'
            }`}
          >
            <span className={k.available ? 'text-gray-400 dark:text-gray-500 group-hover:text-brand-gold/80' : 'text-gray-300 dark:text-gray-700'}>
              <RunKindIcon kind={k.kind} className="w-3.5 h-3.5" />
            </span>
            <Glyph name="plus" className="w-2.5 h-2.5 opacity-50" />
            <span>{k.label}</span>
          </button>
        ))}
      </div>
      <div className="ml-auto">
        <button
          onClick={onAbout}
          className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-brand-gold whitespace-nowrap"
        >
          <Glyph name="info" className="w-3.5 h-3.5" />
          {aboutOpen ? 'Hide explainer' : 'About this tab'}
        </button>
      </div>
    </div>
  </div>
);

const ABOUT_ITEMS = [
  { kind: 'standard',    title: 'Standard Run',    body: 'Full grid sweep across every parameter combination you specify. Best when the search space is small (<1k combos).' },
  { kind: 'random',      title: 'Random Search',   body: 'Sample N combos uniformly from a wider range. Better than full grid when dimensions explode.' },
  { kind: 'focused',     title: 'Focused Grid',    body: 'Re-grid finely around a known peak. Pair with NSR results to confirm a plateau.' },
  { kind: 'walkforward', title: 'Walk-Forward',    body: 'Rolling IS/OOS folds, scored by walk-forward efficiency.' },
  { kind: 'nsr',         title: 'NSR Scan',        body: 'Neighborhood Sharpe robustness — penalize fragile peaks.' },
  { kind: 'bayesian',    title: 'Bayesian',        body: 'Gaussian-process surrogate model over the param space.' },
];

const AboutPanel = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="px-3 md:px-5 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/20">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Run Kinds</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {ABOUT_ITEMS.map(it => (
              <div key={it.kind} className="flex gap-2.5">
                <div className="shrink-0 w-7 h-7 rounded-md border border-gray-200 dark:border-gray-700/70 text-gray-500 dark:text-gray-400 bg-white dark:bg-[#161b22] flex items-center justify-center">
                  <RunKindIcon kind={it.kind} />
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-800 dark:text-gray-200">{it.title}</div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">{it.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-brand-gold p-1">
          <Glyph name="x" className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// FILTER BAR
// ─────────────────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: '-created_at',          label: 'Latest first' },
  { value: 'created_at',           label: 'Oldest first' },
  { value: '-best_sharpe',         label: 'Best Sharpe' },
  { value: '-best_blended_sharpe', label: 'Best Blended Sharpe' },
  { value: '-best_return',         label: 'Best Return' },
  { value: '-total_combos',        label: 'Largest grid' },
];

const FilterBar = ({
  filters, setFilter, sort, setSort,
  page, total, perPage, setPage,
  symbolOptions, strategyOptions, idleOptions,
  loading, showPager = true,
}) => {
  const start = total === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);

  const statusOptions = [
    { value: '',         label: 'All' },
    { value: 'complete', label: 'Complete' },
    { value: 'running',  label: 'Running' },
    { value: 'pending',  label: 'Pending' },
    { value: 'failed',   label: 'Failed' },
  ];
  const dateOptions = [
    { value: '',     label: 'All time' },
    { value: '7d',   label: 'Last 7 days' },
    { value: '30d',  label: 'Last 30 days' },
    { value: '90d',  label: 'Last 90 days' },
    { value: '365d', label: 'Last year' },
  ];

  const sortLabel = SORT_OPTIONS.find(s => s.value === sort)?.label || 'Latest first';

  return (
    <div className="sticky top-0 z-10 bg-white/85 dark:bg-[#0d1117]/85 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
      <div className="px-3 md:px-5 py-2.5 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin pb-0.5 -mb-0.5 flex-1 min-w-0">
          <FilterChip
            label="Symbol"
            value={filters.symbol || 'All'}
            onChange={(v) => setFilter('symbol', v)}
            options={[{ value: '', label: 'All' }, ...symbolOptions.map(s => ({ value: s, label: s }))]}
          />
          <FilterChip
            label="Strategy"
            value={filters.strategy || 'All'}
            onChange={(v) => setFilter('strategy', v)}
            options={[{ value: '', label: 'All' }, ...strategyOptions.map(s => ({ value: s, label: s }))]}
          />
          <FilterChip
            label="Idle"
            value={filters.idle || 'All'}
            onChange={(v) => setFilter('idle', v)}
            options={[{ value: '', label: 'All' }, ...idleOptions.map(s => ({ value: s, label: s }))]}
          />
          <FilterChip
            label="Status"
            value={statusOptions.find(o => o.value === filters.status)?.label || 'All'}
            onChange={(v) => setFilter('status', v)}
            options={statusOptions}
          />
          <FilterChip
            label="Date"
            value={dateOptions.find(o => o.value === filters.date)?.label || 'All time'}
            onChange={(v) => setFilter('date', v)}
            options={dateOptions}
          />
        </div>

        <div className="flex items-center gap-3">
          <FilterChip
            label="Sort by"
            value={sortLabel}
            onChange={setSort}
            options={SORT_OPTIONS}
          />
          {showPager ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              <span className="tabular-nums">{start}–{end}</span>
              <span className="text-gray-400 dark:text-gray-600">of</span>
              <span className="tabular-nums text-gray-700 dark:text-gray-300">
                {loading ? '…' : total}
              </span>
              <div className="flex items-center ml-1">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="px-1.5 py-0.5 rounded hover:text-brand-gold disabled:opacity-30 disabled:hover:text-gray-500"
                >
                  <Glyph name="chevron" className="w-3.5 h-3.5 rotate-180" />
                </button>
                <button
                  disabled={end >= total}
                  onClick={() => setPage(page + 1)}
                  className="px-1.5 py-0.5 rounded hover:text-brand-gold disabled:opacity-30 disabled:hover:text-gray-500"
                >
                  <Glyph name="chevron" className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              <span className="text-gray-400 dark:text-gray-600">total</span>
              <span className="tabular-nums text-gray-700 dark:text-gray-300">{loading ? '…' : total}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// SCOPE TABS (By Run / By Combo)
// ─────────────────────────────────────────────────────────────────────────

const ScopeTabs = ({ scope, setScope, byRunCount, byComboCount, total }) => (
  <div className="px-3 md:px-5 pt-3 flex items-center gap-1 border-b border-gray-200 dark:border-gray-800">
    {[
      { key: 'by-run',    label: 'By Run',    sub: `${byRunCount} runs` },
      { key: 'by-combo',  label: 'By Combo',  sub: byComboCount != null ? `${byComboCount.toLocaleString()} combos` : '—' },
      { key: 'by-bucket', label: 'By Bucket', sub: 'config-classes' },
    ].map(t => {
      const active = scope === t.key;
      return (
        <button
          key={t.key}
          onClick={() => setScope(t.key)}
          className={`px-3 py-2 text-sm whitespace-nowrap transition-colors flex items-center gap-1.5 ${
            active
              ? 'border-b-2 border-brand-gold text-brand-gold'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border-b-2 border-transparent'
          }`}
        >
          {t.label}
          <span className={`text-[10px] tabular-nums ${active ? 'text-brand-gold/60' : 'text-gray-400 dark:text-gray-600'}`}>
            {t.sub}
          </span>
        </button>
      );
    })}
    {scope === 'by-run' && (
      <div className="ml-auto pb-1.5 text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-600 hidden md:block">
        live counter · {byRunCount} of {total} matching filters
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// RUN ROW (By Run card)
// ─────────────────────────────────────────────────────────────────────────

const Stat = ({ label, value, valueNode }) => (
  <div className="flex items-center gap-2">
    <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-500">{label}</span>
    {valueNode || <span className="font-mono tabular-nums text-sm text-gray-700 dark:text-gray-200">{value}</span>}
  </div>
);

// Click-to-sort header. Each click on the same field flips direction;
// clicking a different field uses its default direction (DESC for "higher
// is better" metrics, ASC for max_drawdown / blended_max_drawdown where
// "less negative is better"). Backend ORDER BY runs across the FULL grid
// (e.g. all 840K combos) and returns the page — so sort scope is global,
// not just visible rows.
const SortableTh = ({ field, filters, setFilters, children, blue, ascendingDefault }) => {
  const currentField = filters.sort.replace(/^-/, '');
  const isCurrent = currentField === field;
  const isDesc = filters.sort.startsWith('-');
  const handleClick = () => {
    let newSort;
    if (isCurrent) {
      newSort = isDesc ? field : `-${field}`;
    } else {
      newSort = ascendingDefault ? field : `-${field}`;
    }
    setFilters(f => ({ ...f, sort: newSort }));
  };
  const colorBase = blue ? 'text-blue-500 dark:text-blue-400' : '';
  const activeColor = isCurrent ? 'text-brand-gold' : colorBase;
  return (
    <th className={`px-2 py-2 text-right select-none ${colorBase}`}>
      <button
        onClick={handleClick}
        className={`inline-flex items-center gap-1 hover:text-brand-gold transition-colors ${activeColor}`}
        title={`Sort by ${field.replace(/_/g, ' ')}`}
      >
        {children}
        {isCurrent ? (
          <span className="text-brand-gold text-[9px]">{isDesc ? '▼' : '▲'}</span>
        ) : (
          <span className="opacity-30 text-[9px]">⇅</span>
        )}
      </button>
    </th>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// REPLICATE MODAL — same param_ranges, new symbol(s)
// ─────────────────────────────────────────────────────────────────────────

const ReplicateModal = ({ run, onClose, onLaunched, onBatchLaunched, showToast }) => {
  const [config, setConfig]       = useState(null);   // full run detail (has param_ranges)
  const [loadErr, setLoadErr]     = useState('');
  const [symbols, setSymbols]     = useState('');     // comma-separated
  const [startDate, setStartDate] = useState(run.start_date || '2020-01-01');
  const [launching, setLaunching] = useState(false);
  const [launchErr, setLaunchErr] = useState('');
  const [launched, setLaunched]   = useState([]);     // array of {symbol, id, deduped}
  const [pinPeriod, setPinPeriod] = useState(true);   // default ON — copy runs the SAME pinned window
  const [priceSource, setPriceSource] = useState('legacy'); // 'legacy' (vendor adj_close) | 'raw' (RawDailyPrice TR ledger)
  const [endDate, setEndDate] = useState('');   // '' until config loads → defaults to source OOS end; edit = custom cutoff

  // ── Universe (basket sweep) target ─────────────────────────────────────────
  // A replicate can target a comma-separated list of symbols (one /backtest call
  // each, with dedup + pinned window) OR a whole universe sweep: the SAME LOCKED
  // source param_ranges fan across every resolved symbol via /optimize-batch/.
  const [target, setTarget]               = useState('single');  // 'single' | 'universe'
  const [uniShortcuts, setUniShortcuts]   = useState([]);
  const [uniText, setUniText]             = useState('');
  const [mcapMinBn, setMcapMinBn]         = useState('');
  const [topNPerSector, setTopNPerSector] = useState('');
  const [inflightCap, setInflightCap]     = useState(2);
  const [batchPreview, setBatchPreview]   = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [batchLaunched, setBatchLaunched] = useState(null);     // {batch_id, total_symbols, name}
  const isUniverse = target === 'universe';
  const pastedSymbols = uniText
    .split(/[\s,;\n]+/).map(s => s.trim().toUpperCase()).filter(Boolean);
  const refineFilters = useMemo(() => {
    const r = {};
    const mc = parseFloat(mcapMinBn);
    if (!Number.isNaN(mc) && mc > 0) r.market_cap_min = Math.round(mc * 1e9);
    const tn = parseInt(topNPerSector, 10);
    if (!Number.isNaN(tn) && tn > 0) r.top_n_per_sector = tn;
    return r;
  }, [mcapMinBn, topNPerSector]);

  // Fetch full config (includes param_ranges) when modal opens
  useEffect(() => {
    tradingService.getOptimizationStatus(run.id)
      .then(r => {
        setConfig(r.data);
        if (r.data.start_date) setStartDate(r.data.start_date);
      })
      .catch(() => setLoadErr('Failed to load run config. Please try again.'));
  }, [run.id]);

  // Pinned window carried by a copy: the IS/OOS split actually used by the source run,
  // and the OOS end (intended, else the data cutoff). These pin the window so every copy
  // runs the identical date range instead of re-deriving a per-symbol split.
  const pinnedIsEnd  = config?.intended_is_end || config?.is_end_date || '';
  const pinnedOosEnd = config?.intended_oos_end || config?.oos_end_date || config?.data_end_date || '';

  // Editable cutoff: default to the source's OOS end once config loads. `effEndDate`
  // is what we send. When the user changes it off the source window, the copy is
  // "customized" — we drop the pin so the backend derives IS/OOS by the split-%
  // formula over [start, end] and stamps intended_oos_end = end (a NEW config-class,
  // fresh runs, no dedup-skip). Empty end keeps the source's pinned window.
  useEffect(() => { if (pinnedOosEnd && !endDate) setEndDate(pinnedOosEnd); }, [pinnedOosEnd]);  // eslint-disable-line react-hooks/exhaustive-deps
  const effEndDate = endDate || pinnedOosEnd || '';
  const windowCustomized = !!(effEndDate && pinnedOosEnd && effEndDate !== pinnedOosEnd);

  const paramRanges = config?.param_ranges || {};
  const paramSummary = Object.entries(paramRanges).map(([k, vals]) => {
    if (!vals || vals.length === 0) return `${k}: —`;
    const boolVals = vals.filter(v => typeof v === 'boolean');
    const numVals  = vals.filter(v => typeof v === 'number').sort((a, b) => a - b);
    if (boolVals.length > 0) return `${k}: ${vals.map(String).join('/')}`;
    if (numVals.length === 1) return `${k}: ${numVals[0]}`;
    if (numVals.length <= 4) return `${k}: ${numVals.join(', ')}`;
    return `${k}: ${numVals[0]}→${numVals[numVals.length - 1]} (${numVals.length} vals)`;
  });

  const handleLaunch = async () => {
    const symbolList = symbols.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    if (!symbolList.length) { setLaunchErr('Enter at least one symbol.'); return; }
    if (!config?.param_ranges) { setLaunchErr('Config not loaded yet.'); return; }
    setLaunching(true);
    setLaunchErr('');
    const results = [];
    for (const sym of symbolList) {
      try {
        const payload = {
          mode:             'optimize',
          strategy:         run.strategy_name,
          symbol:           sym,
          start_date:       startDate,
          param_ranges:     config.param_ranges,
          maximize:         run.maximize || 'Sharpe Ratio',
          idle_deployment:  run.idle_deployment || 'cash',
          oos_split_pct:    run.oos_split_pct || 0,
          // Price-series provenance for this copy. 'raw' reads the immutable
          // RawDailyPrice total-return ledger (active-strategy spine only);
          // otherwise fall through to the source run's vendor series.
          source:           priceSource === 'raw'
            ? 'raw'
            : (config?.source || run?.source || 'consolidated'),
          // Cutoff: user picks Start + End; the backend derives the IS/OOS split by
          // the formula and bounds data at End. Off the source window → fresh run.
          end_date:         effEndDate || undefined,
        };
        // Pin the source window so the copy runs the identical IS/OOS range
        // (and so an exact re-copy onto the same symbol dedups against the
        // existing complete run instead of recomputing).
        if (pinPeriod && pinnedIsEnd && !windowCustomized) {
          payload.pin_period   = true;
          payload.is_end_date  = pinnedIsEnd;
          payload.oos_end_date = pinnedOosEnd;
        }
        const res = await tradingService.runBacktest(payload);
        const deduped = !!res.data?.deduped;
        results.push({ symbol: sym, id: res.data?.optimization_id, deduped });
        showToast(deduped
          ? `Run #${res.data?.optimization_id} reused for ${sym} (no recompute)`
          : `Run #${res.data?.optimization_id} queued for ${sym}`);
      } catch (e) {
        results.push({ symbol: sym, error: e.response?.data?.error || 'Launch failed' });
      }
    }
    setLaunched(results);
    setLaunching(false);
    const successIds = results.filter(r => r.id).map(r => r.id);
    if (successIds.length) onLaunched(successIds[successIds.length - 1]);
  };

  // Debounced dry-run preview (universe mode): resolve count + combos + unknowns
  // + sector breakdown WITHOUT creating anything, using the source run's LOCKED
  // param_ranges so the scale is visible before committing the sweep.
  useEffect(() => {
    if (!isUniverse || !config?.param_ranges) { setBatchPreview(null); return; }
    if (uniShortcuts.length === 0 && pastedSymbols.length === 0) {
      setBatchPreview(null); return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const payload = {
          dry_run: true,
          strategy: run.strategy_name,
          universe: { shortcuts: uniShortcuts, symbols: pastedSymbols, ...refineFilters },
          param_ranges: config.param_ranges,
          maximize:        run.maximize || 'Sharpe Ratio',
          start_date:      startDate,
          idle_deployment: run.idle_deployment || 'cash',
          oos_split_pct:   run.oos_split_pct || 0,
          // Match the launch rail so the "already covered" dedup count is computed
          // against the SAME source (dedup_key includes source).
          source:          priceSource === 'raw'
            ? 'raw'
            : (config?.source || run?.source || 'consolidated'),
          end_date:        effEndDate || undefined,
        };
        // Pin the source window so the preview can report how many symbols are
        // ALREADY covered by an identical completed run (skipped, not recomputed).
        if (pinPeriod && pinnedIsEnd && !windowCustomized) {
          payload.pin_period   = true;
          payload.is_end_date  = pinnedIsEnd;
          payload.oos_end_date = pinnedOosEnd;
        }
        const res = await tradingService.runOptimizeBatch(payload);
        if (!cancelled) setBatchPreview(res.data);
      } catch (e) {
        if (!cancelled) setBatchPreview({ error: e.response?.data?.error || 'Preview failed' });
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUniverse, uniShortcuts, uniText, config, refineFilters, run.strategy_name,
      pinPeriod, pinnedIsEnd, pinnedOosEnd, startDate, priceSource, effEndDate, windowCustomized]);

  // Universe sweep launch: fan the source run's LOCKED param_ranges across every
  // resolved symbol via /optimize-batch/ (one batch, metered to the spot worker).
  const handleLaunchBatch = async () => {
    if (!config?.param_ranges) { setLaunchErr('Config not loaded yet.'); return; }
    if (!(batchPreview?.total_symbols > 0)) {
      setLaunchErr('No symbols resolved — pick a universe first.'); return;
    }
    setLaunching(true);
    setLaunchErr('');
    try {
      const payload = {
        strategy:        run.strategy_name,
        universe:        { shortcuts: uniShortcuts, symbols: pastedSymbols, ...refineFilters },
        param_ranges:    config.param_ranges,
        maximize:        run.maximize || 'Sharpe Ratio',
        start_date:      startDate,
        idle_deployment: run.idle_deployment || 'cash',
        oos_split_pct:   run.oos_split_pct || 0,
        inflight_cap:    inflightCap,
        // Single-switch: thread the chosen rail to every member run (P&L + factor
        // feed). Without this the batch silently defaulted to 'consolidated'.
        source:          priceSource === 'raw'
          ? 'raw'
          : (config?.source || run?.source || 'consolidated'),
        end_date:        effEndDate || undefined,
      };
      // Pin the source window so already-covered symbols are skipped (not
      // recomputed) and the new members forward-stamp the matching dedup key.
      if (pinPeriod && pinnedIsEnd && !windowCustomized) {
        payload.pin_period   = true;
        payload.is_end_date  = pinnedIsEnd;
        payload.oos_end_date = pinnedOosEnd;
      }
      const res = await tradingService.runOptimizeBatch(payload);
      if (res.data?.success && res.data.batch_id == null) {
        // Every resolved symbol already has an identical completed run — nothing
        // launched. Inform without spawning a phantom batch tile.
        showToast(res.data.message
          || `All ${res.data.total_symbols} symbol(s) already covered — nothing to launch.`);
        setBatchLaunched({
          batch_id: null,
          total_symbols: res.data.total_symbols,
          skipped_existing: res.data.skipped_existing,
          name: `${run.strategy_name} sweep`,
        });
      } else if (res.data?.success) {
        const created = res.data.created ?? res.data.total_symbols;
        const skipped = res.data.skipped_existing || 0;
        showToast(
          `Sweep #${res.data.batch_id} launched · ${created} symbol(s)`
          + (skipped ? ` · ${skipped} already covered` : '')
          + ` · ${inflightCap} at a time`
        );
        setBatchLaunched({
          batch_id: res.data.batch_id,
          total_symbols: created,
          skipped_existing: skipped,
          name: res.data.name || `${run.strategy_name} sweep`,
        });
        onBatchLaunched && onBatchLaunched({
          id: res.data.batch_id,
          status: 'queued',
          counts: {},
          members: [],
          total_symbols: created,
          pct_complete: 0,
          name: res.data.name || `${run.strategy_name} sweep`,
        });
      } else {
        setLaunchErr(res.data?.error || 'Failed to launch sweep');
      }
    } catch (e) {
      setLaunchErr(e.response?.data?.error || e.message || 'Sweep launch failed');
    } finally {
      setLaunching(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg bg-white dark:bg-[#0d1117] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 className="font-serif text-base text-gray-900 dark:text-gray-100">
              {isUniverse ? 'Replicate across a universe' : 'Replicate on new symbol'}
            </h2>
            <p className="text-[11px] text-gray-500 mt-0.5 font-mono">
              {run.strategy_name} · same param ranges · {isUniverse ? 'universe sweep' : 'new symbol'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none">×</button>
        </div>

        {/* Target toggle: replicate onto a comma-separated symbol list (one /backtest
            each, with dedup + pinned window) OR fan this exact LOCKED config across a
            whole universe (basket sweep) via /optimize-batch/. */}
        <div className="px-5 pt-3 pb-1 border-b border-gray-200 dark:border-gray-800">
          <div className="inline-flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            {[['single', 'Symbol list'], ['universe', 'Universe Sweep']].map(([t, lbl]) => (
              <button
                key={t}
                type="button"
                onClick={() => { setTarget(t); setLaunchErr(''); }}
                disabled={launched.length > 0 || !!batchLaunched}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  target === t
                    ? 'bg-white dark:bg-[#0d1117] text-brand-gold shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {/* Param range summary */}
          {!config && !loadErr && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-brand-gold border-t-transparent" />
            </div>
          )}
          {loadErr && <p className="text-xs text-red-400">{loadErr}</p>}
          {config && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Param Ranges (locked)</p>
              <div className="flex flex-wrap gap-1.5">
                {paramSummary.map((s, i) => (
                  <span key={i} className="text-[11px] font-mono px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-md">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* New symbol(s) — single (symbol-list) mode */}
          {!isUniverse && (
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">
                New Symbol(s) — comma-separated for multiple
              </label>
              <input
                type="text"
                value={symbols}
                onChange={e => setSymbols(e.target.value.toUpperCase())}
                placeholder="e.g. MSFT, NVDA, TSLA"
                autoFocus
                className="w-full bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 font-mono outline-none focus:ring-2 focus:ring-brand-gold/30"
              />
            </div>
          )}

          {/* Universe selector — Universe Sweep mode. Fans the SAME LOCKED source
              param_ranges across every resolved symbol, metered to the spot worker. */}
          {isUniverse && config && (
            <div className="rounded-lg border border-brand-gold/30 bg-brand-gold/5 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Universe — the locked config above fans across every resolved symbol
              </div>

              {/* Shortcut chips (multi-select, unioned) */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {UNIVERSE_SHORTCUTS.map(sc => {
                  const on = uniShortcuts.includes(sc.key);
                  return (
                    <button
                      key={sc.key}
                      type="button"
                      onClick={() => setUniShortcuts(prev =>
                        on ? prev.filter(k => k !== sc.key) : [...prev, sc.key])}
                      className={`px-2.5 py-1 text-[11px] font-medium rounded-full border transition-colors ${
                        on
                          ? 'bg-brand-gold text-white border-brand-gold'
                          : 'bg-white dark:bg-[#0d1117] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:border-brand-gold'
                      }`}
                    >
                      {sc.label}
                    </button>
                  );
                })}
              </div>

              {/* Paste-a-list */}
              <label className="text-[10px] text-gray-400 mb-1 block">
                Or paste tickers (comma / space / newline separated) — unioned with chips
              </label>
              <textarea
                className="w-full h-16 px-2 py-1.5 text-xs font-mono rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                value={uniText}
                onChange={e => setUniText(e.target.value)}
                placeholder="AAPL, MSFT, NVDA&#10;TSLA GOOGL"
              />

              {/* Refinement — on-demand live market cap (FMP). Empty = off. */}
              <div className="mt-3 pt-3 border-t border-brand-gold/20 grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-gray-400 mb-1 block">Min market cap ($bn)</label>
                  <input
                    type="number" min={0} step={10} placeholder="e.g. 100"
                    className="w-full px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                    value={mcapMinBn}
                    onChange={e => setMcapMinBn(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 mb-1 block">Top N by cap / sector</label>
                  <input
                    type="number" min={0} step={1} placeholder="e.g. 5"
                    className="w-full px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                    value={topNPerSector}
                    onChange={e => setTopNPerSector(e.target.value)}
                  />
                </div>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">
                Current cap fetched live (not stored). Symbols with no cap data are dropped from cap/sector filters.
              </p>

              {/* Concurrency */}
              <div className="mt-3 flex items-center gap-2">
                <label className="text-[10px] uppercase tracking-wider text-gray-500">Concurrent runs</label>
                <input
                  type="number" min={1} max={8}
                  className="w-16 px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                  value={inflightCap}
                  onChange={e => setInflightCap(Math.max(1, Math.min(8, parseInt(e.target.value) || 2)))}
                />
                <span className="text-[10px] text-gray-400">
                  members dispatched to the one spot worker at once (each fans 8 IS slices — 2 is the sweet spot)
                </span>
              </div>

              {/* Resolved-count preview */}
              {previewLoading && (
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
                  <Glyph name="spinner" className="w-3.5 h-3.5" /> Resolving universe…
                </div>
              )}
              {batchPreview && !previewLoading && !batchPreview.error && (
                <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0d1117] p-3">
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div>
                      <span className="text-gray-500 block">Symbols</span>
                      <span className="text-base font-semibold text-brand-gold">{batchPreview.total_symbols?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Combos / symbol</span>
                      <span className="text-base font-semibold text-gray-800 dark:text-gray-200">{batchPreview.combos_per_member?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Total combos</span>
                      <span className="text-base font-semibold text-gray-800 dark:text-gray-200">{batchPreview.estimated_total_combos?.toLocaleString()}</span>
                    </div>
                  </div>
                  {batchPreview.deduped && batchPreview.skipped_existing > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 flex items-center gap-3 text-xs">
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                        {batchPreview.to_run?.toLocaleString()} to run
                      </span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500">
                        {batchPreview.skipped_existing?.toLocaleString()} already covered (skipped, no recompute)
                      </span>
                    </div>
                  )}
                  {batchPreview.refined && batchPreview.sector_breakdown
                    && Object.keys(batchPreview.sector_breakdown).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">By sector</span>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(batchPreview.sector_breakdown)
                          .sort((a, b) => b[1] - a[1])
                          .map(([sec, n]) => (
                            <span key={sec} className="px-2 py-0.5 text-[10px] rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                              {sec} · {n}
                            </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {batchPreview.dropped_no_mcap?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-[11px] text-gray-500">
                      {batchPreview.dropped_no_mcap.length} symbol(s) dropped — no market-cap data from FMP.
                    </div>
                  )}
                  {batchPreview.unknown_symbols?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-[11px] text-orange-600 dark:text-orange-400">
                      {batchPreview.unknown_symbols.length} unknown ticker(s) skipped: {batchPreview.unknown_symbols.slice(0, 12).join(', ')}{batchPreview.unknown_symbols.length > 12 ? '…' : ''}
                    </div>
                  )}
                </div>
              )}
              {batchPreview?.error && !previewLoading && (
                <div className="mt-3 text-xs text-red-600 dark:text-red-400">{batchPreview.error}</div>
              )}
            </div>
          )}

          {/* Start date */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="w-full bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30"
            />
          </div>

          {/* End date (cutoff) — user picks Start + End; the OOS split % derives the
              IS/OOS boundary via the formula. Off the source window → fresh run(s) in
              a NEW config-class (no dedup-skip). */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">End Date (cutoff)</label>
            <input
              type="date"
              value={effEndDate}
              onChange={e => setEndDate(e.target.value)}
              className="w-full bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30"
            />
            <span className="block text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-1">
              IS/OOS split computed at {run.oos_split_pct || 0}% of [start, end].{' '}
              {windowCustomized
                ? <span className="text-brand-gold">Custom window → fresh run(s), new bucket (no dedup-skip). Data after {effEndDate} stays out-of-sample.</span>
                : 'Matches the source window — already-covered symbols are skipped.'}
            </span>
          </div>

          {/* Pinned period toggle — single (symbol-list) mode only; hidden once the
              window is customized (a custom cutoff can't dedup against the source). */}
          {!isUniverse && pinnedIsEnd && !windowCustomized && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pinPeriod}
                  onChange={e => setPinPeriod(e.target.checked)}
                  className="mt-0.5 accent-brand-gold w-4 h-4 shrink-0"
                />
                <span className="text-[11px] leading-snug">
                  <span className="text-gray-700 dark:text-gray-200 font-medium">Use same date range (pinned)</span>
                  <span className="block text-gray-500 dark:text-gray-400 mt-0.5">
                    IS/OOS split <span className="font-mono text-gray-600 dark:text-gray-300">{pinnedIsEnd}</span>
                    {pinnedOosEnd && <> · OOS end <span className="font-mono text-gray-600 dark:text-gray-300">{pinnedOosEnd}</span></>}.
                    Symbols with shorter history use available data for the split but keep the intended window.
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* Price series toggle — shown in BOTH single and universe modes so a
              sweep can also run the raw total-return ledger. Single-switch: the
              batch threads `source` to every member run (P&L + factor feed), so a
              raw sweep is never scored against a consolidated factor basket.
              Legacy = vendor split+dividend adjusted close (works for any ticker).
              Raw    = immutable RawDailyPrice total-return ledger. */}
          {(
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5">
              <span className="block text-[11px] font-medium text-gray-700 dark:text-gray-200 mb-1.5">Price series</span>
              <div className="inline-flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
                {[
                  { key: 'legacy', label: 'Legacy' },
                  { key: 'raw',    label: 'Raw (TR ledger)' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setPriceSource(opt.key)}
                    className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${
                      priceSource === opt.key
                        ? 'bg-brand-gold text-black'
                        : 'bg-gray-50 dark:bg-[#161b22] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <span className="block text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-1.5">
                {priceSource === 'raw'
                  ? 'Reads the immutable total-return ledger. A symbol (or non-cash idle ETF) outside the ledger is rejected. Factor-basket strategies (e.g. relative-momentum) also need the raw factor basket covered for the window — symbols without raw coverage are rejected, never silently degraded to consolidated. In a universe sweep, uncovered names are skipped/failed per-symbol; the rest run raw.'
                  : 'Vendor split + dividend adjusted close (default). Works for any ticker with sufficient history.'}
              </span>
            </div>
          )}

          {/* Universe mode: the window is ALWAYS pinned to the source (the dedup
              skip of already-covered symbols depends on it), so show it read-only
              instead of a toggle — including the inherited end date. */}
          {isUniverse && pinnedIsEnd && !windowCustomized && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5">
              <span className="text-[11px] leading-snug">
                <span className="text-gray-700 dark:text-gray-200 font-medium">Same date range as source (pinned)</span>
                <span className="block text-gray-500 dark:text-gray-400 mt-0.5">
                  Every symbol runs the source run's identical window — start{' '}
                  <span className="font-mono text-gray-600 dark:text-gray-300">{startDate}</span> · IS/OOS split{' '}
                  <span className="font-mono text-gray-600 dark:text-gray-300">{pinnedIsEnd}</span>
                  {pinnedOosEnd && <> · end <span className="font-mono text-gray-600 dark:text-gray-300">{pinnedOosEnd}</span></>}.
                  This fixed window is what lets already-covered symbols be skipped (no recompute).
                </span>
              </span>
            </div>
          )}

          {/* Universe mode but source has no recorded window end → can't pin.
              Be explicit that the sweep runs to the latest data and nothing is skipped. */}
          {isUniverse && !pinnedIsEnd && (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/15 px-3 py-2.5">
              <span className="text-[11px] leading-snug text-amber-700 dark:text-amber-300">
                Source run has no recorded window end, so this sweep can't pin its dates —
                each symbol runs from <span className="font-mono">{startDate}</span> to the latest available
                data (today), and no symbols can be skipped as already-covered.
              </span>
            </div>
          )}

          {/* Inherited settings */}
          <div className="flex gap-3 text-[11px] text-gray-500 dark:text-gray-400 flex-wrap">
            <span>Maximize: <span className="text-gray-700 dark:text-gray-300">{run.maximize || 'Sharpe Ratio'}</span></span>
            <span>Idle: <span className="text-gray-700 dark:text-gray-300">{run.idle_deployment || 'cash'}</span></span>
            <span>OOS: <span className="text-gray-700 dark:text-gray-300">{run.oos_split_pct || 0}%</span></span>
            <span>Combos/symbol: <span className="text-gray-700 dark:text-gray-300">{(run.total_combos || 0).toLocaleString()}</span></span>
          </div>

          {/* Errors */}
          {launchErr && <p className="text-xs text-red-400">{launchErr}</p>}

          {/* Results after launch — symbol-list mode */}
          {launched.length > 0 && (
            <div className="space-y-1">
              {launched.map((r, i) => (
                <div key={i} className={`text-xs font-mono px-3 py-1.5 rounded-lg ${
                  r.id ? (r.deduped
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                            : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400')
                       : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                }`}>
                  {r.symbol}: {r.id
                    ? (r.deduped ? `Run #${r.id} reused — identical run exists ✓` : `Run #${r.id} queued ✓`)
                    : `Failed — ${r.error}`}
                </div>
              ))}
            </div>
          )}

          {/* Result after launch — universe sweep mode */}
          {batchLaunched && batchLaunched.batch_id == null && (
            <div className="text-xs font-mono px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
              All {batchLaunched.total_symbols} symbol(s) already covered — nothing launched ✓
            </div>
          )}
          {batchLaunched && batchLaunched.batch_id != null && (
            <div className="text-xs font-mono px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
              Sweep #{batchLaunched.batch_id} launched ✓ — {batchLaunched.total_symbols} symbol(s)
              {batchLaunched.skipped_existing ? `, ${batchLaunched.skipped_existing} already covered` : ''}, {inflightCap} at a time
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
          <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
            {(launched.length > 0 || batchLaunched) ? 'Close' : 'Cancel'}
          </button>
          {/* Single (symbol-list) launch */}
          {!isUniverse && launched.length === 0 && (
            <button
              onClick={handleLaunch}
              disabled={launching || !config || !symbols.trim()}
              className="px-5 py-2 rounded-lg bg-brand-gold text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition-all"
            >
              {launching
                ? `Launching ${symbols.split(',').filter(s => s.trim()).length} run(s)…`
                : `Launch on ${symbols.split(',').filter(s => s.trim()).length || '?'} symbol(s)`}
            </button>
          )}
          {/* Universe sweep launch */}
          {isUniverse && !batchLaunched && (
            <button
              onClick={handleLaunchBatch}
              disabled={launching || !config || !(batchPreview?.total_symbols > 0)}
              className="px-5 py-2 rounded-lg bg-brand-gold text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition-all"
            >
              {launching
                ? 'Launching sweep…'
                : `Launch sweep on ${(batchPreview?.deduped ? batchPreview?.to_run : batchPreview?.total_symbols)?.toLocaleString() || '?'} symbol(s)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PARALLEL_OPT_THRESHOLD = 5_000;  // mirror backend constant

const SIGNAL_COLORS = {
  BUY:  'text-green-600 dark:text-green-400',
  SELL: 'text-red-600 dark:text-red-400',
  FLAT: 'text-gray-500 dark:text-gray-400',
};

// One deployment of a run: account · symbol · current signal. Read-only chip.
const DeploymentChip = ({ d }) => {
  const sig = d.current_signal;
  const sigColor = sig ? (SIGNAL_COLORS[sig.signal_type] || 'text-gray-500') : 'text-gray-400';
  const alloc = d.allocation_usd ? ` · $${Number(d.allocation_usd).toLocaleString()}` : '';
  const sigTitle = sig ? ` · ${sig.signal_type} (${sig.status}) ${sig.signal_date || ''}` : ' · no signal yet';
  return (
    <span
      onClick={(e) => e.stopPropagation()}
      title={`${d.strategy_name}${alloc}${sigTitle}`}
      className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-2 py-0.5 text-[11px] cursor-default"
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${d.is_active ? 'bg-brand-gold' : 'bg-gray-400 dark:bg-gray-600'}`}
        title={d.is_active ? 'Active' : 'Inactive'}
      />
      <span className="font-medium text-gray-700 dark:text-gray-200">{d.account?.name || 'account'}</span>
      {d.account?.is_paper === false && (
        <span className="text-[9px] uppercase tracking-wider text-red-500 font-semibold">live</span>
      )}
      {d.account?.is_paper === true && (
        <span className="text-[9px] uppercase tracking-wider text-gray-400">paper</span>
      )}
      {d.symbol && <span className="font-mono text-gray-500 dark:text-gray-400">{d.symbol}</span>}
      {sig
        ? <span className={`font-mono font-semibold ${sigColor}`}>{sig.signal_type}</span>
        : <span className="text-gray-400 dark:text-gray-500 italic">no signal</span>}
    </span>
  );
};

// Parse a "done/total" progress string (e.g. "1234567/2240000") to a 0..1
// fraction. Returns 0 for empty/malformed strings.
const parseProgressFrac = (s) => {
  const [a, b] = String(s || '').split('/');
  const done = parseInt(a, 10);
  const total = parseInt(b, 10);
  return total > 0 && !isNaN(done) && !isNaN(total) ? Math.min(1, done / total) : 0;
};

// Compact labelled progress bar used in the run card for whichever phase is
// live (IS / OOS / NSR). Mirrors the original IS-only bar, just colour-coded.
const PhaseBar = ({ label, frac, textClass, barClass }) => (
  <div className={`flex items-center gap-2 text-[11px] ${textClass} ml-auto`}>
    <span className="uppercase tracking-wider text-[9px] opacity-70">{label}</span>
    <span className="font-mono tabular-nums">{Math.round(frac * 100)}%</span>
    <div className="w-24 h-1 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
      <div className={`h-full ${barClass}`} style={{ width: `${frac * 100}%` }} />
    </div>
  </div>
);

const RunRow = ({ run, onClick, onDeleteClick, onCancelClick, onReplicateClick, onResumeParallelClick }) => {
  const isFailed = run.status === 'failed';
  const isPending = run.status === 'pending';
  const isRunning = run.status === 'running';
  const kind = run.kind || 'standard';
  const progress = run.total_combos > 0 ? (run.completed_combos || 0) / run.total_combos : 0;
  // OOS/NSR phases run AFTER the IS grid finalizes (status==='complete'), so the
  // IS bar won't show then. Parse their "done/total" strings to drive matching
  // bars while each phase runs.
  const oosRunning = run.oos_status === 'running';
  const nsrRunning = run.nsr_status === 'running';
  const oosFrac = parseProgressFrac(run.oos_progress);
  const nsrFrac = parseProgressFrac(run.nsr_progress);

  return (
    <div
      onClick={onClick}
      className="group relative rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22] hover:border-brand-gold/40 hover:shadow-[0_0_0_1px_rgba(201,169,110,0.15)] transition-all cursor-pointer opt-fade-in"
    >
      <div className="px-4 py-3.5 flex items-start gap-4">
        <IdBadge id={run.id} kind={kind} />

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="font-serif text-[17px] text-gray-900 dark:text-gray-50 leading-tight">
              {run.symbol}
              <span className="text-gray-500 dark:text-gray-500 font-sans text-[13px] font-normal"> · </span>
              <span className="font-mono text-[13px] text-gray-700 dark:text-gray-300 font-normal">{run.strategy_name}</span>
            </h3>
            <IdleTag idle={run.idle_deployment} />
            {run.oos_split_pct > 0 && <OosTag oosSplitPct={run.oos_split_pct} isEndDate={run.is_end_date} />}
            {(() => { const st = pipelineStage(run); return <StatusBadge status={st.state} label={st.label} />; })()}
            <span className="ml-auto text-[11px] text-gray-500 dark:text-gray-500 tabular-nums hidden sm:inline">
              {fmtAge(run.created_at)}
            </span>
          </div>

          {!isPending && !isFailed && (
            <div className="mt-2.5 flex items-center gap-4 flex-wrap">
              <Stat label="combos" value={(run.total_combos || 0).toLocaleString()} />
              <Stat label="best sharpe" valueNode={<SharpeMiniBar value={run.best_sharpe} />} />
              {run.idle_deployment && run.idle_deployment !== 'cash' && run.best_blended_sharpe != null && (
                <BlendedNote
                  symbol={run.idle_deployment}
                  sharpe={run.best_blended_sharpe}
                  baseSharpe={run.best_sharpe}
                />
              )}
              {isRunning && (
                <PhaseBar label="IS" frac={progress} textClass="text-yellow-600 dark:text-yellow-400" barClass="bg-yellow-400/80" />
              )}
              {!isRunning && oosRunning && (
                <PhaseBar label="OOS" frac={oosFrac} textClass="text-sky-600 dark:text-sky-400" barClass="bg-sky-400/80" />
              )}
              {!isRunning && !oosRunning && nsrRunning && (
                <PhaseBar label="NSR" frac={nsrFrac} textClass="text-amber-600 dark:text-amber-400" barClass="bg-amber-400/80" />
              )}
            </div>
          )}

          {isFailed && run.error_message && (
            <div className="mt-2.5 text-xs text-red-500 dark:text-red-400 font-mono">
              <span className="text-red-500/70 mr-1.5">error:</span>{run.error_message}
            </div>
          )}
          {isPending && (
            <div className="mt-2.5 text-xs text-blue-500 dark:text-blue-400 font-mono flex items-center gap-1.5">
              <Glyph name="spinner" className="w-3 h-3" />
              awaiting worker
            </div>
          )}

          {!isPending && run.best_params && Object.keys(run.best_params).length > 0 && (
            <div className="mt-2.5">
              <ParamChips params={run.best_params} />
            </div>
          )}

          {Array.isArray(run.deployments) && run.deployments.length > 0 && (
            <div className="mt-2.5 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">Deployed</span>
              {run.deployments.map(d => <DeploymentChip key={d.strategy_id} d={d} />)}
            </div>
          )}
        </div>

        {/* Right rail: replicate / cancel / delete buttons (visible on hover) + chevron */}
        <div className="self-center flex items-center gap-1 shrink-0">
          {/* Replicate — available on complete runs */}
          {run.status === 'complete' && onReplicateClick && (
            <button
              onClick={(e) => { e.stopPropagation(); onReplicateClick(run); }}
              title="Replicate on another symbol"
              className="p-1.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 hover:text-brand-gold transition-all"
            >
              {/* copy icon */}
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="9" y="9" width="13" height="13" rx="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          {isRunning && onCancelClick && (
            <button
              onClick={(e) => { e.stopPropagation(); onCancelClick(run); }}
              title={`Cancel optimization #${run.id}`}
              className="p-1.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 hover:text-orange-400 transition-all"
            >
              <Glyph name="x" className="w-3.5 h-3.5" />
            </button>
          )}
          {/* Resume on spot — available on large failed/running/pending runs */}
          {(isRunning || isFailed || isPending) && run.total_combos >= PARALLEL_OPT_THRESHOLD && onResumeParallelClick && (
            <button
              onClick={(e) => { e.stopPropagation(); onResumeParallelClick(run); }}
              title="Resume remaining combos on spot worker (parallel)"
              className="p-1.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 hover:text-sky-400 transition-all"
            >
              {/* cloud-upload icon */}
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </button>
          )}
          {!isRunning && onDeleteClick && (
            <button
              onClick={(e) => { e.stopPropagation(); onDeleteClick(run); }}
              title={`Delete optimization #${run.id}`}
              className="p-1.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 hover:text-red-400 transition-all"
            >
              <Glyph name="x" className="w-3.5 h-3.5" />
            </button>
          )}
          <div className="text-gray-300 dark:text-gray-600 group-hover:text-brand-gold transition-colors">
            <Glyph name="chevron" className="w-4 h-4" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// COMBO TABLE (By Combo, cross-run)
// ─────────────────────────────────────────────────────────────────────────

const ComboTable = ({ combos, selected, toggleOne, toggleAll, allSelected, onRunClick, hasIdle }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22] overflow-hidden">
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800/40 text-[10px] uppercase tracking-wider text-gray-500">
          <tr>
            <th className="px-3 py-2.5 text-left w-8">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-brand-gold cursor-pointer" />
            </th>
            <th className="px-3 py-2.5 text-left">Run</th>
            <th className="px-3 py-2.5 text-left">Symbol</th>
            <th className="px-3 py-2.5 text-left">Strategy</th>
            <th className="px-3 py-2.5 text-left min-w-[280px]">Params</th>
            <th className="px-3 py-2.5 text-right">Sharpe</th>
            <th className="px-3 py-2.5 text-right">Return</th>
            <th className="px-3 py-2.5 text-right">Drawdown</th>
            <th className="px-3 py-2.5 text-right">Trades</th>
            {hasIdle && <th className="px-3 py-2.5 text-right">Blended</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {combos.map((c, i) => (
            <tr
              key={`${c.run_id}-${c.combo_index ?? i}`}
              className={`hover:bg-gray-50 dark:hover:bg-gray-800/30 ${selected.has(i) ? 'bg-brand-gold/5 dark:bg-brand-gold/[0.04]' : ''}`}
            >
              <td className="px-3 py-2">
                <input type="checkbox" checked={selected.has(i)} onChange={() => toggleOne(i)} className="accent-brand-gold cursor-pointer" />
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                <button
                  onClick={() => onRunClick && onRunClick(c.run_id)}
                  className="inline-flex items-center gap-1 font-mono text-xs text-brand-gold hover:underline"
                >
                  #{c.run_id}
                </button>
              </td>
              <td className="px-3 py-2 font-serif text-gray-900 dark:text-gray-100 whitespace-nowrap">{c.symbol}</td>
              <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{c.strategy_name}</td>
              <td className="px-3 py-2"><ParamChips params={c.parameters || {}} max={4} /></td>
              <td className={`px-3 py-2 text-right tabular-nums font-mono ${valColor(c.sharpe_ratio)}`}>{fmtNum(c.sharpe_ratio)}</td>
              <td className={`px-3 py-2 text-right tabular-nums font-mono ${valColor(c.total_return)}`}>{fmtPct(c.total_return)}</td>
              <td className={`px-3 py-2 text-right tabular-nums font-mono text-red-400`}>{fmtPct(c.max_drawdown)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-mono text-gray-600 dark:text-gray-400">{c.num_trades ?? '—'}</td>
              {hasIdle && (
                <td className={`px-3 py-2 text-right tabular-nums font-mono ${c.blended_sharpe != null ? 'text-blue-500 dark:text-blue-400' : 'text-gray-500'}`}>
                  {c.blended_sharpe != null ? c.blended_sharpe.toFixed(2) : '—'}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// BULK SAVE BAR (floating)
// ─────────────────────────────────────────────────────────────────────────

const BulkSaveBar = ({ count, onClear, onSave }) => (
  <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 opt-bulk-in">
    <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-[#161b22] border border-brand-gold/40 shadow-[0_8px_30px_rgba(201,169,110,0.18)]">
      <span className="text-xs text-gray-300">
        <span className="font-mono tabular-nums text-brand-gold">{count}</span>
        {` combo${count !== 1 ? 's' : ''} selected`}
      </span>
      <button onClick={onClear} className="text-[11px] text-gray-500 hover:text-gray-300">Clear</button>
      <button
        onClick={onSave}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-gold text-[#0d1117] text-xs font-medium hover:bg-brand-gold-light opt-pulse-soft"
      >
        <Glyph name="check" className="w-3.5 h-3.5" />
        Save selected to Analytics
      </button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// LIST VIEW
// ─────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────
// RUN LAUNCHER MODAL — manual optimization configuration
// ─────────────────────────────────────────────────────────────────────────
//
// v1: Standard Run only (full grid sweep). Random / Focused / Bayesian /
// NSR / Walk-Forward come in R4 — those buttons toast "coming soon".
//
// Uses tradingService.getAvailableStrategies() to populate strategy +
// default param ranges. Doesn't fetch entry/exit rules or strategy
// description (those need the agent's prepare_optimization endpoint —
// added later as an enhancement).
//
// Risk modifier options + maximize options are hardcoded to mirror the
// chat config card, so manual + chat launches produce identical run shape.

const RUN_RISK_OPTIONS = [
  { type: 'trailing_stop', label: 'Trailing Stop',   param: 'pct',         default_range: [3, 5, 7, 10] },
  { type: 'take_profit',   label: 'Take Profit',     param: 'target_pct',  default_range: [5, 10, 15, 20] },
  { type: 'stop_loss',     label: 'Stop Loss',       param: 'pct',         default_range: [3, 5, 7] },
  { type: 'volatility_stop', label: 'Volatility Stop', param: 'sigma_mult', default_range: [1.0, 1.5, 2.0] },
  { type: 'time_exit',     label: 'Time Exit',       param: 'max_bars',    default_range: [10, 20, 30] },
];

// Defined OUTSIDE RunLauncherModal so React sees a stable component type across
// renders. If defined inside the modal (even inside a .map()), React treats it
// as a new type on every render and unmounts/remounts it — causing focus loss
// after every keystroke.
const RangeInput = ({ value, onChange, disabled, placeholder, width = 'w-16' }) => (
  <input
    type="text"
    inputMode="decimal"
    value={value ?? ''}
    placeholder={placeholder}
    onChange={e => onChange(e.target.value)}
    disabled={disabled}
    className={`${width} px-2 py-1 text-xs rounded border bg-white dark:bg-gray-900 text-center tabular-nums focus:outline-none transition-colors ${
      disabled
        ? 'border-gray-700 text-gray-600 cursor-not-allowed'
        : 'border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200 focus:border-brand-gold/60'
    }`}
  />
);

const RunLauncherModal = ({ open, kind, onClose, onLaunched, onBatchLaunched, showToast }) => {
  const [strategies, setStrategies] = useState([]);
  const [loadingStrategies, setLoadingStrategies] = useState(false);
  const [strategy, setStrategy] = useState('');
  const [symbol, setSymbol] = useState('');
  const [startDate, setStartDate] = useState('2020-01-01');
  const [idleDeployment, setIdleDeployment] = useState('cash');
  const [oosSplitPct, setOosSplitPct] = useState(0);
  const [maximize, setMaximize] = useState('Sharpe Ratio');
  const [priceSource, setPriceSource] = useState('legacy'); // 'legacy' | 'raw' — parity with copy flow
  const [endDate, setEndDate] = useState('');               // '' = latest bar; set = fixed cutoff (backend formula-splits [start,end])
  // paramSpecs: one entry per param, stores the range spec (not the final array).
  // type 'numeric'          → { start, end, step }
  // type 'bool'             → { includeTrue, includeFalse }
  // type 'nullable_numeric' → { start, end, step, includeOff }
  const [paramSpecs, setParamSpecs] = useState({});
  const [paramDescriptions, setParamDescriptions] = useState({});
  const [excludedParams, setExcludedParams] = useState(new Set());
  const [tooltipParam, setTooltipParam] = useState(null);
  const [riskEnabled, setRiskEnabled] = useState({});
  const [riskRanges, setRiskRanges] = useState(() =>
    RUN_RISK_OPTIONS.reduce((acc, r) => ({ ...acc, [r.type]: [...r.default_range] }), {})
  );
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [dataCoverage, setDataCoverage] = useState(null);   // set on 400 data-coverage errors

  // ── Universe (basket sweep) target ─────────────────────────────────────────
  // A Standard Run can target a single Symbol OR a whole universe (sweep): the
  // SAME frozen config fans across every resolved symbol via /optimize-batch/.
  const [target, setTarget] = useState('single');           // 'single' | 'universe'
  const [uniShortcuts, setUniShortcuts] = useState([]);     // e.g. ['sp500']
  const [uniText, setUniText] = useState('');               // pasted ticker list
  const [mcapMinBn, setMcapMinBn] = useState('');           // $bn min market cap
  const [topNPerSector, setTopNPerSector] = useState('');   // top N by mcap / sector
  const [inflightCap, setInflightCap] = useState(2);        // concurrent members
  const [batchPreview, setBatchPreview] = useState(null);   // dry-run result
  const [previewLoading, setPreviewLoading] = useState(false);
  const isUniverse = target === 'universe';
  const pastedSymbols = uniText
    .split(/[\s,;\n]+/).map(s => s.trim().toUpperCase()).filter(Boolean);
  const refineFilters = useMemo(() => {
    const r = {};
    const mc = parseFloat(mcapMinBn);
    if (!Number.isNaN(mc) && mc > 0) r.market_cap_min = Math.round(mc * 1e9);
    const tn = parseInt(topNPerSector, 10);
    if (!Number.isNaN(tn) && tn > 0) r.top_n_per_sector = tn;
    return r;
  }, [mcapMinBn, topNPerSector]);

  // ── Range spec helpers ───────────────────────────────────────────────────
  // Detect uniform step from a sorted numeric array.
  const detectStep = (nums) => {
    if (nums.length < 2) return 1;
    const steps = [];
    for (let i = 1; i < nums.length; i++)
      steps.push(Math.round((nums[i] - nums[i - 1]) * 1e10) / 1e10);
    const base = steps[0];
    const uniform = steps.every(s => Math.abs(s - base) < Math.abs(base) * 0.001 + 1e-9);
    return uniform && base > 0 ? base : base;
  };

  // Convert an existing values array (from strategy defaults) to a spec object.
  // For _pct params: values are stored as fractions (0.05 = 5%) but displayed
  // as percentages (5) so the user types "5" not "0.05". isPct flag controls
  // the ×100 display and ÷100 buildRange conversion.
  const arrayToSpec = (name, arr) => {
    const isBool = /^use_/.test(name) || (arr || []).some(v => typeof v === 'boolean');
    const hasNull = (arr || []).some(v => v === null);
    if (isBool) {
      return { type: 'bool', includeTrue: arr.includes(true), includeFalse: arr.includes(false) };
    }
    const isPct = /_pct$/.test(name);
    const nums = (arr || []).filter(v => v !== null && typeof v === 'number').sort((a, b) => a - b);
    // Convert fractions to display percentages for _pct params
    const displayNums = isPct ? nums.map(v => Math.round(v * 100 * 1e6) / 1e6) : nums;
    const start = displayNums.length > 0 ? displayNums[0] : '';
    const end   = displayNums.length > 0 ? displayNums[displayNums.length - 1] : '';
    const step  = displayNums.length > 1 ? detectStep(displayNums) : (isPct ? 5 : 1);
    const base  = { start, end, step, isPct };
    if (hasNull || isPct) return { type: 'nullable_numeric', ...base, includeOff: hasNull };
    return { type: 'numeric', ...base };
  };

  // Build final values array from a spec (what gets sent to the backend).
  // For isPct specs: user types display-% (5), we send fraction (0.05).
  const buildRange = (spec) => {
    if (!spec) return [];
    if (spec.type === 'bool') {
      const v = [];
      if (spec.includeTrue)  v.push(true);
      if (spec.includeFalse) v.push(false);
      return v;
    }
    const s = parseFloat(spec.start), e = parseFloat(spec.end), st = parseFloat(spec.step);
    const base = [];
    if (spec.includeOff) base.push(null);
    if (isNaN(s) || isNaN(e) || isNaN(st) || st <= 0 || s > e) return base;
    const EPS = st * 1e-6;
    for (let v = s; v <= e + EPS && base.length < 500; v = Math.round((v + st) * 1e10) / 1e10) {
      // Convert display-% back to fraction for backend
      base.push(spec.isPct ? Math.round(v / 100 * 1e8) / 1e8 : v);
    }
    return base;
  };

  // Derived param ranges sent to the backend — never store arrays directly.
  // Excluded params are omitted entirely so the strategy uses its __init__ default.
  const paramRanges = useMemo(() => {
    const out = {};
    for (const [name, spec] of Object.entries(paramSpecs)) {
      if (excludedParams.has(name)) continue;
      const vals = buildRange(spec);
      if (vals.length > 0) out[name] = vals;
    }
    return out;
    // eslint-disable-next-line
  }, [paramSpecs, excludedParams]);

  const updateSpec = (name, patch) =>
    setParamSpecs(prev => ({ ...prev, [name]: { ...prev[name], ...patch } }));

  // Fetch available strategies on first open
  useEffect(() => {
    if (!open || strategies.length > 0) return;
    setLoadingStrategies(true);
    tradingService.getAvailableStrategies()
      .then(res => {
        const list = (res.data || []).filter(s => !s.error);
        setStrategies(list);
        if (list.length > 0 && !strategy) {
          const tdb = list.find(s => s.name === 'trend_dip_buyer') || list[0];
          setStrategy(tdb.name);
          const specs = {};
          Object.entries(tdb.parameter_ranges || {}).forEach(([k, v]) => { specs[k] = arrayToSpec(k, v); });
          setParamSpecs(specs);
          setParamDescriptions(tdb.parameter_descriptions || {});
          setExcludedParams(new Set());
        }
      })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoadingStrategies(false));
    // eslint-disable-next-line
  }, [open]);

  // When strategy changes, reset param specs to that strategy's defaults
  useEffect(() => {
    if (!strategy) return;
    const s = strategies.find(x => x.name === strategy);
    if (s?.parameter_ranges) {
      const specs = {};
      Object.entries(s.parameter_ranges).forEach(([k, v]) => { specs[k] = arrayToSpec(k, v); });
      setParamSpecs(specs);
      setParamDescriptions(s.parameter_descriptions || {});
      setExcludedParams(new Set());
    }
    // eslint-disable-next-line
  }, [strategy, strategies]);

  const totalCombos = useMemo(() => {
    let total = 1;
    Object.values(paramRanges).forEach(range => {
      if (Array.isArray(range) && range.length > 0) total *= range.length;
    });
    Object.entries(riskEnabled).forEach(([type, enabled]) => {
      if (enabled && riskRanges[type]?.length > 0) total *= riskRanges[type].length;
    });
    return total;
  }, [paramRanges, riskEnabled, riskRanges]);

  const estTime = Math.max(1, Math.round(totalCombos * 0.15));

  const removeRiskChip = (type, value) => {
    setRiskRanges(prev => ({ ...prev, [type]: prev[type].filter(v => v !== value) }));
  };

  const handleRun = async () => {
    if (!symbol.trim()) {
      setError('Symbol is required');
      return;
    }
    if (totalCombos === 0) {
      setError('At least one parameter must have a value');
      return;
    }
    setRunning(true);
    setError(null);
    setDataCoverage(null);
    const riskConfig = Object.entries(riskEnabled)
      .filter(([, enabled]) => enabled)
      .map(([type]) => {
        const opt = RUN_RISK_OPTIONS.find(r => r.type === type);
        return { type, [opt?.param || 'pct']: riskRanges[type] };
      });
    try {
      const res = await tradingService.runBacktest({
        mode: 'optimize',
        strategy,
        symbol: symbol.trim().toUpperCase(),
        start_date: startDate,
        param_ranges: paramRanges,
        maximize,
        risk_config: riskConfig,
        idle_deployment: idleDeployment.trim() || 'cash',
        oos_split_pct: oosSplitPct,
        source: priceSource === 'raw' ? 'raw' : 'consolidated',
        end_date: endDate || undefined,
      });
      const runId = res.data?.optimization_id;
      const combos = res.data?.total_combinations || totalCombos;
      const isParallel = res.data?.compute_mode === 'parallel';
      if (runId) {
        showToast(
          isParallel
            ? `Run #${runId} → ☁ Spot worker · ${combos.toLocaleString()} combos · boots in ~2 min`
            : `Run #${runId} queued · ${combos.toLocaleString()} combos`
        );
        onLaunched(runId);
        onClose();
      } else {
        setError('Backend did not return an optimization_id');
      }
    } catch (e) {
      const errData = e.response?.data;
      setError(errData?.error || e.message || 'Launch failed');
      if (errData?.data_coverage) setDataCoverage(errData.data_coverage);
    } finally {
      setRunning(false);
    }
  };

  // Debounced dry-run preview: resolve the universe (count + combos + unknowns
  // + sector breakdown) WITHOUT creating anything, so the scale is visible
  // before committing. Mirrors the single-symbol data preflight intent.
  useEffect(() => {
    if (!open || !isUniverse) { setBatchPreview(null); return; }
    if (uniShortcuts.length === 0 && pastedSymbols.length === 0) {
      setBatchPreview(null); return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await tradingService.runOptimizeBatch({
          dry_run: true,
          strategy,
          universe: { shortcuts: uniShortcuts, symbols: pastedSymbols, ...refineFilters },
          param_ranges: paramRanges,
          source: priceSource === 'raw' ? 'raw' : 'consolidated',
          end_date: endDate || undefined,
        });
        if (!cancelled) setBatchPreview(res.data);
      } catch (e) {
        if (!cancelled) setBatchPreview({ error: e.response?.data?.error || 'Preview failed' });
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isUniverse, uniShortcuts, uniText, strategy, paramRanges, refineFilters, priceSource, endDate]);

  const handleLaunchBatch = async () => {
    if (totalCombos === 0) {
      setError('At least one parameter must have a value');
      return;
    }
    if (!(batchPreview?.total_symbols > 0)) {
      setError('No symbols resolved — pick a universe first');
      return;
    }
    setRunning(true);
    setError(null);
    try {
      const res = await tradingService.runOptimizeBatch({
        strategy,
        universe: { shortcuts: uniShortcuts, symbols: pastedSymbols, ...refineFilters },
        param_ranges: paramRanges,
        maximize,
        start_date: startDate,
        idle_deployment: idleDeployment.trim() || 'cash',
        oos_split_pct: oosSplitPct,
        inflight_cap: inflightCap,
        source: priceSource === 'raw' ? 'raw' : 'consolidated',
        end_date: endDate || undefined,
      });
      if (res.data?.success) {
        showToast(
          `Sweep #${res.data.batch_id} launched · ${res.data.total_symbols} symbol(s) · ${inflightCap} at a time`
        );
        onBatchLaunched && onBatchLaunched({
          id: res.data.batch_id,
          status: 'queued',
          counts: {},
          members: [],
          total_symbols: res.data.total_symbols,
          pct_complete: 0,
          name: res.data.name || `${strategy} sweep`,
        });
        onClose();
      } else {
        setError(res.data?.error || 'Failed to launch sweep');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Sweep launch failed');
    } finally {
      setRunning(false);
    }
  };

  if (!open) return null;

  const selectedStrategy = strategies.find(s => s.name === strategy);
  const isStandard = kind === 'standard';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="my-8 w-full max-w-3xl rounded-lg bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 shadow-2xl">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RunKindIcon kind={kind} className="w-4 h-4 text-brand-gold" />
            <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">
              Launch {isStandard ? 'Standard Run' : kind} optimization
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-brand-gold">
            <Glyph name="x" className="w-4 h-4" />
          </button>
        </div>

        {!isStandard && (
          <div className="px-4 py-3 bg-yellow-500/10 border-b border-yellow-500/30 text-xs text-yellow-700 dark:text-yellow-400">
            {kind} runs aren't wired in v1 of the manual launcher — defaulting to a Standard Run grid sweep with the params below.
          </div>
        )}

        {loadingStrategies && (
          <div className="px-4 py-6 text-center text-xs text-gray-500">Loading strategies…</div>
        )}

        {!loadingStrategies && (
          <>
            {/* Target toggle: optimise a single symbol, or fan this exact config
                across a whole universe (basket sweep) via /optimize-batch/. */}
            {isStandard && (
              <div className="px-4 pt-3 pb-1 border-b border-gray-200 dark:border-gray-800">
                <div className="inline-flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
                  {[['single', 'Single Symbol'], ['universe', 'Universe Sweep']].map(([t, lbl]) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => { setTarget(t); setError(null); }}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        target === t
                          ? 'bg-white dark:bg-[#0d1117] text-brand-gold shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Top form: strategy / symbol / start_date / idle / maximize */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 block">Strategy</label>
                <select
                  value={strategy}
                  onChange={(e) => setStrategy(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                >
                  {strategies.map(s => (
                    <option key={s.name} value={s.name}>
                      {s.display_name} {s.source === 'custom' ? `(${s.author || 'custom'})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {!isUniverse && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 block">Symbol</label>
                  <input
                    type="text"
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                    placeholder="e.g. AAPL"
                    className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117] uppercase"
                  />
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 block">Start date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 block">
                  End date <span className="text-gray-400 normal-case">(cutoff — blank = latest bar)</span>
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                />
                <span className="block text-[10px] text-gray-400 mt-1">
                  Bounds optimization data; IS/OOS split is computed by the split % over [start, end]. Data after it stays out-of-sample.
                </span>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 block">Price series</label>
                <div className="inline-flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-700">
                  {[{ key: 'legacy', label: 'Legacy' }, { key: 'raw', label: 'Raw (TR ledger)' }].map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setPriceSource(opt.key)}
                      className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${
                        priceSource === opt.key
                          ? 'bg-brand-gold text-black'
                          : 'bg-gray-50 dark:bg-[#0d1117] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <span className="block text-[10px] text-gray-400 mt-1">
                  {priceSource === 'raw'
                    ? 'Raw total-return ledger. Symbols/idle ETFs outside it are rejected; factor-basket strategies need raw coverage for the window.'
                    : 'Vendor split + dividend adjusted close (default).'}
                </span>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 block">
                  Idle deployment <span className="text-gray-400 normal-case">(cash or ticker)</span>
                </label>
                <input
                  type="text"
                  value={idleDeployment}
                  onChange={(e) => setIdleDeployment(e.target.value)}
                  placeholder="cash"
                  className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 block">Maximize</label>
                <select
                  value={maximize}
                  onChange={(e) => setMaximize(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                >
                  <option value="Sharpe Ratio">Sharpe Ratio</option>
                  <option value="Return">Return</option>
                  <option value="Win Rate">Win Rate</option>
                  <option value="Calmar">Calmar</option>
                  {idleDeployment.trim() && idleDeployment.trim().toLowerCase() !== 'cash' && (
                    <>
                      <option value="Blended Sharpe">Blended Sharpe</option>
                      <option value="Blended Return">Blended Return</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 block">
                  OOS hold-out <span className="text-gray-400 normal-case">(reserve last N% for test)</span>
                </label>
                <div className="flex gap-1">
                  {[0, 10, 20, 30].map(pct => (
                    <button
                      key={pct}
                      type="button"
                      onClick={() => setOosSplitPct(pct)}
                      className={`flex-1 py-1 text-xs rounded border transition-colors ${
                        oosSplitPct === pct
                          ? 'bg-[#c9a96e] border-[#c9a96e] text-white font-semibold'
                          : 'border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-[#c9a96e] hover:text-[#c9a96e]'
                      }`}
                    >
                      {pct === 0 ? 'Off' : `${pct}%`}
                    </button>
                  ))}
                </div>
                {oosSplitPct > 0 && (
                  <p className="text-[10px] text-gray-400 mt-1">
                    Last {oosSplitPct}% of price history withheld — validate chosen combos on it after the run.
                  </p>
                )}
              </div>
            </div>

            {/* Universe selector (Universe Sweep target) — runs the SAME config
                below across every resolved symbol, metered to the spot worker. */}
            {isUniverse && (
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                <div className="rounded-lg border border-brand-gold/30 bg-brand-gold/5 p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                    Universe — this exact config fans across every resolved symbol
                  </div>

                  {/* Shortcut chips (multi-select, unioned) */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {UNIVERSE_SHORTCUTS.map(sc => {
                      const on = uniShortcuts.includes(sc.key);
                      return (
                        <button
                          key={sc.key}
                          type="button"
                          onClick={() => setUniShortcuts(prev =>
                            on ? prev.filter(k => k !== sc.key) : [...prev, sc.key])}
                          className={`px-2.5 py-1 text-[11px] font-medium rounded-full border transition-colors ${
                            on
                              ? 'bg-brand-gold text-white border-brand-gold'
                              : 'bg-white dark:bg-[#0d1117] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:border-brand-gold'
                          }`}
                        >
                          {sc.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Paste-a-list */}
                  <label className="text-[10px] text-gray-400 mb-1 block">
                    Or paste tickers (comma / space / newline separated) — unioned with chips
                  </label>
                  <textarea
                    className="w-full h-16 px-2 py-1.5 text-xs font-mono rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                    value={uniText}
                    onChange={e => setUniText(e.target.value)}
                    placeholder="AAPL, MSFT, NVDA&#10;TSLA GOOGL"
                  />

                  {/* Refinement — on-demand live market cap (FMP). Empty = off. */}
                  <div className="mt-3 pt-3 border-t border-brand-gold/20 grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-gray-400 mb-1 block">Min market cap ($bn)</label>
                      <input
                        type="number" min={0} step={10} placeholder="e.g. 100"
                        className="w-full px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                        value={mcapMinBn}
                        onChange={e => setMcapMinBn(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 mb-1 block">Top N by cap / sector</label>
                      <input
                        type="number" min={0} step={1} placeholder="e.g. 5"
                        className="w-full px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                        value={topNPerSector}
                        onChange={e => setTopNPerSector(e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">
                    Current cap fetched live (not stored). Symbols with no cap data are dropped from cap/sector filters.
                  </p>

                  {/* Concurrency */}
                  <div className="mt-3 flex items-center gap-2">
                    <label className="text-[10px] uppercase tracking-wider text-gray-500">Concurrent runs</label>
                    <input
                      type="number" min={1} max={8}
                      className="w-16 px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]"
                      value={inflightCap}
                      onChange={e => setInflightCap(Math.max(1, Math.min(8, parseInt(e.target.value) || 2)))}
                    />
                    <span className="text-[10px] text-gray-400">
                      members dispatched to the one spot worker at once (each fans 8 IS slices — 2 is the sweet spot)
                    </span>
                  </div>

                  {/* Resolved-count preview */}
                  {previewLoading && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
                      <Glyph name="spinner" className="w-3.5 h-3.5" /> Resolving universe…
                    </div>
                  )}
                  {batchPreview && !previewLoading && !batchPreview.error && (
                    <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0d1117] p-3">
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div>
                          <span className="text-gray-500 block">Symbols</span>
                          <span className="text-base font-semibold text-brand-gold">{batchPreview.total_symbols?.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 block">Combos / symbol</span>
                          <span className="text-base font-semibold text-gray-800 dark:text-gray-200">{batchPreview.combos_per_member?.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 block">Total combos</span>
                          <span className="text-base font-semibold text-gray-800 dark:text-gray-200">{batchPreview.estimated_total_combos?.toLocaleString()}</span>
                        </div>
                      </div>
                      {batchPreview.refined && batchPreview.sector_breakdown
                        && Object.keys(batchPreview.sector_breakdown).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                          <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">By sector</span>
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(batchPreview.sector_breakdown)
                              .sort((a, b) => b[1] - a[1])
                              .map(([sec, n]) => (
                                <span key={sec} className="px-2 py-0.5 text-[10px] rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                                  {sec} · {n}
                                </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {batchPreview.dropped_no_mcap?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-[11px] text-gray-500">
                          {batchPreview.dropped_no_mcap.length} symbol(s) dropped — no market-cap data from FMP.
                        </div>
                      )}
                      {batchPreview.unknown_symbols?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-[11px] text-orange-600 dark:text-orange-400">
                          {batchPreview.unknown_symbols.length} unknown ticker(s) skipped: {batchPreview.unknown_symbols.slice(0, 12).join(', ')}{batchPreview.unknown_symbols.length > 12 ? '…' : ''}
                        </div>
                      )}
                    </div>
                  )}
                  {batchPreview?.error && !previewLoading && (
                    <div className="mt-3 text-xs text-red-600 dark:text-red-400">{batchPreview.error}</div>
                  )}
                </div>
              </div>
            )}

            {/* Param ranges */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">
                Strategy Parameters
                {selectedStrategy && (
                  <span className="ml-2 text-gray-400 normal-case">
                    · default lookback {selectedStrategy.lookback || '?'} bars
                  </span>
                )}
              </div>
              {Object.keys(paramSpecs).length === 0 ? (
                <div className="text-xs text-gray-500">No optimizable params for this strategy.</div>
              ) : (
                <div className="space-y-2.5">
                  {Object.entries(paramSpecs).map(([name, spec]) => {
                    const excluded = excludedParams.has(name);
                    const desc = paramDescriptions[name];
                    const vals = excluded ? [] : buildRange(spec);
                    const count = vals.filter(v => v !== null).length;
                    const showDesc = tooltipParam === name;

                    const toggleExclude = () => setExcludedParams(prev => {
                      const next = new Set(prev);
                      next.has(name) ? next.delete(name) : next.add(name);
                      return next;
                    });

                    const ri = (field, placeholder, width) => (
                      <RangeInput
                        value={spec[field] ?? ''}
                        onChange={v => updateSpec(name, { [field]: v })}
                        disabled={excluded}
                        placeholder={placeholder}
                        width={width}
                      />
                    );

                    const nameLabel = (
                      <div className="flex items-center gap-1.5 w-36 shrink-0">
                        <button
                          onClick={toggleExclude}
                          title={excluded ? 'Re-include (click to restore)' : 'Exclude from grid — fixes at strategy default'}
                          className={`shrink-0 w-4 h-4 flex items-center justify-center text-[10px] transition-colors ${
                            excluded ? 'text-red-400/70 hover:text-red-400' : 'text-gray-600 hover:text-gray-400'
                          }`}
                        >{excluded ? '✕' : '–'}</button>
                        <button
                          onClick={() => setTooltipParam(showDesc ? null : name)}
                          className={`font-mono text-xs truncate text-left transition-colors ${
                            excluded
                              ? 'text-gray-600 line-through cursor-pointer'
                              : desc
                              ? 'text-gray-500 dark:text-gray-400 hover:text-brand-gold/80'
                              : 'text-gray-500 dark:text-gray-400 cursor-default'
                          }`}
                        >{name}{desc && !excluded && <span className="ml-0.5 text-[9px] text-gray-600">ℹ</span>}</button>
                      </div>
                    );

                    if (spec.type === 'bool') {
                      return (
                        <div key={name} className="space-y-1">
                          <div className={`flex items-center gap-2 ${excluded ? 'opacity-40' : ''}`}>
                            {nameLabel}
                            <div className="flex gap-1.5">
                              <button disabled={excluded} onClick={() => updateSpec(name, { includeTrue: !spec.includeTrue })}
                                className={`px-3 py-1 text-xs rounded transition-colors disabled:cursor-not-allowed ${spec.includeTrue ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'border border-dashed border-gray-600 text-gray-500 hover:border-green-500/40 hover:text-green-500/70'}`}
                              >ON</button>
                              <button disabled={excluded} onClick={() => updateSpec(name, { includeFalse: !spec.includeFalse })}
                                className={`px-3 py-1 text-xs rounded transition-colors disabled:cursor-not-allowed ${spec.includeFalse ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'border border-dashed border-gray-600 text-gray-500 hover:border-gray-400 hover:text-gray-400'}`}
                              >OFF</button>
                            </div>
                            {!excluded && <span className="text-[10px] text-gray-500">{[spec.includeTrue && 'ON', spec.includeFalse && 'OFF'].filter(Boolean).join(', ') || '—'}</span>}
                          </div>
                          {showDesc && desc && (
                            <div className="ml-6 px-2.5 py-2 rounded bg-gray-800/60 border border-gray-700/50 text-[11px] text-gray-300 leading-relaxed">
                              {desc}
                            </div>
                          )}
                        </div>
                      );
                    }

                    // numeric / nullable_numeric
                    const isPct = !!spec.isPct;
                    const previewVals = vals.filter(v => v !== null);
                    // For pct params: display as % (multiply stored fractions by 100)
                    const previewDisplay = isPct
                      ? previewVals.map(v => parseFloat((v * 100).toPrecision(4)))
                      : previewVals;
                    const fmtVal = v => Number.isInteger(v) ? v : parseFloat(v.toPrecision(4));
                    const previewStr = previewDisplay.length <= 6
                      ? previewDisplay.map(fmtVal).join(', ') + (isPct && previewDisplay.length > 0 ? '%' : '')
                      : `${fmtVal(previewDisplay[0])}, ${fmtVal(previewDisplay[1])}, … ${fmtVal(previewDisplay[previewDisplay.length - 1])}${isPct ? '%' : ''}`;

                    return (
                      <div key={name} className="space-y-1">
                        <div className={`flex items-center gap-2 flex-wrap transition-opacity ${excluded ? 'opacity-40' : ''}`}>
                          {nameLabel}
                          <div className="flex items-center gap-1.5">
                            {ri('start', 'from')}
                            <span className="text-gray-500 text-xs">→</span>
                            {ri('end', 'to')}
                            {isPct && <span className="text-[10px] text-gray-400">%</span>}
                            <span className="text-[10px] text-gray-500 ml-1">step</span>
                            {ri('step', 'step', 'w-14')}
                            {spec.type === 'nullable_numeric' && (
                              <button disabled={excluded} onClick={() => updateSpec(name, { includeOff: !spec.includeOff })}
                                className={`px-2.5 py-1 text-xs rounded ml-1 transition-colors disabled:cursor-not-allowed ${spec.includeOff ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'border border-dashed border-gray-600 text-gray-500 hover:border-gray-400 hover:text-gray-400'}`}
                              >OFF</button>
                            )}
                          </div>
                          {!excluded && (
                            <span className="text-[10px] text-gray-500 ml-1">
                              {count > 0
                                ? <><span className="text-brand-gold/80 font-medium">{count + (spec.includeOff ? 1 : 0)}</span><span className="text-gray-600"> values · {previewStr}</span></>
                                : <span className="text-red-400/60">—</span>}
                            </span>
                          )}
                          {excluded && <span className="text-[10px] text-gray-600 italic">fixed at default</span>}
                        </div>
                        {showDesc && desc && (
                          <div className="ml-6 px-2.5 py-2 rounded bg-gray-800/60 border border-gray-700/50 text-[11px] text-gray-300 leading-relaxed">
                            {desc}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Risk modifiers — not supported by the batch sweep endpoint, so
                surfaced as a note (never silently dropped) in universe mode. */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Risk Modifiers (optional)</div>
              {isUniverse ? (
                <div className="text-[11px] text-gray-500 dark:text-gray-400 italic">
                  Risk modifiers aren't applied to universe sweeps yet — launch a single-symbol run to grid them.
                </div>
              ) : (
              <div className="space-y-2">
                {RUN_RISK_OPTIONS.map(r => (
                  <div key={r.type} className="flex items-center gap-2 flex-wrap">
                    <label className="flex items-center gap-1.5 w-32 shrink-0">
                      <input
                        type="checkbox"
                        checked={!!riskEnabled[r.type]}
                        onChange={(e) => setRiskEnabled(prev => ({ ...prev, [r.type]: e.target.checked }))}
                        className="accent-brand-gold"
                      />
                      <span className="text-xs text-gray-700 dark:text-gray-300">{r.label}</span>
                    </label>
                    {riskEnabled[r.type] && (
                      <div className="flex gap-1 flex-wrap">
                        {(riskRanges[r.type] || []).map(v => (
                          <button
                            key={String(v)}
                            onClick={() => removeRiskChip(r.type, v)}
                            className="px-2 py-0.5 text-xs rounded-full bg-purple-500/15 text-purple-500 dark:text-purple-400 hover:bg-red-500/20 hover:text-red-400"
                          >
                            {v} ×
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              )}
            </div>

            {/* Footer with combo counter + run button */}
            {error && !dataCoverage && (
              <div className="px-4 py-2 text-xs text-red-700 dark:text-red-400 bg-red-500/10 border-b border-red-500/30">
                {error}
              </div>
            )}
            {dataCoverage && (
              <div className="px-4 py-3 bg-red-500/10 border-b border-red-500/30">
                <div className="flex items-center gap-1.5 mb-2">
                  <svg className="w-3.5 h-3.5 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <span className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider">
                    Insufficient Data — Cannot Start Optimization
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                  {[
                    { label: 'Bars Found', value: dataCoverage.total_bars?.toLocaleString() || '0', warn: true },
                    { label: 'Bars Required', value: dataCoverage.required?.toLocaleString() || '—' },
                    { label: 'Data From', value: dataCoverage.earliest || '—' },
                    { label: 'Data To', value: dataCoverage.latest || '—' },
                  ].map(({ label, value, warn }) => (
                    <div key={label} className="bg-red-500/10 rounded p-2">
                      <div className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</div>
                      <div className={`text-sm font-mono font-semibold ${warn ? 'text-red-500' : 'text-gray-800 dark:text-gray-200'}`}>{value}</div>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400">
                  Sources tried: <span className="text-gray-700 dark:text-gray-300">yfinance, FMP, IBKR</span>
                  {dataCoverage.sources?.length > 0 && (
                    <> · Data found in: <span className="text-gray-700 dark:text-gray-300">{dataCoverage.sources.join(', ')}</span></>
                  )}
                </div>
              </div>
            )}
            <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {isUniverse ? (
                  batchPreview?.total_symbols > 0 ? (
                    <>
                      <span className="font-mono tabular-nums text-gray-900 dark:text-gray-100">
                        {batchPreview.total_symbols.toLocaleString()}
                      </span>
                      {' symbols × '}
                      <span className="font-mono tabular-nums text-gray-900 dark:text-gray-100">
                        {totalCombos.toLocaleString()}
                      </span>
                      {' combos · '}
                      <span className="text-gray-500">{inflightCap} at a time</span>
                    </>
                  ) : (
                    <span className="text-gray-500">Pick a universe to resolve scale…</span>
                  )
                ) : (
                  <>
                    <span className="font-mono tabular-nums text-gray-900 dark:text-gray-100">
                      {totalCombos.toLocaleString()}
                    </span>
                    {' combinations · '}
                    <span className="text-gray-500">
                      ~{estTime < 60 ? `${estTime}s` : `${Math.round(estTime / 60)} min`} estimated
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                {isUniverse ? (
                  <button
                    onClick={handleLaunchBatch}
                    disabled={running || totalCombos === 0 || !(batchPreview?.total_symbols > 0)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-gold text-[#0d1117] text-xs font-medium hover:bg-brand-gold-light disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {running ? (
                      <><Glyph name="spinner" className="w-3.5 h-3.5" /> Launching…</>
                    ) : (
                      <><Glyph name="check" className="w-3.5 h-3.5" />
                        {batchPreview?.total_symbols > 0
                          ? `Launch sweep — ${batchPreview.total_symbols.toLocaleString()} symbols`
                          : 'Launch sweep'}
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleRun}
                    disabled={running || totalCombos === 0 || !symbol.trim()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-gold text-[#0d1117] text-xs font-medium hover:bg-brand-gold-light disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {running ? (
                      <><Glyph name="spinner" className="w-3.5 h-3.5" /> Launching…</>
                    ) : (
                      <><Glyph name="check" className="w-3.5 h-3.5" /> Launch optimization</>
                    )}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// BATCH SWEEP TILE — aggregate view of one launched universe sweep. Collapsed
// it shows progress + counts; expanded it reveals the per-member grid + sector
// filter (the experience that used to live on the retired Backtest tab). Polls
// its own status so multiple sweeps can run side-by-side.
// ─────────────────────────────────────────────────────────────────────────
const BatchSweepTile = ({ initial, onDismiss }) => {
  const [batch, setBatch] = useState(initial);
  const [expanded, setExpanded] = useState(false);
  const [fundsLoaded, setFundsLoaded] = useState(false);
  const [sectorFilter, setSectorFilter] = useState('');
  const [err, setErr] = useState(null);

  const active = batch && !['complete', 'cancelled'].includes(batch.status);

  // Poll batch status while active. Once sectors are loaded, keep fetching WITH
  // fundamentals so the member filter survives refreshes.
  useEffect(() => {
    if (!batch?.id || !active) return;
    const poll = setInterval(async () => {
      try {
        const res = await tradingService.getOptimizeBatchStatus(batch.id, fundsLoaded);
        setBatch(res.data);
      } catch { /* ignore transient */ }
    }, 5000);
    return () => clearInterval(poll);
  }, [batch?.id, active, fundsLoaded]);

  const loadFunds = useCallback(async () => {
    if (!batch?.id) return;
    try {
      const res = await tradingService.getOptimizeBatchStatus(batch.id, true);
      setBatch(res.data);
      setFundsLoaded(true);
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not load sectors');
    }
  }, [batch?.id]);

  const cancel = useCallback(async () => {
    if (!batch?.id) return;
    try {
      await tradingService.cancelOptimizeBatch(batch.id);
      setBatch(prev => (prev ? { ...prev, cancel_requested: true } : prev));
    } catch (e) {
      setErr(e.response?.data?.error || 'Cancel failed');
    }
  }, [batch?.id]);

  const [restarting, setRestarting] = useState(false);
  const restart = useCallback(async () => {
    if (!batch?.id) return;
    setErr(null);
    setRestarting(true);
    try {
      const res = await tradingService.restartOptimizeBatch(batch.id);
      // Optimistically flip to running; the 5s poll will refresh real counts.
      setBatch(prev => (prev ? { ...prev, status: 'running', cancel_requested: false } : prev));
      setErr(`Re-queued ${res.data?.requeued ?? 0} failed member(s).`);
    } catch (e) {
      setErr(e.response?.data?.error || 'Restart failed');
    } finally {
      setRestarting(false);
    }
  }, [batch?.id]);

  const sectors = useMemo(() => {
    const s = new Set((batch?.members || []).map(m => m.sector).filter(Boolean));
    return Array.from(s).sort();
  }, [batch?.members]);

  const visibleMembers = useMemo(() => {
    const members = batch?.members || [];
    return sectorFilter ? members.filter(m => m.sector === sectorFilter) : members;
  }, [batch?.members, sectorFilter]);

  if (!batch) return null;
  const statusLabel = batch.cancel_requested && batch.status !== 'cancelled'
    ? 'cancelling' : batch.status;
  const pct = batch.pct_complete ?? 0;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22] overflow-hidden">
      {/* Aggregate header — click to expand the member grid */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
      >
        <div className="flex items-center gap-2 mb-2">
          <Glyph name={expanded ? 'chevron-d' : 'chevron'} className="w-3.5 h-3.5 text-gray-400" />
          <span className="font-serif text-sm text-gray-900 dark:text-gray-100">
            Sweep #{batch.id}
          </span>
          <span className="text-[11px] text-gray-500 font-mono truncate">{batch.name}</span>
          <span className={`ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full ${
            batch.status === 'complete' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' :
            batch.status === 'cancelled' ? 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-400' :
            'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
          }`}>
            {statusLabel}
          </span>
        </div>
        <div className="flex justify-between text-[10px] text-gray-500 mb-1">
          <span>{batch.counts?.terminal || 0} / {batch.total_symbols} done</span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 w-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
          <div className="h-full bg-brand-gold transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
          {[
            ['queued', batch.counts?.queued, 'text-gray-500'],
            ['running', batch.counts?.running, 'text-blue-600 dark:text-blue-400'],
            ['complete', batch.counts?.complete, 'text-green-600 dark:text-green-400'],
            ['failed', batch.counts?.failed, 'text-red-600 dark:text-red-400'],
          ].map(([label, n, cls]) => (
            <span key={label} className={`px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 ${cls}`}>
              {label}: {n || 0}
            </span>
          ))}
        </div>
      </button>

      {/* Action row */}
      <div className="px-3 pb-2 flex items-center gap-3">
        {active && !batch.cancel_requested && (
          <button onClick={cancel} className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline">
            <Glyph name="x" className="w-3 h-3" /> Cancel
          </button>
        )}
        {(batch.counts?.failed || 0) > 0 && batch.status !== 'cancelled' && !batch.cancel_requested && (
          <button
            onClick={restart}
            disabled={restarting}
            className="flex items-center gap-1 text-xs text-brand-gold hover:underline disabled:opacity-50"
            title={`Re-queue ${batch.counts?.failed || 0} failed member(s) and resume the sweep`}
          >
            <Glyph name={restarting ? 'spinner' : 'refresh'} className={`w-3 h-3 ${restarting ? 'animate-spin' : ''}`} />
            {restarting ? 'Restarting…' : `Restart failed (${batch.counts?.failed || 0})`}
          </button>
        )}
        <button onClick={() => onDismiss(batch.id)} className="text-xs text-gray-400 hover:text-gray-600">
          Dismiss
        </button>
        {err && <span className="text-[11px] text-red-500">{err}</span>}
      </div>

      {/* Expanded: member filter + grid */}
      {expanded && batch.members?.length > 0 && (
        <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-800 pt-2">
          <div className="flex items-center gap-2 mb-2">
            {!fundsLoaded ? (
              <button onClick={loadFunds} className="text-[11px] text-brand-gold hover:underline">
                Load sectors to filter
              </button>
            ) : (
              <>
                <label className="text-[10px] uppercase tracking-wider text-gray-500">Sector</label>
                <select
                  value={sectorFilter}
                  onChange={e => setSectorFilter(e.target.value)}
                  className="bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-xs text-gray-700 dark:text-gray-300"
                >
                  <option value="">All sectors ({batch.members.length})</option>
                  {sectors.map(sec => (
                    <option key={sec} value={sec}>
                      {sec} ({batch.members.filter(m => m.sector === sec).length})
                    </option>
                  ))}
                </select>
                {sectorFilter && (
                  <button onClick={() => setSectorFilter('')} className="text-[11px] text-gray-400 hover:text-gray-600">clear</button>
                )}
              </>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto scrollbar-thin">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
              {visibleMembers.map(m => (
                <div
                  key={m.id}
                  title={m.error_message || `${m.symbol}${m.sector ? ' · ' + m.sector : ''}${m.market_cap ? ' · $' + Math.round(m.market_cap / 1e9) + 'bn' : ''} · ${m.status} · oos=${m.oos_status || '–'} · nsr=${m.nsr_status || '–'}`}
                  className={`flex items-center justify-between rounded px-2 py-1 text-[11px] border ${
                    m.status === 'failed' ? 'border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-900/10' :
                    m.terminal ? 'border-green-300 dark:border-green-900 bg-green-50 dark:bg-green-900/10' :
                    m.dispatch_state === 'released' ? 'border-blue-300 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/10' :
                    'border-gray-200 dark:border-gray-800'
                  }`}
                >
                  <span className="font-mono font-medium text-gray-700 dark:text-gray-300 truncate">{m.symbol}</span>
                  <span className="text-gray-400 ml-1 shrink-0">
                    {m.status === 'failed' ? '✕' : m.terminal ? '✓' : m.dispatch_state === 'released' ? '⟳' : '·'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PAGE_SIZE = 50;

// ── Grid tile: one parameter-grid (search space, e.g. 11.88M combos) under a
// strategy. Header shows the combo count + per-status tallies. When expanded it
// lazily loads ALL its runs in one shot (no pagination — the grid_key list path
// returns the whole bounded grid), buckets them into Running / Completed /
// Failed status tiles, and reveals the flat run list for whichever status tile
// you click. Action row (left) carries Resume-failed now; Investigate / Bulk
// deploy are stubbed for later. Polls every 5s while it holds active runs.
const STATUS_TILE_ORDER = ['running', 'completed', 'failed'];

// ── Run-config facets (Path B filter bar). These are the EXACT run-level config
// dimensions that define a "config-class" — the symbol-free dedup tuple. Two
// complete runs sharing all of these (within one param-grid) differ ONLY by
// ticker, so they form one cap-weightable deployable basket. NOT per-combo
// hyperparameters (those ARE the grid). Order = display order in the chip bar.
const CONFIG_FACETS = [
  ['idle', 'Idle'],
  ['intended_start', 'Start'],
  ['is_end', 'IS end'],
  ['oos_end', 'OOS end'],
  ['oos_split_pct', 'OOS %'],
  ['maximize', 'Maximize'],
  ['source', 'Source'],
];

// Human label for one facet value. '' (blank legacy column) renders as an em
// dash; oos_split_pct gets a % suffix. Everything else is shown verbatim.
const fmtFacetVal = (key, v) => {
  if (v === '' || v === null || v === undefined) return '—';
  if (key === 'oos_split_pct') return `${v}%`;
  return String(v);
};

const GridTile = ({
  strategyName, grid, filters, sort, refreshKey, expanded, onToggle,
  onSelectRun, onDeleteClick, onCancelClick, onReplicateClick, onResumeParallelClick,
  onDeployBasket, showToast,
}) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [openStatus, setOpenStatus] = useState(null);  // 'running' | 'completed' | 'failed' | null
  const [resuming, setResuming] = useState(false);

  // ── Path B config-class state: the run-config filter bar feeds off this. ──
  const [cfgClasses, setCfgClasses] = useState([]);   // [{config_class_key, ...facets, run_ids, deployable_count, ...}]
  const [cfgFacets, setCfgFacets] = useState({});     // {idle:[...], is_end:[...], ...} distinct values
  const [cfgLoaded, setCfgLoaded] = useState(false);
  const [cfgFilters, setCfgFilters] = useState({});   // {facetKey: selectedValue} — '' / absent = All

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = {
        strategy: strategyName,
        grid_key: grid.grid_key,
        limit: 100000,   // grid_key path returns the whole bounded grid (no pagination)
        offset: 0,
        sort,
      };
      if (filters.symbol) params.symbol = filters.symbol;
      if (filters.idle)   params.idle_deployment = filters.idle;
      if (filters.status) params.status = filters.status;
      if (filters.date) {
        const days = parseInt(filters.date, 10);
        if (!isNaN(days)) params.created_after = new Date(Date.now() - days * 86400000).toISOString();
      }
      const res = await tradingService.listOptimizations(params);
      setRuns(res.data.results || []);
      setLoaded(true);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [strategyName, grid.grid_key, sort, filters.symbol, filters.idle, filters.status, filters.date]);

  // Load (and reload) only while expanded — keeps collapsed tiles cheap.
  useEffect(() => {
    if (!expanded) return;
    fetchRuns();
    // eslint-disable-next-line
  }, [expanded, fetchRuns, refreshKey]);

  // Config-class aggregation (filter bar). Lightweight endpoint — safe to
  // refetch alongside the run list. A failure here must NOT block the run list,
  // so we swallow it and simply omit the filter bar.
  const fetchConfigClasses = useCallback(async () => {
    try {
      const res = await tradingService.getConfigClasses({
        grid_key: grid.grid_key,
        strategy: strategyName,
      });
      setCfgClasses(res.data?.classes || []);
      setCfgFacets(res.data?.facets || {});
    } catch {
      setCfgClasses([]);
      setCfgFacets({});
    } finally {
      setCfgLoaded(true);
    }
  }, [grid.grid_key, strategyName]);

  useEffect(() => {
    if (!expanded) return;
    fetchConfigClasses();
    // eslint-disable-next-line
  }, [expanded, fetchConfigClasses, refreshKey]);

  // Facets that actually vary across this grid's runs → render a chip each.
  // Invariant facets (single distinct value) aren't actionable filters.
  const varyingFacets = useMemo(
    () => CONFIG_FACETS.filter(([key]) => (cfgFacets[key] || []).length > 1),
    [cfgFacets]
  );

  // Is any config filter currently narrowing the view?
  const filtersActive = useMemo(
    () => CONFIG_FACETS.some(([key]) => {
      const v = cfgFilters[key];
      return v !== undefined && v !== '' && v !== null;
    }),
    [cfgFilters]
  );

  // Config-classes satisfying EVERY active filter (exact equality — no fuzzy).
  const matchedClasses = useMemo(
    () => cfgClasses.filter(c => CONFIG_FACETS.every(([key]) => {
      const f = cfgFilters[key];
      if (f === undefined || f === '' || f === null) return true;
      return String(c[key]) === String(f);
    })),
    [cfgClasses, cfgFilters]
  );

  // Run-ids belonging to the matched classes — the membership set we filter the
  // displayed buckets by. The list endpoint omits source/intended_* fields, so
  // we MUST filter by id membership, never by recomputing the class key client-side.
  const matchedRunIds = useMemo(() => {
    const s = new Set();
    matchedClasses.forEach(c => (c.run_ids || []).forEach(id => s.add(id)));
    return s;
  }, [matchedClasses]);

  // Poll while any loaded run is still active (running/pending).
  const hasActive = useMemo(
    () => runs.some(r => { const s = pipelineStage(r).state; return s === 'running' || s === 'pending'; }),
    [runs]
  );
  useEffect(() => {
    if (!expanded || !hasActive) return;
    // 15s (was 5s): each tick refetches the full optimizations list
    // (optimizations/?limit=100000 ≈ 736KB) and the single gunicorn worker has
    // to serve it. Optimizations run for minutes-to-hours, so a 5s cadence
    // bought no responsiveness it just flooded the worker. 15s is plenty to
    // reflect run-state transitions while cutting that load 3×.
    const id = setInterval(() => fetchRuns(), 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [expanded, hasActive, fetchRuns]);

  // Runs visible after the config-class filter. No active filter → all runs.
  const visibleRuns = useMemo(
    () => (filtersActive ? runs.filter(r => matchedRunIds.has(r.id)) : runs),
    [runs, filtersActive, matchedRunIds]
  );

  // Bucket the visible runs: running / completed / failed (pipeline-aware).
  const buckets = useMemo(() => {
    const b = { running: [], completed: [], failed: [] };
    visibleRuns.forEach(r => { b[statusBucketOf(r)].push(r); });
    // Within Running, surface the ones actually executing (a worker has picked
    // them up) above those still awaiting a worker (pending/queued). Stable —
    // preserves the incoming sort within each tier.
    b.running.sort((x, y) => {
      const rx = pipelineStage(x).state === 'running' ? 0 : 1;
      const ry = pipelineStage(y).state === 'running' ? 0 : 1;
      return rx - ry;
    });
    return b;
  }, [visibleRuns]);

  // Group config-classes by IS-end date → the "batches by date" branch strip.
  // Each distinct nominal IS-end window is a batch the user launched; surfacing
  // them as clickable branches (vs the hidden Config dropdown) makes the batches
  // read at a glance. Clicking a branch scopes the run list + Deploy basket to it.
  const isEndGroups = useMemo(() => {
    const m = new Map();
    cfgClasses.forEach((c) => {
      const k = c.is_end || '—';
      const g = m.get(k) || { is_end: k, run_count: 0, completed: 0, running: 0, failed: 0, deployable: 0, classes: 0 };
      g.run_count += c.run_count || 0;
      g.completed += c.completed || 0;
      g.running += c.running || 0;
      g.failed += c.failed || 0;
      g.deployable += c.deployable_count || 0;
      g.classes += 1;
      m.set(k, g);
    });
    return [...m.values()].sort((a, b) => (b.run_count - a.run_count));
  }, [cfgClasses]);

  // Tallies: loaded-run buckets once we have them, else the tree payload so the
  // header reads correctly before the runs land.
  const counts = loaded
    ? { running: buckets.running.length, completed: buckets.completed.length, failed: buckets.failed.length }
    : { running: grid.running || 0, completed: grid.completed || 0, failed: grid.failed || 0 };

  // Resume-failed: loop the existing per-run resume endpoint over this grid's
  // failed runs. Requires the runs to be loaded (so we know which IDs to hit).
  const resumeFailed = useCallback(async () => {
    const failed = buckets.failed;
    if (!failed.length) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Resume ${failed.length} failed run${failed.length !== 1 ? 's' : ''} in this grid?`)) return;
    setResuming(true);
    let ok = 0, bad = 0;
    for (const r of failed) {
      try { await tradingService.resumeOptimization(r.id); ok++; }
      catch { bad++; }
    }
    setResuming(false);
    showToast?.(`Resumed ${ok} run${ok !== 1 ? 's' : ''}${bad ? ` · ${bad} could not queue` : ''}`);
    fetchRuns();
  }, [buckets.failed, fetchRuns, showToast]);

  return (
    <div className={`rounded-lg border transition-colors ${expanded
      ? 'border-brand-gold/40 bg-white dark:bg-[#161b22]'
      : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22]'}`}>
      {/* ── Grid tile header — combo count + per-status tallies. Click to expand. ── */}
      <button
        onClick={onToggle}
        className="w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/30 rounded-lg transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className={`text-[10px] transition-transform ${expanded ? 'rotate-90' : ''} text-gray-400`}>▶</span>
          <span className="font-mono font-semibold text-brand-gold/90 text-sm tracking-wide">{fmtCombos(grid.total_combos)} combos</span>
          <span className="ml-auto flex items-center gap-2.5 text-[11px] font-mono tabular-nums">
            <span className="text-yellow-600 dark:text-yellow-500">⏳{counts.running}</span>
            <span className="text-green-600 dark:text-green-400">✓{counts.completed}</span>
            <span className="text-red-600 dark:text-red-400">✗{counts.failed}</span>
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-500 pl-4">
          <span>{grid.run_count} run{grid.run_count !== 1 ? 's' : ''}</span>
          <span>·</span>
          <span>{grid.symbol_count} stock{grid.symbol_count !== 1 ? 's' : ''}</span>
          {grid.grid_key !== 'nogrid' && (
            <span className="font-mono text-gray-400 dark:text-gray-600" title="Parameter-range fingerprint — identical ranges share this key">{grid.grid_key}</span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-800 pt-2.5 space-y-3">
          {/* ── Action row (left): resume failed now; investigate / bulk-deploy later. ── */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={resumeFailed}
              disabled={resuming || !loaded || counts.failed === 0}
              className="inline-flex items-center gap-1.5 text-xs text-brand-gold hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
              title={counts.failed ? `Re-queue ${counts.failed} failed run(s)` : 'No failed runs'}
            >
              <Glyph name={resuming ? 'spinner' : 'refresh'} className={`w-3.5 h-3.5 ${resuming ? 'animate-spin' : ''}`} />
              {resuming ? 'Resuming…' : `Resume failed${counts.failed ? ` (${counts.failed})` : ''}`}
            </button>
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-600 cursor-not-allowed" title="Coming soon">
              <Glyph name="info" className="w-3.5 h-3.5" /> Investigate failed
            </span>
            {/* ── Deploy basket: only legal when the active filters collapse the
                grid to ONE config-class with ≥1 deployable run. A deployable
                basket MUST be a single config-class (exact window/idle/etc). ── */}
            {(() => {
              const single = matchedClasses.length === 1;
              const deployable = single ? (matchedClasses[0].deployable_count || 0) : 0;
              const canDeploy = single && deployable > 0;
              const title = !cfgLoaded || cfgClasses.length === 0
                ? 'No config classes in this grid yet'
                : matchedClasses.length === 0
                  ? 'No runs match the current filters'
                  : matchedClasses.length > 1
                    ? `Narrow filters to a single config window (${matchedClasses.length} matched)`
                    : deployable === 0
                      ? 'No deployable (complete + saved) runs in this window'
                      : `Deploy ${deployable} cap-weighted name${deployable !== 1 ? 's' : ''} from this window`;
              return (
                <button
                  onClick={() => {
                    if (!canDeploy) return;
                    if (onDeployBasket) {
                      onDeployBasket({ strategyName, gridKey: grid.grid_key, configClass: matchedClasses[0] });
                    } else {
                      showToast?.('Deploy basket — wiring coming soon');
                    }
                  }}
                  disabled={!canDeploy}
                  className="inline-flex items-center gap-1.5 text-xs text-brand-gold hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:dark:text-gray-600"
                  title={title}
                >
                  <Glyph name="plus" className="w-3.5 h-3.5" /> Deploy basket{matchedClasses.length === 1 && deployable > 0 ? ` (${deployable})` : ''}
                </button>
              );
            })()}
          </div>

          {/* ── IS-end window branches: one row per distinct IS-end date (the
              "batch" unit). Click to scope the grid to that window (sets the
              is_end config filter); shows per-window run tallies + deployable
              count. Only when the grid spans >1 window (else the Config summary
              below already says it). ── */}
          {cfgLoaded && isEndGroups.length > 1 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">
                IS-end windows · {isEndGroups.length} batches
              </div>
              <div className="space-y-1 border-l-2 border-gray-200 dark:border-gray-800 pl-2">
                {isEndGroups.map((g) => {
                  const active = String(cfgFilters.is_end || '') === String(g.is_end);
                  return (
                    <button
                      key={g.is_end}
                      onClick={() => setCfgFilters((f) => ({ ...f, is_end: active ? '' : g.is_end }))}
                      className={`w-full text-left rounded-md px-2.5 py-1.5 flex items-center gap-2 text-[11px] border transition-colors ${
                        active
                          ? 'border-brand-gold/50 bg-brand-gold/10'
                          : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/40'
                      }`}
                      title={active ? 'Showing this window — click to clear' : 'Scope to this IS-end window'}
                    >
                      <span className={`font-mono ${active ? 'text-brand-gold font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>
                        IS ≤ {fmtFacetVal('is_end', g.is_end)}
                      </span>
                      <span className="text-gray-400">
                        · {g.run_count} run{g.run_count !== 1 ? 's' : ''}
                        {g.classes > 1 ? ` · ${g.classes} configs` : ''}
                      </span>
                      <span className="ml-auto flex items-center gap-2 font-mono tabular-nums">
                        <span className="text-green-600 dark:text-green-400">✓{g.completed}</span>
                        {g.running > 0 && <span className="text-yellow-600 dark:text-yellow-500">⏳{g.running}</span>}
                        {g.failed > 0 && <span className="text-red-600 dark:text-red-400">✗{g.failed}</span>}
                        {g.deployable > 0 && (
                          <span className="text-brand-gold/80" title="deployable (IS+OOS+NSR complete)">◆{g.deployable}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Run-config filter bar (Path B). Chips for facets that vary; a
              single-window grid shows a static summary instead. Narrowing to one
              config-class unlocks Deploy basket above. ── */}
          {cfgLoaded && cfgClasses.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-500">Config</span>
              {varyingFacets.length === 0 ? (
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  Single window · {fmtFacetVal('is_end', cfgClasses[0].is_end)} IS · {fmtFacetVal('idle', cfgClasses[0].idle)} idle
                </span>
              ) : (
                varyingFacets.map(([key, label]) => (
                  <FilterChip
                    key={key}
                    label={label}
                    value={cfgFilters[key] ? fmtFacetVal(key, cfgFilters[key]) : ''}
                    onChange={(v) => setCfgFilters(f => ({ ...f, [key]: v }))}
                    options={[
                      { value: '', label: 'All' },
                      ...(cfgFacets[key] || []).map(v => ({ value: v, label: fmtFacetVal(key, v) })),
                    ]}
                  />
                ))
              )}
              {filtersActive && (
                <button
                  onClick={() => setCfgFilters({})}
                  className="text-[11px] text-gray-400 hover:text-brand-gold transition-colors"
                >
                  Clear
                </button>
              )}
              <span className="ml-auto text-[11px] font-mono tabular-nums text-gray-500 dark:text-gray-400">
                {matchedClasses.length} window{matchedClasses.length !== 1 ? 's' : ''} · {matchedRunIds.size} run{matchedRunIds.size !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {loading && !loaded && (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
              <Glyph name="spinner" className="w-3.5 h-3.5" /> Loading runs…
            </div>
          )}
          {err && <div className="text-[11px] text-red-500 py-1">{err}</div>}

          {/* ── Status tiles: Running / Completed / Failed. Click to reveal list. ── */}
          {loaded && (
            <div className="grid grid-cols-3 gap-2">
              {STATUS_TILE_ORDER.map((bk) => {
                const meta = STATUS_GROUP_META[bk];
                const active = openStatus === bk;
                return (
                  <button
                    key={bk}
                    onClick={() => setOpenStatus(active ? null : bk)}
                    className={`rounded-lg border px-3 py-2.5 text-left transition-all ${active
                      ? 'border-brand-gold/50 bg-brand-gold/5'
                      : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#0d1117] hover:border-brand-gold/30'}`}
                  >
                    <div className={`text-[10px] uppercase tracking-wider font-semibold ${meta.tone}`}>{meta.glyph} {meta.label}</div>
                    <div className="mt-1 font-mono text-xl tabular-nums text-gray-900 dark:text-gray-100">{counts[bk]}</div>
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Run list for the open status bucket — flat, no pagination. ── */}
          {loaded && openStatus && (
            <div className="space-y-2.5 pt-1">
              {buckets[openStatus].length === 0 ? (
                <div className="text-xs text-gray-400 py-2">No {openStatus} runs.</div>
              ) : (
                buckets[openStatus].map((r) => (
                  <RunRow
                    key={r.id}
                    run={r}
                    onClick={() => onSelectRun(r.id)}
                    onDeleteClick={onDeleteClick}
                    onCancelClick={onCancelClick}
                    onReplicateClick={onReplicateClick}
                    onResumeParallelClick={onResumeParallelClick}
                  />
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Deploy-basket-from-tree modal (#76/#77) ──────────────────────────────────
// Materialize a live, cap-weighted portfolio from ONE config-class of a grid.
// Flow: config → dry-run preview → create plan → (pending sweep poll) → Deploy.
// `target` = { strategyName, gridKey, configClass } (the single matched class).
const fmtUSD = (n) =>
  (n == null || isNaN(n)) ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// Per-allocation-method preview progress copy. Keyed by optimizer_method so each
// method describes what its OWN preview is doing — cap-weight fetches fundamentals
// & liquidity and weights the stored best combo (no re-optimize, no HRP); sector
// cascade re-runs each strategy for within-sector HRP. Lookup with a safe fallback.
const PREVIEW_PROGRESS_COPY = {
  market_cap: {
    label: () => 'Fetching market caps & liquidity…',
    detail: 'Cap-weighting the stored best combo per name by market cap — fetching fundamentals & liquidity. No re-optimization.',
  },
  sector_cascade: {
    label: (starting) => (starting ? 'Loading price history…' : 'Pricing strategies'),
    detail: 'Re-running each strategy for within-sector HRP — this can take a few minutes for a large basket.',
  },
};
const previewProgressCopy = (m) => PREVIEW_PROGRESS_COPY[m] || PREVIEW_PROGRESS_COPY.market_cap;

const DeployBasketModal = ({ target, onClose, showToast }) => {
  const { strategyName, gridKey, configClass } = target || {};

  // phase: config → preview → pending → ready → deploying → done
  const [phase, setPhase] = useState('config');
  const [err, setErr] = useState('');

  // config inputs
  const [name, setName] = useState(`${strategyName || 'Basket'} — ${(configClass?.is_end || '').slice(0, 10) || 'window'}`);
  const [totalAlloc, setTotalAlloc] = useState('10000');
  const [minFloor, setMinFloor] = useState('500');

  // allocation method: 'market_cap' (cap-weight) or 'sector_cascade'
  const [method, setMethod] = useState('market_cap');
  const [showKnobs, setShowKnobs] = useState(false);
  const [knobs, setKnobs] = useState({
    theta: '1.0', cap_mult: '2.5', cap_abs: '0.50',
    mom_weight: '1.0', disp_weight: '1.0', window_months: '18', ewma_halflife: '63',
    name_cap_abs: '0.03', name_cap_mult: '2.5',
  });
  const setKnob = (k, v) => setKnobs(prev => ({ ...prev, [k]: v }));

  // IBKR accounts
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(null);
  const [liveConfirmed, setLiveConfirmed] = useState(false);

  // Deploy-time price rail for LIVE signal generation across the whole basket.
  // '' = follow each strategy's optimization source; 'raw' / 'consolidated' pin it.
  const [priceSource, setPriceSource] = useState('');

  // results
  const [preview, setPreview] = useState(null);   // dry-run preview payload
  const [plan, setPlan] = useState(null);          // created plan payload
  const [deployResult, setDeployResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(null); // {done,total,symbol,phase}

  useEffect(() => {
    let cancelled = false;
    tradingService.listIBKRAccounts()
      .then(res => {
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setAccounts(data);
        const def = data.find(a => a.is_default) || data[0] || null;
        setAccountId(def ? def.id : null);
      })
      .catch(() => { if (!cancelled) setAccounts([]); });
    return () => { cancelled = true; };
  }, []);

  const selectedAccount = accounts.find(a => a.id === accountId) || null;
  const deployingToLive = selectedAccount && !selectedAccount.is_paper;

  const cfgBody = () => ({
    grid_key: gridKey,
    strategy: strategyName,
    config_class: configClass,
    total_allocation: parseFloat(totalAlloc) || 0,
    min_floor: parseFloat(minFloor) || 0,
    // Allocation method + cascade knobs. Knobs are always sent; the cap-weight
    // backend path ignores them, the sector-cascade path reads them.
    optimizer_method: method,
    theta: parseFloat(knobs.theta) || 0,
    cap_mult: parseFloat(knobs.cap_mult) || 0,
    cap_abs: parseFloat(knobs.cap_abs) || 0,
    mom_weight: parseFloat(knobs.mom_weight) || 0,
    disp_weight: parseFloat(knobs.disp_weight) || 0,
    window_months: parseInt(knobs.window_months, 10) || 18,
    ewma_halflife: parseFloat(knobs.ewma_halflife) || 63,
    // Per-name cap → sector-ETF spill. name_cap_abs=0 disables (uncapped book).
    name_cap_abs: parseFloat(knobs.name_cap_abs) || 0,
    name_cap_mult: parseFloat(knobs.name_cap_mult) || 0,
  });

  const apiErr = (e) => e?.response?.data?.error || e?.message || 'Request failed.';

  const runPreview = async () => {
    const alloc = parseFloat(totalAlloc);
    if (isNaN(alloc) || alloc <= 0) { setErr('Total allocation must be a positive number.'); return; }
    setBusy(true); setErr(''); setPreviewProgress(null);
    try {
      const res = await tradingService.previewPortfolioFromTree(cfgBody(), setPreviewProgress);
      setPreview(res.data);
      setPhase('preview');
    } catch (e) { setErr(apiErr(e)); }
    finally { setBusy(false); setPreviewProgress(null); }
  };

  // Poll a pending plan until it leaves pending_optimization.
  useEffect(() => {
    if (phase !== 'pending' || !plan?.id) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await tradingService.getPortfolioFromTreePlan(plan.id);
        if (cancelled) return;
        const p = res.data;
        setPlan(prev => ({ ...prev, ...p }));
        if (p.lifecycle_state === 'ready_to_deploy') setPhase('ready');
        else if (p.lifecycle_state === 'failed') { setErr(p.status_detail || 'The optimization sweep failed.'); setPhase('config'); }
      } catch (_e) { /* transient — keep polling */ }
    };
    const id = setInterval(tick, 5000);
    tick();
    return () => { cancelled = true; clearInterval(id); };
  }, [phase, plan?.id]);

  const createPlan = async () => {
    setBusy(true); setErr('');
    try {
      const res = await tradingService.createPortfolioFromTree({
        ...cfgBody(),
        name: name.trim() || undefined,
        ...(accountId ? { account_id: accountId } : {}),
      });
      const p = res.data;
      setPlan(p);
      if (p.lifecycle_state === 'ready_to_deploy') setPhase('ready');
      else setPhase('pending');  // pending_optimization → poll
    } catch (e) { setErr(apiErr(e)); }
    finally { setBusy(false); }
  };

  const runDeploy = async () => {
    if (deployingToLive && !liveConfirmed) { setErr('Confirm you intend to deploy to a real-money account.'); return; }
    setBusy(true); setErr(''); setPhase('deploying');
    try {
      const res = await tradingService.deployPortfolioFromTree(plan.id, {
        name: name.trim() || undefined,
        ...(priceSource ? { live_source_override: priceSource } : {}),
      });
      setDeployResult(res.data);
      setPhase('done');
      showToast?.(`Deployed ${res.data.deployed_count} name(s) · ${res.data.signals_dispatched ?? 0} order(s) ${res.data.signals_held_until ? 'staged at 09:31 ET (after the live pre-open release)' : 'staged'}`);
    } catch (e) {
      // 400 = not enough deployable names (plan stays ready); 409 = busy/pending.
      setErr(apiErr(e));
      setPhase('ready');
    } finally { setBusy(false); }
  };

  const dropEntries = preview ? Object.entries(preview.dropped || {}) : [];
  const isCascade = preview?.weight_basis === 'sector_cascade';
  // Create is blocked only when the basket can't reach 2 live members AND there's
  // nothing to sweep. Cap-weight counts deployable names; cascade counts strategy
  // positions + ETF floors (its deployable member set).
  const cascadeMemberCount = isCascade
    ? ((preview?.members || []).length + (preview?.etf_floor_members || []).length)
    : 0;
  const createBlocked = preview
    ? (isCascade
        ? (cascadeMemberCount < 2 && (preview.missing_count || 0) === 0)
        : (preview.deployable_count < 2 && preview.missing_count === 0))
    : true;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-2xl my-8 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0d1117] shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h3 className="text-base font-serif text-gray-900 dark:text-gray-100">Deploy basket</h3>
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">{strategyName} · {method === 'sector_cascade' ? 'sector cascade' : 'cap-weight'} · single config window</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <Glyph name="x" className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Config-window summary */}
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div><span className="text-gray-500">Idle</span> <span className="text-gray-800 dark:text-gray-200">{configClass?.idle || '—'}</span></div>
            <div><span className="text-gray-500">Maximize</span> <span className="text-gray-800 dark:text-gray-200">{configClass?.maximize || '—'}</span></div>
            <div><span className="text-gray-500">IS ≤</span> <span className="text-gray-800 dark:text-gray-200">{(configClass?.is_end || '—').slice(0, 10)}</span></div>
            <div><span className="text-gray-500">OOS end</span> <span className="text-gray-800 dark:text-gray-200">{(configClass?.oos_end || '—').slice(0, 10)}</span></div>
            <div><span className="text-gray-500">Deployable</span> <span className="text-brand-gold">{configClass?.deployable_count ?? 0} name(s)</span></div>
            <div><span className="text-gray-500">OOS split</span> <span className="text-gray-800 dark:text-gray-200">{configClass?.oos_split_pct ?? 0}%</span></div>
          </div>

          {err && (
            <div className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-xs text-red-700 dark:text-red-300">
              {err}
            </div>
          )}

          {/* live preview progress — the sector-cascade preview re-runs a backtest per
              name, so at ~500 names this takes minutes; show a real bar, not a blank spinner. */}
          {busy && previewProgress && (() => {
            const { done = 0, total = 0, symbol, phase: pphase } = previewProgress;
            const pctDone = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : null;
            const copy = previewProgressCopy(method);
            return (
              <div className="rounded-md border border-brand-gold/30 bg-brand-gold/5 px-3 py-2.5">
                <div className="flex items-center justify-between text-[11px] mb-1.5">
                  <span className="text-gray-600 dark:text-gray-300 font-medium">
                    {copy.label(pphase === 'starting')}
                    {symbol ? <span className="text-gray-400"> · {symbol}</span> : null}
                  </span>
                  <span className="tabular-nums text-brand-gold font-semibold">
                    {total > 0 ? `${done}/${total}${pctDone != null ? ` · ${pctDone}%` : ''}` : '…'}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
                  <div
                    className="h-full bg-brand-gold transition-all duration-300"
                    style={{ width: pctDone != null ? `${pctDone}%` : '15%' }}
                  />
                </div>
                <div className="text-[10px] text-gray-400 mt-1.5">
                  {copy.detail} You can leave this open.
                </div>
              </div>
            );
          })()}

          {/* ── Phase: config ─────────────────────────────────────────────── */}
          {phase === 'config' && (
            <div className="space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-gray-500">Portfolio name</span>
                <input value={name} onChange={e => setName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161b22] px-3 py-2 text-sm text-gray-900 dark:text-gray-100" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-gray-500">Total allocation (USD)</span>
                  <input value={totalAlloc} onChange={e => setTotalAlloc(e.target.value)} inputMode="decimal"
                    className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161b22] px-3 py-2 text-sm text-gray-900 dark:text-gray-100" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-gray-500">Min per-name floor (USD)</span>
                  <input value={minFloor} onChange={e => setMinFloor(e.target.value)} inputMode="decimal"
                    className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161b22] px-3 py-2 text-sm text-gray-900 dark:text-gray-100" />
                </label>
              </div>

              {/* Allocation method */}
              <div>
                <span className="text-[10px] uppercase tracking-wider text-gray-500">Allocation method</span>
                <div className="mt-1 flex gap-2">
                  {[['market_cap', 'Cap-weight'], ['sector_cascade', 'Sector cascade']].map(([v, lbl]) => (
                    <button key={v} type="button" onClick={() => setMethod(v)}
                      className={`flex-1 rounded-md border px-3 py-2 text-xs transition-colors ${method === v ? 'border-brand-gold text-brand-gold bg-brand-gold/10' : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-800'}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price source — basket-wide LIVE signal rail. Blank = each strategy keeps its
                  optimization run's source; Consolidated/Raw pins EVERY member to that rail
                  (stamped as live_source_override, honored by _live_signal_source). No re-optimize. */}
              <div>
                <span className="text-[10px] uppercase tracking-wider text-gray-500">Price source (live signals)</span>
                <div className="mt-1 flex gap-2">
                  {[['', "Follow each run"], ['consolidated', 'Consolidated'], ['raw', 'Raw']].map(([v, lbl]) => (
                    <button key={v || 'follow'} type="button" onClick={() => setPriceSource(v)}
                      className={`flex-1 rounded-md border px-3 py-2 text-xs transition-colors ${priceSource === v ? 'border-brand-gold text-brand-gold bg-brand-gold/10' : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-800'}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  {priceSource === 'raw'
                    ? 'Pins every member to the RAW total-return ledger for live signal generation. Names without raw coverage fall back to the default rail automatically.'
                    : priceSource === 'consolidated'
                    ? 'Pins every member to the CONSOLIDATED vendor tape for live signal generation.'
                    : 'Each strategy keeps the price rail from its optimization run (no override).'}
                </p>
              </div>

              {method === 'sector_cascade' && (
                <div className="rounded-md border border-gray-200 dark:border-gray-800 p-3 space-y-2">
                  <button type="button" onClick={() => setShowKnobs(s => !s)} className="text-[11px] text-gray-500 hover:text-brand-gold">
                    {showKnobs ? '▾' : '▸'} Advanced knobs (θ, sector + name caps, momentum/dispersion, window)
                  </button>
                  {showKnobs && (
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        ['theta', 'θ aggressiveness'], ['cap_mult', 'Sector rel cap ×anchor'], ['cap_abs', 'Sector abs cap (0=off)'],
                        ['name_cap_abs', 'Name abs cap (0=off)'], ['name_cap_mult', 'Name cap ×SPX wt'],
                        ['mom_weight', 'Momentum wt'], ['disp_weight', 'Dispersion wt'], ['window_months', 'HRP window (mo)'],
                        ['ewma_halflife', 'EWMA halflife (d)'],
                      ].map(([k, lbl]) => (
                        <label key={k} className="block">
                          <span className="text-[9px] uppercase tracking-wider text-gray-500">{lbl}</span>
                          <input value={knobs[k]} onChange={e => setKnob(k, e.target.value)} inputMode="decimal"
                            className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161b22] px-2 py-1 text-xs text-gray-900 dark:text-gray-100" />
                        </label>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-gray-500">S&amp;P-anchored sector tilt × within-sector HRP. Idle → sector ETF; uncovered sectors held as ETFs. Per-name cap = max(name abs cap, name×SPX wt); overflow spills into the name's sector ETF. Recomputes on rebalance (not deploy-and-forget). Preview may take a minute — it re-runs each strategy.</p>
                </div>
              )}

              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-gray-500">IBKR account</span>
                <select value={accountId ?? ''} onChange={e => { setAccountId(Number(e.target.value) || null); setLiveConfirmed(false); }}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161b22] px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                  {accounts.length === 0 && <option value="">No accounts — uses your default</option>}
                  {accounts.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name} · {a.account_id} {a.is_model ? '(EXT BOOK)' : a.is_paper ? '(Paper)' : '(LIVE)'}{a.is_default ? ' — default' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <p className="text-[11px] text-gray-500">
                Cap-weighted across deployable names (need ≥2). Names below the floor are dropped and the rest renormalize. Weights freeze at deploy.
              </p>
            </div>
          )}

          {/* ── Phase: preview ────────────────────────────────────────────── */}
          {phase === 'preview' && preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center text-[11px]">
                <div className="rounded-md bg-gray-50 dark:bg-[#161b22] py-2">
                  <div className="text-gray-500 uppercase tracking-wider text-[9px]">Covered</div>
                  <div className="text-green-600 dark:text-green-400 text-sm font-semibold">{preview.covered_count}</div>
                </div>
                <div className="rounded-md bg-gray-50 dark:bg-[#161b22] py-2">
                  <div className="text-gray-500 uppercase tracking-wider text-[9px]">Missing</div>
                  <div className={`text-sm font-semibold ${preview.missing_count > 0 ? 'text-amber-500' : 'text-gray-400'}`}>{preview.missing_count}</div>
                </div>
                <div className="rounded-md bg-gray-50 dark:bg-[#161b22] py-2">
                  <div className="text-gray-500 uppercase tracking-wider text-[9px]">Deployable</div>
                  <div className="text-brand-gold text-sm font-semibold">{preview.deployable_count}</div>
                </div>
              </div>

              {preview.missing_count > 0 && (
                <div className="rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
                  {preview.missing_count} name(s) not yet optimized — creating the plan will launch a sweep for just those, then unlock Deploy: {preview.missing_symbols.slice(0, 12).join(', ')}{preview.missing_symbols.length > 12 ? '…' : ''}
                </div>
              )}

              {(preview.excluded_pending_validation_count || 0) > 0 && (
                <div className="rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-[#161b22] px-3 py-2 text-[11px] text-gray-600 dark:text-gray-400">
                  {preview.excluded_pending_validation_count} name(s) finished in-sample but are still validating (OOS/NSR) — excluded from this basket so the previewed weights match exactly what deploys. They'll qualify once validation completes: {(preview.excluded_pending_validation || []).slice(0, 12).join(', ')}{(preview.excluded_pending_validation || []).length > 12 ? '…' : ''}
                </div>
              )}

              {!isCascade && (preview.members.length > 0 ? (
                <div className="rounded-md border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-50 dark:bg-[#161b22] text-gray-500 uppercase tracking-wider text-[9px]">
                      <tr><th className="text-left px-3 py-1.5">Symbol</th><th className="text-right px-3 py-1.5">Mkt cap</th><th className="text-right px-3 py-1.5">Weight</th><th className="text-right px-3 py-1.5">Alloc</th></tr>
                    </thead>
                    <tbody>
                      {preview.members.map(m => (
                        <tr key={m.symbol} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200 font-medium">{m.symbol}</td>
                          <td className="px-3 py-1.5 text-right text-gray-600 dark:text-gray-400">{m.market_cap ? fmtUSD(m.market_cap) : '—'}</td>
                          <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200">{(m.weight * 100).toFixed(1)}%</td>
                          <td className="px-3 py-1.5 text-right text-green-600 dark:text-green-400">{fmtUSD(m.allocation_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 dark:bg-[#161b22] text-[10px]">
                      <tr><td className="px-3 py-1.5 text-gray-500 uppercase tracking-wider" colSpan={3}>Allocated</td><td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200">{fmtUSD(preview.total_allocated)} / {fmtUSD(preview.total_allocation)}</td></tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-xs text-gray-500 text-center py-3">No deployable members projected yet.</div>
              ))}

              {/* ── Sector-cascade preview: strategy members + ETF floors ── */}
              {isCascade && (
                <div className="space-y-3">
                  <div className="rounded-md border border-gray-200 dark:border-gray-800 overflow-hidden">
                    <div className="px-3 py-1.5 bg-gray-50 dark:bg-[#161b22] text-[9px] uppercase tracking-wider text-gray-500">Strategy positions · sector × HRP</div>
                    <table className="w-full text-[11px]">
                      <thead className="bg-gray-50 dark:bg-[#161b22] text-gray-500 uppercase tracking-wider text-[9px]">
                        <tr><th className="text-left px-3 py-1.5">Symbol</th><th className="text-left px-3 py-1.5">Sector</th><th className="text-right px-3 py-1.5">Weight</th><th className="text-right px-3 py-1.5">Alloc</th></tr>
                      </thead>
                      <tbody>
                        {(preview.members || []).map(m => (
                          <tr key={m.symbol} className="border-t border-gray-100 dark:border-gray-800">
                            <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200 font-medium">
                              {m.symbol}
                              {m.capped && <span title="Capped — excess spilled into the sector ETF" className="ml-1.5 rounded bg-amber-100 dark:bg-amber-900/40 px-1 py-0.5 text-[8px] uppercase tracking-wider text-amber-700 dark:text-amber-400">cap</span>}
                            </td>
                            <td className="px-3 py-1.5 text-gray-500">{m.sector}</td>
                            <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200">{(m.weight * 100).toFixed(2)}%</td>
                            <td className="px-3 py-1.5 text-right text-green-600 dark:text-green-400">{fmtUSD(m.allocation_usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {(preview.etf_floor_members || []).length > 0 && (
                    <div className="rounded-md border border-gray-200 dark:border-gray-800 overflow-hidden">
                      <div className="px-3 py-1.5 bg-gray-50 dark:bg-[#161b22] text-[9px] uppercase tracking-wider text-gray-500">Sector-ETF holdings · uncovered sectors (pure beta)</div>
                      <table className="w-full text-[11px]">
                        <tbody>
                          {preview.etf_floor_members.map(m => (
                            <tr key={m.symbol} className="border-t border-gray-100 dark:border-gray-800">
                              <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200 font-medium">{m.symbol}</td>
                              <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200">{(m.weight * 100).toFixed(2)}%</td>
                              <td className="px-3 py-1.5 text-right text-blue-600 dark:text-blue-400">{fmtUSD(m.allocation_usd)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="flex justify-between text-[10px] text-gray-500 px-1">
                    <span>{(preview.members || []).length} strategies · {(preview.etf_floor_members || []).length} ETF floors</span>
                    <span>Allocated {fmtUSD(preview.total_allocated)} / {fmtUSD(preview.total_allocation)}</span>
                  </div>
                </div>
              )}

              {dropEntries.length > 0 && (
                <div className="text-[11px] text-gray-500">
                  <span className="uppercase tracking-wider text-[9px]">Dropped:</span>{' '}
                  {dropEntries.map(([s, r]) => `${s} (${r})`).join(', ')}
                </div>
              )}

              {!isCascade && preview.deployable_count < 2 && preview.missing_count === 0 && (
                <div className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-[11px] text-red-700 dark:text-red-300">
                  Need at least 2 deployable names to build a portfolio. Add names or lower the floor.
                </div>
              )}
            </div>
          )}

          {/* ── Phase: pending (sweep running) ────────────────────────────── */}
          {phase === 'pending' && (
            <div className="text-center py-6 space-y-2">
              <Glyph name="spinner" className="w-6 h-6 mx-auto animate-spin text-brand-gold" />
              <div className="text-sm text-gray-700 dark:text-gray-300">Optimizing missing names…</div>
              <div className="text-[11px] text-gray-500">{plan?.status_detail || 'This can take a few minutes. The Deploy button unlocks automatically when the sweep settles.'}</div>
            </div>
          )}

          {/* ── Phase: ready ──────────────────────────────────────────────── */}
          {phase === 'ready' && (
            <div className="space-y-3">
              <div className="rounded-md border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950/30 px-3 py-2 text-xs text-green-700 dark:text-green-300">
                Plan ready. Deploy materializes live strategies, activates them, and stages cap-weighted orders for the next market open.
              </div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-gray-500">Portfolio name</span>
                <input value={name} onChange={e => setName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161b22] px-3 py-2 text-sm text-gray-900 dark:text-gray-100" />
                <span className="text-[10px] text-gray-400">Rename here if the name is taken, then Deploy.</span>
              </label>
              {deployingToLive && (
                <label className="flex items-start gap-2 text-[11px] text-red-600 dark:text-red-400">
                  <input type="checkbox" checked={liveConfirmed} onChange={e => setLiveConfirmed(e.target.checked)} className="mt-0.5" />
                  <span>This is a <strong>real-money</strong> account ({selectedAccount.account_id}). Orders staged will use real funds.</span>
                </label>
              )}
            </div>
          )}

          {/* ── Phase: deploying ──────────────────────────────────────────── */}
          {phase === 'deploying' && (
            <div className="text-center py-6 space-y-2">
              <Glyph name="spinner" className="w-6 h-6 mx-auto animate-spin text-brand-gold" />
              <div className="text-sm text-gray-700 dark:text-gray-300">Materializing portfolio &amp; staging orders…</div>
            </div>
          )}

          {/* ── Phase: done ───────────────────────────────────────────────── */}
          {phase === 'done' && deployResult && (
            <div className="space-y-3">
              <div className="rounded-md border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950/30 px-3 py-3 text-center">
                <Glyph name="check" className="w-6 h-6 mx-auto text-green-500" />
                <div className="text-sm text-gray-800 dark:text-gray-200 mt-1">Deployed {deployResult.deployed_count} name(s)</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{deployResult.signals_dispatched ?? 0} order(s) {deployResult.signals_held_until ? 'staged at 09:31 ET (after the live pre-open release)' : 'staged for next open'} · portfolio #{deployResult.portfolio_id}</div>
              </div>
              {deployResult.dropped && Object.keys(deployResult.dropped).length > 0 && (
                <div className="text-[11px] text-gray-500">
                  <span className="uppercase tracking-wider text-[9px]">Dropped:</span>{' '}
                  {Object.entries(deployResult.dropped).map(([s, r]) => `${s} (${r})`).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-800">
          {phase === 'config' && (
            <>
              <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Cancel</button>
              <button onClick={runPreview} disabled={busy}
                className="px-4 py-1.5 text-xs rounded-md bg-brand-gold/15 text-brand-gold border border-brand-gold/40 hover:bg-brand-gold/25 disabled:opacity-40">
                {busy ? 'Previewing…' : 'Preview'}
              </button>
            </>
          )}
          {phase === 'preview' && (
            <>
              <button onClick={() => { setPhase('config'); setErr(''); }} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Back</button>
              <button onClick={createPlan}
                disabled={busy || (createBlocked)}
                className="px-4 py-1.5 text-xs rounded-md bg-brand-gold/15 text-brand-gold border border-brand-gold/40 hover:bg-brand-gold/25 disabled:opacity-40">
                {busy ? 'Creating…' : (preview.missing_count > 0 ? 'Create plan & optimize missing' : 'Create plan')}
              </button>
            </>
          )}
          {phase === 'pending' && (
            <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Close (keeps running)</button>
          )}
          {phase === 'ready' && (
            <>
              <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Later</button>
              <button onClick={runDeploy} disabled={busy || (deployingToLive && !liveConfirmed)}
                className="px-4 py-1.5 text-xs rounded-md bg-brand-gold text-black font-medium hover:bg-brand-gold/90 disabled:opacity-40">
                Deploy basket
              </button>
            </>
          )}
          {phase === 'done' && (
            <button onClick={onClose} className="px-4 py-1.5 text-xs rounded-md bg-brand-gold/15 text-brand-gold border border-brand-gold/40 hover:bg-brand-gold/25">Done</button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── 'By Bucket' scope ────────────────────────────────────────────────────────
// A config-class = the EXACT (strategy, params, window incl. OOS cutoff, maximize,
// idle, source) tuple; every run in it differs only by symbol, so it is ONE
// date-aligned, allocator-ready cohort. The OOS cutoff (oos_end) is the headline —
// it's the binding key that must match across buckets for the capital allocator to
// compare their curves. "Stop all pending" cancels the owning batch(es) through the
// existing cooperative batch-cancel (never a hard kill; completed runs untouched).
const bucketLabel = (c) => {
  const cut = (c.oos_end || '').slice(0, 10) || '—';
  const parts = [`OOS→${cut}`, `max ${c.maximize || '—'}`, c.source || '—'];
  if (c.idle && c.idle !== 'cash') parts.push(`idle ${c.idle}`);
  return parts.join(' · ');
};

const BucketCard = ({ cls, onStop, stopping }) => {
  const [open, setOpen] = useState(false);
  const pending = cls.pending || 0;
  const deployable = cls.deployable_count || 0;
  const total = cls.run_count || 0;
  const completed = cls.completed || 0;
  const symbols = cls.symbols || [];
  const depSet = new Set(cls.deployable_symbols || []);
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22]">
      <div className="w-full px-3 py-2.5 flex items-center gap-2">
        <button onClick={() => setOpen(o => !o)} className="min-w-0 flex-1 text-left flex items-start gap-2">
          <span className={`mt-0.5 text-[10px] transition-transform ${open ? 'rotate-90' : ''} text-gray-400`}>▶</span>
          <span className="min-w-0">
            <span className="block font-mono text-[12px] text-gray-800 dark:text-gray-200 truncate">{bucketLabel(cls)}</span>
            <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] font-mono tabular-nums">
              <span className="text-brand-gold">{deployable} deployable</span>
              <span className="text-green-600 dark:text-green-400">✓{completed}</span>
              <span className="text-yellow-600 dark:text-yellow-500">⏳{pending} pending</span>
              <span className="text-gray-500">/ {total} total</span>
            </span>
          </span>
        </button>
        {pending > 0 && (
          <button
            onClick={() => onStop(cls)}
            disabled={stopping}
            className="shrink-0 text-[11px] px-2 py-1 rounded border border-red-400/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
            title="Cancel every pending run in this bucket (cooperative; completed runs untouched)"
          >
            {stopping ? 'Stopping…' : `Stop ${pending} pending`}
          </button>
        )}
      </div>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800 text-[11px] text-gray-600 dark:text-gray-400">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 my-2 font-mono">
            <div><span className="text-gray-400">IS start</span> {(cls.intended_start || '—').slice(0, 10)}</div>
            <div><span className="text-gray-400">IS end</span> {(cls.is_end || '—').slice(0, 10)}</div>
            <div><span className="text-gray-400">OOS end</span> <span className="text-brand-gold">{(cls.oos_end || '—').slice(0, 10)}</span></div>
            <div><span className="text-gray-400">OOS split</span> {cls.oos_split_pct ?? 0}%</div>
          </div>
          <div className="text-gray-400 mb-1">{symbols.length} stock(s) · deployable highlighted</div>
          <div className="flex flex-wrap gap-1">
            {symbols.map(s => (
              <span key={s} className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${depSet.has(s) ? 'bg-brand-gold/15 text-brand-gold' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>{s}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const BucketView = ({ strategies, showToast, onBumpRefresh }) => {
  const [openStrat, setOpenStrat] = useState(null);
  const [classesByStrat, setClassesByStrat] = useState({});   // strategy_name -> [class]
  const [loadingStrat, setLoadingStrat] = useState(null);
  const [stopping, setStopping] = useState(null);             // config_class_key in-flight

  const loadStrategy = useCallback(async (st) => {
    const name = st.strategy_name;
    setLoadingStrat(name);
    try {
      const grids = st.grids || [];
      const results = await Promise.all(grids.map(g =>
        tradingService.getConfigClasses({ grid_key: g.grid_key, strategy: name })
          .then(r => r.data?.classes || [])
          .catch(() => [])
      ));
      const flat = results.flat();
      // Most-recent cutoff first, then most-populated — the buckets you'd allocate.
      flat.sort((a, b) =>
        (b.oos_end || '').localeCompare(a.oos_end || '') ||
        (b.run_count || 0) - (a.run_count || 0));
      setClassesByStrat(prev => ({ ...prev, [name]: flat }));
    } finally {
      setLoadingStrat(null);
    }
  }, []);

  const toggleStrat = (st) => {
    const name = st.strategy_name;
    if (openStrat === name) { setOpenStrat(null); return; }
    setOpenStrat(name);
    if (!classesByStrat[name]) loadStrategy(st);
  };

  const stopPending = async (st, cls) => {
    const ids = cls.batch_ids || [];
    if (!ids.length) { showToast && showToast('No batch found for this bucket to cancel'); return; }
    setStopping(cls.config_class_key);
    try {
      await Promise.all(ids.map(id => tradingService.cancelOptimizeBatch(id)));
      showToast && showToast(`Stopping ${cls.pending} pending run(s)…`);
      await loadStrategy(st);            // reflect the cancel in this bucket's counts
      onBumpRefresh && onBumpRefresh();  // let the by-run tree reload too
    } catch (e) {
      showToast && showToast(e.response?.data?.error || 'Cancel failed');
    } finally {
      setStopping(null);
    }
  };

  const list = strategies || [];
  if (list.length === 0) {
    return <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">No optimizations yet.</div>;
  }

  return (
    <div className="space-y-2">
      <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">
        Each bucket is a config-class — one date-aligned cohort (same window, OOS cutoff, maximize, source).
        The <span className="text-brand-gold">OOS cutoff</span> is the allocator's alignment key.
      </div>
      {list.map(st => {
        const name = st.strategy_name;
        const isOpen = openStrat === name;
        const classes = classesByStrat[name];
        return (
          <div key={name} className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/40 dark:bg-[#12161c]">
            <button onClick={() => toggleStrat(st)} className="w-full text-left px-3 py-2.5 flex items-center gap-2">
              <span className={`text-[10px] transition-transform ${isOpen ? 'rotate-90' : ''} text-gray-400`}>▶</span>
              <span className="font-serif text-sm text-gray-900 dark:text-gray-100 capitalize">{(name || '').replace(/_/g, ' ')}</span>
              <span className="text-[11px] text-gray-500 tabular-nums">{st.run_count || 0} runs · {st.symbol_count || 0} stocks</span>
              {classes && <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">{classes.length} bucket{classes.length !== 1 ? 's' : ''}</span>}
            </button>
            {isOpen && (
              <div className="px-3 pb-3 space-y-2">
                {loadingStrat === name && !classes && (
                  <div className="flex items-center gap-2 py-4 text-xs text-gray-500"><Glyph name="spinner" className="w-4 h-4" /> Loading buckets…</div>
                )}
                {classes && classes.length === 0 && <div className="py-3 text-xs text-gray-500">No config-classes in this strategy.</div>}
                {(classes || []).map(cls => (
                  <BucketCard
                    key={cls.config_class_key}
                    cls={cls}
                    stopping={stopping === cls.config_class_key}
                    onStop={(c) => stopPending(st, c)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const OptimizationsList = ({ onSelectRun, showToast }) => {
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Server-aggregated strategy → grid tree (drives the tile navigation in the
  // 'by-run' scope). All strategies/grids, not one page — leaf runs are paged
  // lazily inside each GridBranch. refreshKey bumps force open branches + the
  // tree to reload after a delete/cancel/resume mutation.
  const [tree, setTree] = useState({ strategies: [], total: 0, symbols: [], idle_deployments: [] });
  const [treeLoading, setTreeLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Default status to '' (All) — defaulting to 'complete' hid running/pending
  // runs on page refresh, so a user who launched a run and refreshed would
  // think it had been deleted. Users who want only completes can pick that
  // filter explicitly.
  const [filters, setFilters] = useState({
    symbol: '', strategy: '', idle: '', status: '', date: '',
  });
  const [sort, setSort] = useState('-created_at');
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState('by-run');
  const [aboutOpen, setAboutOpen] = useState(false);
  // Drill-down selection for the by-run tiles (single-select at each level):
  // strategy tile → grid tiles → status tiles → flat run list.
  const [openStrategy, setOpenStrategy] = useState(null);  // strategy key
  const [openGrid, setOpenGrid] = useState(null);          // grid key

  // By Combo state
  const [combos, setCombos] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [comboLoading, setComboLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Manual launcher state
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [launcherKind, setLauncherKind] = useState('standard');

  // Active universe sweeps launched this session — rendered as aggregate tiles
  // that expand to their member grid (replaces the retired Backtest tab).
  const [sweeps, setSweeps] = useState([]);

  // Delete state — confirm modal + in-flight tracking
  const [deleteTarget, setDeleteTarget] = useState(null);  // OptimizationResult row
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Replicate state
  const [replicateTarget, setReplicateTarget] = useState(null);  // run to replicate

  // Deploy-basket-from-tree target — { strategyName, gridKey, configClass } for a
  // single matched config-class. Opens the cap-weight deploy modal (#76/#77).
  const [deployBasketTarget, setDeployBasketTarget] = useState(null);

  // Cancel state — confirm modal + in-flight tracking
  const [cancelTarget, setCancelTarget] = useState(null);  // OptimizationResult row
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  // Resume-as-parallel state — confirm modal + in-flight tracking
  const [resumeParallelTarget, setResumeParallelTarget] = useState(null);
  const [resumingParallel, setResumingParallel] = useState(false);
  const [resumeParallelError, setResumeParallelError] = useState(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await tradingService.deleteOptimization(deleteTarget.id);
      // Optimistically remove from the visible list, then refresh
      setRuns(prev => prev.filter(r => r.id !== deleteTarget.id));
      setTotal(prev => Math.max(0, prev - 1));
      setDeleteTarget(null);
      // Re-sync: bump refreshKey (reloads tiles + any open grid branch) and,
      // in by-combo scope, re-page the flat run list.
      setRefreshKey(k => k + 1);
      if (scope === 'by-combo') fetchRuns();
    } catch (e) {
      setDeleteError(e.response?.data?.error || e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await tradingService.cancelOptimization(cancelTarget.id);
      // Optimistically update status in the list so the row reacts immediately
      setRuns(prev => prev.map(r => r.id === cancelTarget.id ? { ...r, status: 'failed' } : r));
      setCancelTarget(null);
      // Re-fetch authoritative state: tiles + open branch, and flat list if shown.
      setRefreshKey(k => k + 1);
      if (scope === 'by-combo') fetchRuns();
    } catch (e) {
      setCancelError(e.response?.data?.error || e.message || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  const confirmResumeParallel = async () => {
    if (!resumeParallelTarget) return;
    setResumingParallel(true);
    setResumeParallelError(null);
    try {
      const res = await tradingService.resumeAsParallelOptimization(resumeParallelTarget.id);
      const info = res.data?.spot_worker_info || {};
      const launched = info.launched;
      setResumeParallelTarget(null);
      setFilters(f => ({ ...f, status: '' }));
      setPage(1);
      setRefreshKey(k => k + 1);
      if (scope === 'by-combo') fetchRuns();
      showToast(
        launched
          ? `Run #${resumeParallelTarget.id} → ☁ Spot worker launching · remaining combos resuming`
          : `Run #${resumeParallelTarget.id} dispatched to parallel queue (spot may already be up)`
      );
    } catch (e) {
      setResumeParallelError(e.response?.data?.error || e.message || 'Resume failed');
    } finally {
      setResumingParallel(false);
    }
  };

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };

  const offset = (page - 1) * PAGE_SIZE;

  // ── 'by-run' scope: aggregated strategy → grid tree (all of it, not a page).
  // Leaf runs are loaded lazily per grid by GridBranch.
  const fetchTree = useCallback(async () => {
    setTreeLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.symbol)   params.symbol = filters.symbol;
      if (filters.strategy) params.strategy = filters.strategy;
      if (filters.idle)     params.idle_deployment = filters.idle;
      if (filters.status)   params.status = filters.status;
      if (filters.date) {
        const days = parseInt(filters.date, 10);
        if (!isNaN(days)) params.created_after = new Date(Date.now() - days * 86400000).toISOString();
      }
      const res = await tradingService.getOptimizationsTree(params);
      setTree(res.data || { strategies: [], total: 0, symbols: [], idle_deployments: [] });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setTree({ strategies: [], total: 0, symbols: [], idle_deployments: [] });
    } finally {
      setTreeLoading(false);
    }
  }, [filters.symbol, filters.strategy, filters.idle, filters.status, filters.date]);

  useEffect(() => {
    // by-bucket reuses the same strategy→grid tree (it drives the bucket list +
    // the per-grid config-class fetch), so load it for both scopes.
    if (scope !== 'by-run' && scope !== 'by-bucket') return;
    fetchTree();
  }, [scope, fetchTree, refreshKey]);

  // Tree-level auto-poll: while any strategy still has running/pending runs,
  // refresh the aggregate counts every 5s so tiles + badges tick live. Open
  // GridBranches poll their own leaf runs independently.
  const treeHasActive = useMemo(
    () => (tree.strategies || []).some(s => (s.running || 0) > 0),
    [tree]
  );
  useEffect(() => {
    if (scope !== 'by-run' || !treeHasActive) return;
    const id = setInterval(() => fetchTree(), 5000);
    return () => clearInterval(id);
  }, [scope, treeHasActive, fetchTree]);

  // ── 'by-combo' scope: pull a page of runs to aggregate combos across. ──
  const fetchRuns = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset, sort };
      if (filters.symbol)   params.symbol = filters.symbol;
      if (filters.strategy) params.strategy = filters.strategy;
      if (filters.idle)     params.idle_deployment = filters.idle;
      if (filters.status)   params.status = filters.status;
      if (filters.date) {
        const days = parseInt(filters.date, 10);
        if (!isNaN(days)) {
          const since = new Date(Date.now() - days * 86400000).toISOString();
          params.created_after = since;
        }
      }
      const res = await tradingService.listOptimizations(params);
      setRuns(res.data.results || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setRuns([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (scope !== 'by-combo') return;
    fetchRuns();
    // eslint-disable-next-line
  }, [scope, filters.symbol, filters.strategy, filters.idle, filters.status, filters.date, sort, page]);

  // Cross-run combo aggregation: pull top combos from each visible run
  const fetchCombosAcrossRuns = async () => {
    setComboLoading(true);
    try {
      // Exclude large streamed runs — their combos are filtered server-side only
      const visibleRuns = runs.filter(r => r.status === 'complete' && !r.combos_streamed);
      const all = [];
      for (const r of visibleRuns.slice(0, 20)) {
        try {
          const res = await tradingService.filterOptimizationCombos(r.id, {
            limit: 50, offset: 0, sort: '-sharpe_ratio',
          });
          for (const c of res.data.combos || []) {
            all.push({
              ...c,
              run_id: r.id,
              symbol: r.symbol,
              strategy_name: r.strategy_name,
              idle_deployment: r.idle_deployment,
            });
          }
        } catch (_) { /* skip failed run */ }
      }
      all.sort((a, b) => (b.sharpe_ratio || 0) - (a.sharpe_ratio || 0));
      setCombos(all.slice(0, 1000));
    } finally {
      setComboLoading(false);
    }
  };

  useEffect(() => {
    if (scope === 'by-combo' && runs.length > 0) {
      fetchCombosAcrossRuns();
    }
    // eslint-disable-next-line
  }, [scope, runs]);

  // FilterChip option lists. The tree endpoint returns COMPLETE distinct
  // symbol/idle/strategy lists (across all runs, not one page), so the chips
  // are exhaustive in by-run mode. Fall back to the loaded page in by-combo.
  const symbolOptions   = useMemo(
    () => (tree.symbols?.length ? tree.symbols : [...new Set(runs.map(r => r.symbol).filter(Boolean))].sort()),
    [tree.symbols, runs]
  );
  const strategyOptions = useMemo(
    () => ((tree.strategies || []).length
      ? tree.strategies.map(s => s.strategy_name).filter(Boolean).sort()
      : [...new Set(runs.map(r => r.strategy_name).filter(Boolean))].sort()),
    [tree.strategies, runs]
  );
  const idleOptions     = useMemo(
    () => (tree.idle_deployments?.length ? tree.idle_deployments : [...new Set(runs.map(r => r.idle_deployment).filter(Boolean))].sort()),
    [tree.idle_deployments, runs]
  );

  const hasIdleInCombos = useMemo(() => combos.some(c => c.idle_deployment && c.idle_deployment !== 'cash'), [combos]);
  const allSelected = combos.length > 0 && selected.size === combos.length;

  // By-run hierarchy is now SERVER-aggregated (tree endpoint) — every strategy
  // and every grid, not just one flat page. We only decorate the server payload
  // with stable client keys for collapse/scroll state; the per-symbol leaf runs
  // are lazy-loaded + paginated inside each GridBranch. This is what stops a big
  // multi-million-combo run getting buried below a freshly-launched sweep.
  const runTree = useMemo(() => {
    return (tree.strategies || []).map(st => {
      const sKey = `strat_${st.strategy_name}`;
      return {
        key: sKey,
        label: st.strategy_name,
        counts: { completed: st.completed || 0, failed: st.failed || 0, running: st.running || 0 },
        bestSharpe: (typeof st.best_sharpe === 'number' && Number.isFinite(st.best_sharpe)) ? st.best_sharpe : null,
        symbolCount: st.symbol_count || 0,
        gridCount: st.grid_count || 0,
        runCount: st.run_count || 0,
        grids: (st.grids || []).map(g => ({
          key: `${sKey}__grid_${g.grid_key}`,
          grid: g,  // raw payload handed to GridBranch (grid_key, total_combos, counts…)
        })),
      };
    });
  }, [tree]);

  // The currently-expanded strategy node (drives the inline detail box below
  // the strategy-tile grid). Null = nothing open.
  const openStrat = useMemo(
    () => runTree.find(s => s.key === openStrategy) || null,
    [runTree, openStrategy]
  );

  const toggleOne = (i) => {
    const next = new Set(selected);
    if (next.has(i)) next.delete(i); else next.add(i);
    setSelected(next);
  };
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(combos.map((_, i) => i)));
  };

  return (
    <div className="min-h-[calc(100vh-3rem)] bg-page dark:bg-page-dark text-gray-800 dark:text-gray-200">
      <Hero
        onLaunch={(kind, available) => {
          if (!available) {
            showToast(`${kind} runs are coming in R4. Standard Run available now.`);
            return;
          }
          setLauncherKind(kind);
          setLauncherOpen(true);
        }}
        onAbout={() => setAboutOpen(o => !o)}
        aboutOpen={aboutOpen}
      />
      <AboutPanel open={aboutOpen} onClose={() => setAboutOpen(false)} />

      {/* Compute Queue visibility (Layer 3): what's actually queued/in-flight
          on the spot-worker pipeline, with orphan flagging + admin reconcile. */}
      <ComputeQueuePanel showToast={showToast} />

      <RunLauncherModal
        open={launcherOpen}
        kind={launcherKind}
        onClose={() => setLauncherOpen(false)}
        onLaunched={(runId) => {
          // Switch the status filter to "all" so the newly-queued/running
          // run shows up (default filter is 'complete'). Refresh the tree
          // (and the flat list if we're in by-combo); tree auto-poll then
          // ticks progress live as the run advances.
          setFilters(f => ({ ...f, status: '' }));
          setPage(1);
          setRefreshKey(k => k + 1);
          fetchTree();
          showToast(`Run #${runId} queued — refreshing list…`);
        }}
        onBatchLaunched={(batch) => {
          // Surface the sweep as an aggregate tile, and refresh the tree so its
          // per-symbol members show up under the strategy's grid.
          setSweeps(prev => [batch, ...prev.filter(s => s.id !== batch.id)]);
          setFilters(f => ({ ...f, status: '' }));
          setPage(1);
          setRefreshKey(k => k + 1);
          fetchTree();
        }}
        showToast={showToast}
      />

      {/* Delete confirmation modal. Shows the run's identity + warns about
          cascade. The Confirm button shows a "Deleting..." spinner state
          while the request is in flight (DELETE on a 840K-row run takes
          a few seconds for the cascade to clean up OptimizationCombo). */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-lg bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 shadow-2xl"
          >
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">Delete optimization?</h3>
              {!deleting && (
                <button onClick={() => setDeleteTarget(null)} className="text-gray-400 hover:text-brand-gold">
                  <Glyph name="x" className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="px-4 py-4 space-y-3">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                You're about to permanently delete:
              </div>
              <div className="px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 text-sm font-mono">
                <span className="text-gray-500">#{deleteTarget.id}</span>{' '}
                <span className="text-gray-900 dark:text-gray-100">{deleteTarget.symbol}</span>{' '}
                <span className="text-gray-500">·</span>{' '}
                <span className="text-gray-700 dark:text-gray-300">{deleteTarget.strategy_name}</span>
                <div className="text-[11px] text-gray-500 mt-1">
                  {(deleteTarget.total_combos || 0).toLocaleString()} combo{deleteTarget.total_combos === 1 ? '' : 's'}
                  {deleteTarget.status && <> · status: {deleteTarget.status}</>}
                </div>
              </div>
              <div className="text-xs text-red-500 dark:text-red-400">
                This removes the run AND all its per-combo data. Cannot be undone.
              </div>
              {deleteError && (
                <div className="px-3 py-2 text-xs text-red-700 dark:text-red-300 bg-red-500/10 border border-red-500/30 rounded">
                  {deleteError}
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-500/90 hover:bg-red-500 text-white text-xs font-medium disabled:opacity-50"
              >
                {deleting ? (
                  <><Glyph name="spinner" className="w-3.5 h-3.5" /> Deleting…</>
                ) : (
                  <><Glyph name="x" className="w-3.5 h-3.5" /> Delete permanently</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replicate modal */}
      {replicateTarget && (
        <ReplicateModal
          run={replicateTarget}
          onClose={() => setReplicateTarget(null)}
          showToast={showToast}
          onLaunched={(newId) => {
            setReplicateTarget(null);
            onSelectRun(newId);
            setRefreshKey(k => k + 1);
            fetchTree();
          }}
          onBatchLaunched={(batch) => {
            // Surface the sweep as an aggregate tile and refresh the tree so its
            // per-symbol members show up under the strategy's grid. (Mirror of the
            // RunLauncherModal batch handler.)
            setSweeps(prev => [batch, ...prev.filter(s => s.id !== batch.id)]);
            setFilters(f => ({ ...f, status: '' }));
            setPage(1);
            setRefreshKey(k => k + 1);
            fetchTree();
          }}
        />
      )}

      {/* Deploy cap-weighted basket from a single config-class (#76/#77) */}
      {deployBasketTarget && (
        <DeployBasketModal
          target={deployBasketTarget}
          onClose={() => setDeployBasketTarget(null)}
          showToast={showToast}
        />
      )}

      {/* Cancel confirmation modal — orange-flavoured to distinguish from delete */}
      {cancelTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !cancelling && setCancelTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-lg bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 shadow-2xl"
          >
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">Cancel optimization?</h3>
              {!cancelling && (
                <button onClick={() => setCancelTarget(null)} className="text-gray-400 hover:text-brand-gold">
                  <Glyph name="x" className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="px-4 py-4 space-y-3">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Stop the in-progress run:
              </div>
              <div className="px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 text-sm font-mono">
                <span className="text-gray-500">#{cancelTarget.id}</span>{' '}
                <span className="text-gray-900 dark:text-gray-100">{cancelTarget.symbol}</span>{' '}
                <span className="text-gray-500">·</span>{' '}
                <span className="text-gray-700 dark:text-gray-300">{cancelTarget.strategy_name}</span>
                <div className="mt-1 text-gray-500 text-[11px]">
                  {(cancelTarget.completed_combos || 0).toLocaleString()} / {(cancelTarget.total_combos || 0).toLocaleString()} combos completed
                </div>
              </div>
              <p className="text-xs text-orange-600 dark:text-orange-400">
                The worker will stop after the current chunk finishes (a few seconds). Progress so far is saved — you can re-run later.
              </p>
              {cancelError && (
                <div className="px-3 py-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded">
                  {cancelError}
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
              <button
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
                className="px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                Keep running
              </button>
              <button
                onClick={confirmCancel}
                disabled={cancelling}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-orange-500/90 hover:bg-orange-500 text-white text-xs font-medium disabled:opacity-50"
              >
                {cancelling ? (
                  <><Glyph name="spinner" className="w-3.5 h-3.5" /> Cancelling…</>
                ) : (
                  <><Glyph name="x" className="w-3.5 h-3.5" /> Stop run</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume-as-parallel confirmation modal — sky/blue to signal "cloud" action */}
      {resumeParallelTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !resumingParallel && setResumeParallelTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-lg bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 shadow-2xl"
          >
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">Resume on spot worker?</h3>
              </div>
              {!resumingParallel && (
                <button onClick={() => setResumeParallelTarget(null)} className="text-gray-400 hover:text-brand-gold">
                  <Glyph name="x" className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="px-4 py-4 space-y-3">
              <div className="px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 text-sm font-mono">
                <span className="text-gray-500">#{resumeParallelTarget.id}</span>{' '}
                <span className="text-gray-900 dark:text-gray-100">{resumeParallelTarget.symbol}</span>{' '}
                <span className="text-gray-500">·</span>{' '}
                <span className="text-gray-700 dark:text-gray-300">{resumeParallelTarget.strategy_name}</span>
                <div className="mt-1 text-gray-500 text-[11px]">
                  {(resumeParallelTarget.completed_combos || 0).toLocaleString()} / {(resumeParallelTarget.total_combos || 0).toLocaleString()} combos completed
                  {' '}· {(((resumeParallelTarget.total_combos || 0) - (resumeParallelTarget.completed_combos || 0))).toLocaleString()} remaining
                </div>
              </div>
              <div className="px-3 py-2.5 rounded bg-sky-500/10 border border-sky-500/30 text-xs text-sky-700 dark:text-sky-300 space-y-1">
                <div className="font-semibold uppercase tracking-wider text-[10px]">What happens</div>
                <ul className="list-disc list-inside space-y-0.5 text-sky-700/90 dark:text-sky-300/90">
                  <li>An on-demand spot worker boots (~2 min) on 8 vCPUs</li>
                  <li>Grid is split into 8 parallel slices</li>
                  <li>Already-completed combos are automatically skipped</li>
                  <li>Worker self-terminates when done (~$0.05/hr)</li>
                </ul>
              </div>
              {resumeParallelError && (
                <div className="px-3 py-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded">
                  {resumeParallelError}
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
              <button
                onClick={() => setResumeParallelTarget(null)}
                disabled={resumingParallel}
                className="px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmResumeParallel}
                disabled={resumingParallel}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sky-500/90 hover:bg-sky-500 text-white text-xs font-medium disabled:opacity-50"
              >
                {resumingParallel ? (
                  <><Glyph name="spinner" className="w-3.5 h-3.5" /> Dispatching…</>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Resume on spot
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <FilterBar
        filters={filters}
        setFilter={setFilter}
        sort={sort}
        setSort={(v) => { setSort(v); setPage(1); }}
        page={page}
        total={scope === 'by-run' ? (tree.total || 0) : total}
        perPage={PAGE_SIZE}
        setPage={setPage}
        symbolOptions={symbolOptions}
        strategyOptions={strategyOptions}
        idleOptions={idleOptions}
        loading={scope === 'by-run' ? treeLoading : loading}
        showPager={scope === 'by-combo'}
      />

      <ScopeTabs
        scope={scope}
        setScope={(s) => { setScope(s); setSelected(new Set()); }}
        byRunCount={scope === 'by-run' ? (tree.total || 0) : runs.length}
        byComboCount={scope === 'by-combo' ? combos.length : null}
        total={scope === 'by-run' ? (tree.total || 0) : total}
      />

      <div className="px-3 md:px-5 py-4">
        {error && (
          <div className="mb-3 px-3 py-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded">
            {error}
          </div>
        )}

        {/* Active universe sweeps — aggregate tiles, click to expand members. */}
        {sweeps.length > 0 && (
          <div className="mb-5 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Universe Sweeps</div>
            {sweeps.map(s => (
              <BatchSweepTile
                key={s.id}
                initial={s}
                onDismiss={(id) => setSweeps(prev => prev.filter(x => x.id !== id))}
              />
            ))}
          </div>
        )}

        {scope === 'by-run' ? (
          <>
            {treeLoading && runTree.length === 0 && (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500 dark:text-gray-400">
                <Glyph name="spinner" className="w-4 h-4" /> Loading optimizations…
              </div>
            )}
            {!treeLoading && runTree.length === 0 && !error && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
                No optimizations match your filters. Run one from the chat to get started.
              </div>
            )}
            {/* ── Strategy tiles. Click a tile to expand it (single-select)
                into the detail box below — no separate tree. ── */}
            {runTree.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                {runTree.map((strat) => {
                  const isOpen = openStrategy === strat.key;
                  return (
                    <button
                      key={`tile_${strat.key}`}
                      onClick={() => { setOpenStrategy(isOpen ? null : strat.key); setOpenGrid(null); }}
                      className={`text-left rounded-lg border px-3 py-2.5 transition-all ${isOpen
                        ? 'border-brand-gold/60 bg-brand-gold/5 shadow-[0_0_0_1px_rgba(201,169,110,0.25)]'
                        : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22] hover:border-brand-gold/40 hover:shadow-[0_0_0_1px_rgba(201,169,110,0.15)]'}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] transition-transform ${isOpen ? 'rotate-90' : ''} text-gray-400`}>▶</span>
                        <span className="font-serif text-sm text-gray-900 dark:text-gray-100 capitalize truncate">{strat.label.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-500 tabular-nums pl-4">
                        {strat.runCount} run{strat.runCount !== 1 ? 's' : ''} · {strat.symbolCount} stock{strat.symbolCount !== 1 ? 's' : ''}
                        {strat.gridCount > 1 ? ` · ${strat.gridCount} grids` : ''}
                      </div>
                      <div className="mt-1.5 flex items-center gap-2.5 text-[11px] font-mono tabular-nums pl-4">
                        <span className="text-yellow-600 dark:text-yellow-500">⏳{strat.counts.running}</span>
                        <span className="text-green-600 dark:text-green-400">✓{strat.counts.completed}</span>
                        <span className="text-red-600 dark:text-red-400">✗{strat.counts.failed}</span>
                      </div>
                      <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-500 pl-4">
                        best sharpe{' '}
                        <span className={`font-mono tabular-nums ${strat.bestSharpe == null ? 'text-gray-400 dark:text-gray-600' : strat.bestSharpe >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {strat.bestSharpe == null ? '—' : strat.bestSharpe.toFixed(2)}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* ── Expanded strategy box: stacked grid tiles (each drills into
                status tiles → run list). ── */}
            {openStrat && (
              <div className="mt-3 rounded-lg border border-brand-gold/30 bg-gray-50/40 dark:bg-[#12161c] p-3 md:p-4">
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="text-sm font-serif font-semibold text-gray-800 dark:text-gray-200 capitalize">
                    {openStrat.label.replace(/_/g, ' ')}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
                    {openStrat.gridCount} grid{openStrat.gridCount !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={() => { setOpenStrategy(null); setOpenGrid(null); }}
                    className="ml-auto text-[11px] text-gray-400 hover:text-brand-gold"
                  >
                    close
                  </button>
                </div>
                <div className="space-y-2">
                  {openStrat.grids.map((g) => (
                    <GridTile
                      key={g.key}
                      strategyName={openStrat.label}
                      grid={g.grid}
                      filters={filters}
                      sort={sort}
                      refreshKey={refreshKey}
                      expanded={openGrid === g.key}
                      onToggle={() => setOpenGrid(prev => (prev === g.key ? null : g.key))}
                      onSelectRun={onSelectRun}
                      onDeleteClick={(run) => { setDeleteError(null); setDeleteTarget(run); }}
                      onCancelClick={(run) => { setCancelError(null); setCancelTarget(run); }}
                      onReplicateClick={(run) => setReplicateTarget(run)}
                      onResumeParallelClick={(run) => { setResumeParallelError(null); setResumeParallelTarget(run); }}
                      onDeployBasket={(payload) => setDeployBasketTarget(payload)}
                      showToast={showToast}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : scope === 'by-bucket' ? (
          <BucketView
            strategies={tree.strategies}
            showToast={showToast}
            onBumpRefresh={() => setRefreshKey(k => k + 1)}
          />
        ) : (
          <>
            {comboLoading && (
              <div className="text-center py-6 text-xs text-gray-500">
                Loading top combos across {runs.length} run{runs.length === 1 ? '' : 's'}…
              </div>
            )}
            {!comboLoading && combos.length === 0 && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
                No combos to show. Open a run to drill into combo-level results.
              </div>
            )}
            {combos.length > 0 && (
              <ComboTable
                combos={combos}
                selected={selected}
                toggleOne={toggleOne}
                toggleAll={toggleAll}
                allSelected={allSelected}
                onRunClick={onSelectRun}
                hasIdle={hasIdleInCombos}
              />
            )}
          </>
        )}
      </div>

      {scope === 'by-combo' && selected.size > 0 && (
        <BulkSaveBar
          count={selected.size}
          onClear={() => setSelected(new Set())}
          onSave={() => setShowSaveModal(true)}
        />
      )}

      {showSaveModal && (
        <CrossRunSaveModal
          combos={[...selected].map(i => combos[i])}
          onClose={() => setShowSaveModal(false)}
          onSaved={() => { setShowSaveModal(false); setSelected(new Set()); showToast('Combos saved to Analytics'); }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// CROSS-RUN SAVE MODAL (used from By Combo scope)
// ─────────────────────────────────────────────────────────────────────────

const CrossRunSaveModal = ({ combos, onClose, onSaved }) => {
  const [items, setItems] = useState(() =>
    combos.filter(Boolean).map(c => ({
      run_id: c.run_id,
      combo_index: c.combo_index,
      name: `${c.symbol} ${c.strategy_name} #${c.run_id}-${c.combo_index ?? ''}`,
      description: '',
      sharpe: c.sharpe_ratio,
      return_pct: c.total_return,
    }))
  );
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = items.map(({ sharpe: _s, return_pct: _r, ...rest }) => rest);
      const res = await tradingService.saveOptimizationCombos(payload);
      setResults(res.data);
      if (res.data.error_count === 0) setTimeout(onSaved, 1500);
    } catch (e) {
      setResults({ error: e.response?.data?.error || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="my-8 w-full max-w-2xl rounded-lg bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">
            Save {items.length} combo{items.length === 1 ? '' : 's'} to Analytics
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none">✕</button>
        </div>
        <div className="p-4 max-h-[60vh] overflow-y-auto scrollbar-thin">
          {results ? (
            <div className="space-y-2">
              {results.error && <div className="text-sm text-red-500">{results.error}</div>}
              {(results.saved || []).map((r, i) => (
                <div key={i} className={`text-xs px-2 py-1.5 rounded ${r.success ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-red-500/10 text-red-700 dark:text-red-400'}`}>
                  {r.success ? `✓ Saved "${r.name}" (id ${r.strategy_id})` : `✗ Combo ${r.combo_index}: ${r.error}`}
                </div>
              ))}
              {results.success_count > 0 && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Saved {results.success_count} of {items.length}. Visit the Analytics tab to see them.
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item, i) => (
                <div key={i} className="p-3 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                  <div className="flex items-center justify-between mb-2 text-[11px]">
                    <span className="text-gray-500 dark:text-gray-400">
                      Run #{item.run_id} · Combo #{item.combo_index} · Sharpe {fmtNum(item.sharpe)} · Return {fmtPct(item.return_pct)}
                    </span>
                  </div>
                  <input
                    value={item.name}
                    onChange={(e) => setItems(arr => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                    placeholder="Name"
                    className="w-full px-2 py-1.5 mb-1 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                  />
                  <input
                    value={item.description}
                    onChange={(e) => setItems(arr => arr.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                    placeholder="Description (optional)"
                    className="w-full px-2 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {!results && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-700">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 text-xs bg-brand-gold text-gray-900 rounded font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : `Save ${items.length}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// DETAIL VIEW — kept from R1 scaffold (full design treatment is R2 scope)
// ─────────────────────────────────────────────────────────────────────────

const KpiTile = ({ label, value, color, accent }) => (
  <div className={`p-2 rounded-lg border ${accent ? 'border-blue-500/30 bg-blue-500/5' : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22]'}`}>
    <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</div>
    <div className={`text-base font-mono ${color || ''}`}>{value}</div>
  </div>
);

const ThreshInput = ({ label, value, onChange, step }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-[11px] text-gray-600 dark:text-gray-400">{label}</span>
    <input
      type="number"
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="—"
      className="w-16 px-1.5 py-0.5 text-xs text-right rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
    />
  </div>
);

// Tab nav for the run detail page (Design v2 pattern). Mirrors the design's
// VizTabs — Table | Heatmap | Distribution | Scatter | Stability | NSR.
// Walk-Forward / DSR remain disabled until those pipelines land. NSR enables
// once `nsr_status === 'complete'` on the run; before that it shows a tooltip
// hinting at the "Compute NSR" button in the Hero.
const VIZ_TABS = [
  { k: 'table',        l: 'Table' },
  { k: 'heatmap',      l: 'Heatmap' },
  { k: 'distribution', l: 'Distribution' },
  { k: 'scatter',      l: 'Scatter' },
  { k: 'stability',    l: 'Stability' },
];
const VIZ_TABS_FUTURE = [
  { k: 'walkforward',  l: 'Walk-Forward', disabled: true, hint: 'walk-forward runs only' },
  { k: 'dsr',          l: 'DSR',          disabled: true, hint: 'coming soon' },
];

const VizTabs = ({ active, onChange, nsrAvailable }) => (
  <nav className="flex overflow-x-auto scrollbar-thin gap-1 border-b border-gray-200 dark:border-gray-800 mb-4">
    {VIZ_TABS.map(t => (
      <button
        key={t.k}
        onClick={() => onChange(t.k)}
        className={`px-3 py-2 text-sm whitespace-nowrap transition-colors border-b-2 ${
          active === t.k
            ? 'border-brand-gold text-brand-gold'
            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
      >
        {t.l}
      </button>
    ))}
    {/* NSR: enabled when nsrAvailable, disabled with hint otherwise. */}
    <button
      onClick={() => nsrAvailable && onChange('nsr')}
      disabled={!nsrAvailable}
      title={nsrAvailable ? '' : 'click "Compute NSR" in the Hero to populate this tab'}
      className={`px-3 py-2 text-sm whitespace-nowrap transition-colors border-b-2 ${
        !nsrAvailable
          ? 'text-gray-300 dark:text-gray-700 cursor-not-allowed border-transparent'
          : active === 'nsr'
            ? 'border-brand-gold text-brand-gold'
            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
      }`}
    >
      NSR
    </button>
    {VIZ_TABS_FUTURE.map(t => (
      <button
        key={t.k}
        disabled
        title={t.hint}
        className="px-3 py-2 text-sm whitespace-nowrap text-gray-300 dark:text-gray-700 cursor-not-allowed border-b-2 border-transparent"
      >
        {t.l}
      </button>
    ))}
  </nav>
);

// OosButton — small status pill for the full-grid OOS run. States:
//   null/failed  → "Run OOS" (clickable, blue-tinted)
//   pending      → "OOS queued…" (greyed, disabled)
//   running      → "OOS X.XM/Y.YM ⟳" with live progress (blue, disabled)
//   complete     → "OOS ✓" (green)
const OosButton = ({ oosStatus, oosProgress, oosError, triggering, onTrigger }) => {
  if (oosStatus === 'complete') {
    return (
      <span
        title="Full-grid OOS evaluation complete"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider border border-emerald-600/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
      >
        OOS ✓
      </span>
    );
  }
  if (oosStatus === 'pending') {
    return (
      <span
        title="OOS task queued — waiting for Celery worker"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider border border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-400"
      >
        OOS queued…
      </span>
    );
  }
  if (oosStatus === 'running') {
    const parts = (oosProgress || '').split('/');
    const _doneN  = parseInt(parts[0], 10);
    const _totalN = parseInt(parts[1], 10);
    const done  = parts[0] && !isNaN(_doneN)  ? (_doneN  / 1_000_000).toFixed(1) + 'M' : '—';
    const total = parts[1] && !isNaN(_totalN) ? (_totalN / 1_000_000).toFixed(1) + 'M' : '—';
    return (
      <span className="inline-flex items-center gap-1.5">
        <span
          title={`OOS full-grid run in progress — ${oosProgress || '…'} combos evaluated`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider border border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-400"
        >
          OOS {done}/{total} ⟳
        </span>
        <button
          onClick={onTrigger}
          disabled={triggering}
          title="Task appears stuck? Click to restart OOS from where it left off."
          className="text-[9px] text-gray-400 dark:text-gray-500 hover:text-sky-500 dark:hover:text-sky-400 underline transition-colors"
        >
          {triggering ? 'Queuing…' : 'restart'}
        </button>
      </span>
    );
  }
  // null (never run) or failed
  return (
    <button
      onClick={onTrigger}
      disabled={triggering}
      title={oosError ? `Last error: ${oosError}\n\n(click to retry)` : 'Run full-grid OOS evaluation — evaluates every combo on the held-out window via vectorized runner.'}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider border transition-colors ${
        triggering
          ? 'border-gray-300 dark:border-gray-700 text-gray-400'
          : oosStatus === 'failed'
          ? 'border-red-500/40 bg-red-500/10 text-red-500 dark:text-red-400 hover:border-red-500/70'
          : 'border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-400 hover:border-sky-500/70'
      }`}
    >
      {triggering ? 'Queuing…' : oosStatus === 'failed' ? '⚠ Run OOS' : '▶ Run OOS'}
    </button>
  );
};

// NsrButton — small status pill in the run-detail header. States:
//   null/failed  → "Compute NSR" (clickable, gold-tinted)
//   pending      → "Queued…" (greyed, disabled)
//   running      → "Computing… ⟳" (gold, disabled)
//   complete     → "NSR ✓" — click shows inline "Re-run? Yes / No" guard
//                  to prevent accidental wipe of results
const NsrButton = ({ status, progress, error, triggering, onTrigger, metric, summary }) => {
  const [confirmRerun, setConfirmRerun] = React.useState(false);

  if (status === 'complete') {
    const tip = summary
      ? `Computed on ${metric || 'sharpe_ratio'} · Best NSR: ${summary.best_nsr_score?.toFixed(2) ?? '—'} · Avg nbr std: ${summary.avg_neighbor_std?.toFixed(2) ?? '—'} · ${summary.combos_with_neighbors?.toLocaleString() ?? '—'} combos with neighbors`
      : 'NSR computed';
    if (confirmRerun) {
      return (
        <span className="inline-flex items-center gap-1 text-[10px]">
          <span className="text-orange-400">Re-run NSR? (wipes current results)</span>
          <button
            onClick={() => { setConfirmRerun(false); onTrigger(); }}
            disabled={triggering}
            className="px-1.5 py-0.5 rounded bg-orange-500/80 hover:bg-orange-500 text-white text-[10px] font-medium"
          >Yes</button>
          <button
            onClick={() => setConfirmRerun(false)}
            className="px-1.5 py-0.5 rounded border border-gray-500 text-gray-400 hover:text-gray-300 text-[10px]"
          >No</button>
        </span>
      );
    }
    return (
      <button
        onClick={() => setConfirmRerun(true)}
        disabled={triggering}
        title={tip + ' · click to re-run'}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider border border-emerald-600/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:border-emerald-600/70"
      >
        NSR ✓
      </button>
    );
  }
  if (status === 'running' || status === 'pending') {
    const parts = (progress || '').split('/');
    const done  = parts[0] ? (parseInt(parts[0], 10) / 1_000_000).toFixed(1) + 'M' : null;
    const total = parts[1] ? (parseInt(parts[1], 10) / 1_000_000).toFixed(1) + 'M' : null;
    return (
      <span
        title={`NSR computation in progress${progress ? ` — ${progress} combos scored` : ''}`}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider border border-brand-gold/40 bg-brand-gold/10 text-brand-gold"
      >
        {status === 'pending' ? 'NSR queued…' : (
          done && total ? `NSR ${done}/${total} ⟳` : 'NSR computing ⟳'
        )}
      </span>
    );
  }
  // null (never computed) or failed
  return (
    <button
      onClick={onTrigger}
      disabled={triggering}
      title={error ? `Last error: ${error}\n\n(click to retry)` : 'Compute Neighborhood Sharpe Robustness — penalizes isolated spikes, rewards plateaus. Post-hoc, no re-running.'}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider border transition-colors ${
        triggering
          ? 'border-gray-300 dark:border-gray-700 text-gray-400'
          : status === 'failed'
            ? 'border-red-600/40 bg-red-500/10 text-red-700 dark:text-red-400 hover:border-red-600/70'
            : 'border-brand-gold/40 bg-brand-gold/5 text-brand-gold hover:bg-brand-gold/15'
      }`}
    >
      {triggering ? '…' : status === 'failed' ? 'NSR retry' : '+ NSR'}
    </button>
  );
};

// ─── Full Pipeline Button ─────────────────────────────────────────────────────
// One-click smart pipeline dispatch: detects which stages (OOS → NSR →
// leaderboards) are still pending and queues exactly what's missing on a
// spot worker. Stages shown as compact pills when queued.
const FullPipelineButton = ({ stages, triggering, queued, error, onTrigger }) => {
  const STAGE_LABEL = { oos: 'OOS', nsr: 'NSR', leaderboards: 'Leaderboards' };

  if (queued && stages && stages.length > 0) {
    return (
      <span className="inline-flex items-center gap-1 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 font-medium">
          Queued:
        </span>
        {stages.map(s => (
          <span key={s} className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 uppercase tracking-wider">
            {STAGE_LABEL[s] || s}
          </span>
        ))}
        <span className="text-[10px] text-gray-400 dark:text-gray-500 animate-pulse">running on spot…</span>
      </span>
    );
  }

  if (stages && stages.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
        Pipeline ✓
      </span>
    );
  }

  return (
    <button
      onClick={onTrigger}
      disabled={triggering}
      title={
        error
          ? `Last error: ${error}\n\nClick to retry`
          : 'Run the full post-IS pipeline in one click: OOS → NSR → Leaderboards.\nSmart resume: only runs stages still pending.'
      }
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider border transition-colors ${
        triggering
          ? 'border-gray-300 dark:border-gray-700 text-gray-400'
          : error
            ? 'border-red-600/40 bg-red-500/10 text-red-700 dark:text-red-400 hover:border-red-600/70'
            : 'border-violet-500/40 bg-violet-500/5 text-violet-700 dark:text-violet-400 hover:bg-violet-500/15'
      }`}
    >
      {triggering ? '…' : error ? 'Pipeline retry' : '⚡ Full Pipeline'}
    </button>
  );
};

// ─── Combo Detail Modal ──────────────────────────────────────────────────────
// Full breakdown of a single optimization combo: strategy entry/exit rules,
// all parameters with descriptions, IS metrics, NSR metrics, OOS metrics,
// and lazy-loaded equity curve (triggers a backtest re-run on demand).
const ComboDetailModal = ({ combo, run, runId, onClose }) => {
  const [stratDef,     setStratDef]     = useState(null);
  const [chartData,    setChartData]    = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError,   setChartError]   = useState(null);
  const [chartFull,    setChartFull]    = useState(false);

  // Fetch strategy class definition (entry/exit rules, descriptions)
  useEffect(() => {
    tradingService.getAvailableStrategies().then(res => {
      const def = (res.data || []).find(s => s.name === run?.strategy_name);
      if (def) setStratDef(def);
    }).catch(() => {});
  }, [run?.strategy_name]);

  const loadChart = async () => {
    setChartLoading(true);
    setChartError(null);
    try {
      // Prefer DB PK (_id) for live runs; fall back to combo_index for archived
      const comboId = combo._id || (combo._combo_index ?? combo.combo_index);
      const res = await tradingService.getComboChart(runId, comboId);
      setChartData(res.data);
    } catch (e) {
      setChartError(e.response?.data?.error || e.message);
    } finally {
      setChartLoading(false);
    }
  };

  const params     = combo.parameters || {};
  const paramDescs = stratDef?.parameter_descriptions || {};
  const comboIdx   = combo._combo_index ?? combo.combo_index;

  const MetricTile = ({ label, value, isPct, isRaw }) => {
    const display = value == null ? '—'
      : isPct ? fmtPct(value)
      : isRaw ? String(value)
      : fmtNum(value);
    return (
      <div className="bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2">
        <div className="text-[10px] text-gray-400 mb-0.5 truncate">{label}</div>
        <div className={`text-sm font-semibold tabular-nums ${value != null ? valColor(value) : 'text-gray-400'}`}>{display}</div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 md:p-6" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[92vh] bg-white dark:bg-[#0d1117] rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
          <div className="min-w-0 pr-4">
            <h2 className="font-serif text-base md:text-lg text-gray-900 dark:text-gray-100">
              Combo #{comboIdx ?? '?'}
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {run?.strategy_name && (
                <span className="text-[10px] px-2 py-0.5 bg-brand-gold/15 text-brand-gold rounded-full font-medium uppercase tracking-wide">
                  {run.strategy_name.replace(/_/g, ' ')}
                </span>
              )}
              {run?.symbol && (
                <span className="text-[10px] px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 rounded-full font-mono">
                  {run.symbol}
                </span>
              )}
              <span className="text-[10px] px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 rounded-full font-mono">
                idx {comboIdx}
              </span>
              {combo._id && (
                <span className="text-[10px] text-gray-500">pk {combo._id}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none shrink-0 mt-0.5">✕</button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-6">

          {/* Entry / Exit rules — from strategy class definition */}
          {stratDef && (stratDef.entry_rules?.length > 0 || stratDef.exit_rules?.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {stratDef.entry_rules?.length > 0 && (
                <div>
                  <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Entry Rules</h3>
                  <ul className="space-y-1">
                    {stratDef.entry_rules.map((r, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                        <span className="text-green-400 mt-0.5 shrink-0">▸</span>{r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {stratDef.exit_rules?.length > 0 && (
                <div>
                  <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Exit Rules</h3>
                  <ul className="space-y-1">
                    {stratDef.exit_rules.map((r, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                        <span className="text-red-400 mt-0.5 shrink-0">▸</span>{r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Parameters */}
          <div>
            <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Parameters</h3>
            {Object.keys(params).length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {Object.entries(params).map(([k, v]) => (
                  <div key={k} className="bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2">
                    <div className="text-[10px] text-gray-400 mb-0.5 truncate" title={k}>{k.replace(/_/g, ' ')}</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{String(v)}</div>
                    {paramDescs[k] && <div className="text-[10px] text-gray-500 mt-0.5 leading-snug">{paramDescs[k]}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No parameters</p>
            )}
          </div>

          {/* IS Metrics */}
          <div>
            <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">In-Sample Performance</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              <MetricTile label="Sharpe"         value={combo.sharpe_ratio} />
              <MetricTile label="Blended Sharpe" value={combo.blended_sharpe} />
              <MetricTile label="Sortino"        value={combo.sortino_ratio} />
              <MetricTile label="Calmar"         value={combo.calmar_ratio} />
              <MetricTile label="Total Return"   value={combo.total_return}   isPct />
              <MetricTile label="Blended Return" value={combo.blended_return} isPct />
              <MetricTile label="Max Drawdown"   value={combo.max_drawdown}   isPct />
              <MetricTile label="Profit Factor"  value={combo.profit_factor} />
              <MetricTile label="Win Rate"       value={combo.win_rate != null ? combo.win_rate / 100 : null} isPct />
              <MetricTile label="Num Trades"     value={combo.num_trades}     isRaw />
              {combo.blended_sortino  != null && <MetricTile label="Blend Sortino" value={combo.blended_sortino} />}
              {combo.blended_calmar   != null && <MetricTile label="Blend Calmar"  value={combo.blended_calmar} />}
            </div>
          </div>

          {/* NSR Metrics */}
          {combo.nsr_score != null && (
            <div>
              <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">NSR Robustness</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <MetricTile label="NSR Score"      value={combo.nsr_score} />
                <MetricTile label="Neighbor Count" value={combo.nsr_neighbor_count} isRaw />
                <MetricTile label="Neighbor Std"   value={combo.nsr_neighbor_std} />
                <MetricTile label="Neighbor Mean"  value={combo.nsr_neighbor_mean} />
              </div>
            </div>
          )}

          {/* OOS Metrics */}
          {combo.oos_blended_sharpe != null && (
            <div>
              <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Out-of-Sample Validation</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                <MetricTile label="OOS Blend Sharpe"   value={combo.oos_blended_sharpe} />
                <MetricTile label="OOS Sharpe"         value={combo.oos_sharpe_ratio} />
                <MetricTile label="Degradation Ratio"  value={combo.oos_degradation_ratio} />
                <MetricTile label="OOS Total Return"   value={combo.oos_total_return}  isPct />
                <MetricTile label="OOS Max Drawdown"   value={combo.oos_max_drawdown}  isPct />
                <MetricTile label="OOS Trades"         value={combo.oos_num_trades}    isRaw />
              </div>
            </div>
          )}

          {/* Chart — candles (IS/OOS/out-of-OOS) + entry/exit signals + equity below.
              Lazy-loaded: re-runs the backtest full-history → today (~30-60s). */}
          <div>
            <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">
              Chart <span className="text-gray-500 normal-case">— price · signals · equity</span>
            </h3>
            {!chartData && !chartLoading && (
              <button
                onClick={loadChart}
                className="text-[11px] border border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10 px-3 py-1.5 rounded-lg transition-colors"
              >
                Load chart <span className="text-gray-500">(re-runs backtest · ~30-60s)</span>
              </button>
            )}
            {chartLoading && <p className="text-[11px] text-gray-500 animate-pulse">Running backtest full history…</p>}
            {chartError  && <p className="text-[11px] text-red-400">{chartError}</p>}
            {chartData?.candles?.length > 0 && (() => {
              const c   = chartData.candles;
              const eq  = chartData.equity_curve || [];
              const tr  = chartData.trades || [];
              const reg = chartData.regions || {};
              const d10 = (v) => String(v || '').slice(0, 10);
              const rect = (x0, x1, color) => (x0 && x1
                ? [{ type: 'rect', xref: 'x', yref: 'paper', x0, x1, y0: 0, y1: 1,
                     fillcolor: color, line: { width: 0 }, layer: 'below' }]
                : []);
              const entries = tr.filter(t => t.entry_date);
              const exits   = tr.filter(t => t.exit_date && t.exit_price != null);
              // Universal visual nudge (applies to every trade on every ticker): float
              // buy triangles just below the bar and sell triangles just above, so they
              // don't disappear inside the candle wick. Hover still shows the true fill.
              const MK_OFF = 0.045;
              const traces = [
                { type: 'candlestick', name: chartData.symbol, yaxis: 'y',
                  x: c.map(p => p.date), open: c.map(p => p.o), high: c.map(p => p.h),
                  low: c.map(p => p.l), close: c.map(p => p.c),
                  increasing: { line: { color: '#16a34a' } }, decreasing: { line: { color: '#dc2626' } },
                  showlegend: false },
                { type: 'scatter', mode: 'markers', name: 'entry', yaxis: 'y',
                  x: entries.map(t => d10(t.entry_date)), y: entries.map(t => t.entry_price * (1 - MK_OFF)),
                  customdata: entries.map(t => t.entry_price),
                  marker: { symbol: 'triangle-up', color: '#22c55e', size: 14,
                            line: { color: '#ffffff', width: 1.4 } },
                  hovertemplate: 'BUY %{x}<br>$%{customdata:,.2f}<extra></extra>', showlegend: false },
                { type: 'scatter', mode: 'markers', name: 'exit', yaxis: 'y',
                  x: exits.map(t => d10(t.exit_date)), y: exits.map(t => t.exit_price * (1 + MK_OFF)),
                  customdata: exits.map(t => t.exit_price),
                  marker: { symbol: 'triangle-down', color: '#ef4444', size: 14,
                            line: { color: '#ffffff', width: 1.4 } },
                  hovertemplate: 'SELL %{x}<br>$%{customdata:,.2f}<extra></extra>', showlegend: false },
                { type: 'scatter', mode: 'lines', name: 'Equity', yaxis: 'y2',
                  x: eq.map(p => d10(p.date)), y: eq.map(p => p.equity),
                  line: { color: '#c9a96e', width: 1.3 }, fill: 'tozeroy', fillcolor: 'rgba(201,169,110,0.06)',
                  hovertemplate: '$%{y:,.0f}<extra></extra>', showlegend: false },
              ];
              const mkLayout = (height) => ({
                height,
                margin: { t: 10, b: 30, l: 55, r: 10 },
                paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                font: { family: 'monospace', size: 10, color: '#6b7280' },
                hovermode: 'x unified',
                xaxis: { rangeslider: { visible: false }, showgrid: false, color: '#6b7280', anchor: 'y2' },
                yaxis:  { domain: [0.40, 1], showgrid: true, gridcolor: 'rgba(100,100,100,0.12)', color: '#6b7280' },
                yaxis2: { domain: [0, 0.32], showgrid: true, gridcolor: 'rgba(100,100,100,0.12)', color: '#6b7280', tickprefix: '$' },
                shapes: [
                  ...rect(reg.start,   reg.is_end,  'rgba(120,120,120,0.06)'),
                  ...rect(reg.is_end,  reg.oos_end, 'rgba(59,130,246,0.08)'),
                  ...rect(reg.oos_end, reg.today,   'rgba(201,169,110,0.10)'),
                ],
              });
              const legend = (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-gray-500 mb-1">
                  <span><span className="inline-block w-2.5 h-2.5 mr-1 align-middle" style={{ background: 'rgba(120,120,120,0.35)' }} />In-Sample</span>
                  <span><span className="inline-block w-2.5 h-2.5 mr-1 align-middle" style={{ background: 'rgba(59,130,246,0.35)' }} />OOS</span>
                  <span><span className="inline-block w-2.5 h-2.5 mr-1 align-middle" style={{ background: 'rgba(201,169,110,0.5)' }} />Out-of-OOS</span>
                  <span className="text-green-500">▲ entry</span>
                  <span className="text-red-500">▼ exit</span>
                  <button onClick={() => setChartFull(true)}
                    className="ml-auto text-[10px] border border-gray-300 dark:border-gray-700 text-gray-500 hover:text-brand-gold hover:border-brand-gold/50 px-2 py-0.5 rounded transition-colors">
                    ⤢ Maximize
                  </button>
                </div>
              );
              const caption = (
                <div className="text-[10px] text-gray-500 mt-1">
                  {chartData.n_bars} bars · {chartData.n_trades} trades · {chartData.symbol}
                  {chartData.source ? ` (${chartData.source})` : ''}
                </div>
              );
              return (
                <>
                  {legend}
                  <Plot data={traces} layout={mkLayout(440)}
                    config={{ displayModeBar: false, scrollZoom: false }}
                    style={{ width: '100%' }} useResizeHandler />
                  {caption}
                  {chartFull && (
                    <div className="fixed inset-0 z-[100] bg-white dark:bg-[#0d1117] flex flex-col p-4"
                      onClick={(e) => { if (e.target === e.currentTarget) setChartFull(false); }}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs uppercase tracking-wider text-gray-400">
                          {chartData.symbol} · {run?.strategy_name}
                        </div>
                        <button onClick={() => setChartFull(false)}
                          className="text-xs border border-gray-300 dark:border-gray-700 text-gray-500 hover:text-brand-gold hover:border-brand-gold/50 px-3 py-1 rounded transition-colors">
                          ✕ Close
                        </button>
                      </div>
                      {legend}
                      <div className="flex-1 min-h-0">
                        <Plot data={traces}
                          layout={mkLayout(Math.max(400, (typeof window !== 'undefined' ? window.innerHeight : 800) - 140))}
                          config={{ displayModeBar: true, scrollZoom: true, displaylogo: false }}
                          style={{ width: '100%', height: '100%' }} useResizeHandler />
                      </div>
                      {caption}
                    </div>
                  )}
                </>
              );
            })()}
            {chartData && !chartData.candles?.length && (
              <p className="text-[11px] text-gray-500">No chart data returned.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// NsrTab — dedicated tab content. Shows summary tiles + a side-by-side
// "top by Sharpe vs top by NSR" leaderboard so the user can see which
// combos fall off the leaderboard once fragility is penalized.
const NsrTab = ({ runId, run, nsr, combos, totalInRun, setFilters }) => {
  const [topByNsr, setTopByNsr] = useState([]);
  const [topBySharpe, setTopBySharpe] = useState([]);
  const [tabLoading, setTabLoading] = useState(true);
  // Minimum trade filter — excludes low-trade combos that are indistinguishable
  // from buy-and-hold and likely represent backward-looking overfit.
  const [minTrades, setMinTrades] = useState(0);
  const [minTradesInput, setMinTradesInput] = useState('0');
  // Combo detail modal — opens on row click across all three tables
  const [detailCombo, setDetailCombo] = useState(null);
  const openDetail = (combo) => setDetailCombo(combo);
  // Save-to-analytics modal state
  const [savingCombo, setSavingCombo] = useState(null); // combo object being saved
  const [saveName, setSaveName] = useState('');
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'done' | 'error'
  const [saveError, setSaveError] = useState('');

  const handleSave = async () => {
    if (!saveName.trim()) return;
    setSaveStatus('saving');
    try {
      const payload = [{ run_id: runId, combo_index: savingCombo._combo_index, name: saveName.trim(), description: '' }];
      const res = await tradingService.saveOptimizationCombos(payload);
      if (!res.data.error_count) {
        setSaveStatus('done');
        setTimeout(() => { setSavingCombo(null); setSaveStatus(null); setSaveName(''); }, 1500);
      } else {
        setSaveError(res.data.saved?.[0]?.error || 'Save failed');
        setSaveStatus('error');
      }
    } catch (e) {
      setSaveError(e.response?.data?.error || e.message);
      setSaveStatus('error');
    }
  };

  const openSave = (combo) => {
    setSavingCombo(combo);
    setSaveName(`${run?.symbol || ''} ${run?.strategy_name || ''} NSR#${(combo._combo_index ?? 0) + 1}`);
    setSaveStatus(null);
    setSaveError('');
  };

  // Deploy-to-live-trading modal state
  const [deployingCombo, setDeployingCombo] = useState(null);
  const [deployName, setDeployName] = useState('');
  const [deployAlloc, setDeployAlloc] = useState('10000');
  const [deployStatus, setDeployStatus] = useState(null); // null | 'deploying' | 'done' | 'error'
  const [deployError, setDeployError] = useState('');
  const [deployedName, setDeployedName] = useState('');
  // IBKR accounts for the deploy target picker.
  const [accounts, setAccounts] = useState([]);
  const [deployAccountId, setDeployAccountId] = useState(null);
  const [deployLiveConfirmed, setDeployLiveConfirmed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    tradingService.listIBKRAccounts()
      .then(res => {
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setAccounts(data);
      })
      .catch(() => { if (!cancelled) setAccounts([]); });
    return () => { cancelled = true; };
  }, []);

  const selectedAccount = accounts.find(a => a.id === deployAccountId) || null;
  const deployingToLive = selectedAccount && !selectedAccount.is_paper;

  const openDeploy = (combo) => {
    setDeployingCombo(combo);
    const base = `${run?.symbol || ''} ${run?.strategy_name || ''}`.trim();
    setDeployName(`${base} #${(combo._combo_index ?? 0) + 1}`);
    setDeployAlloc('10000');
    setDeployStatus(null);
    setDeployError('');
    setDeployedName('');
    setDeployLiveConfirmed(false);
    const def = accounts.find(a => a.is_default) || accounts[0] || null;
    setDeployAccountId(def ? def.id : null);
  };

  const handleDeploy = async () => {
    if (!deployName.trim()) return;
    const alloc = parseFloat(deployAlloc);
    if (isNaN(alloc) || alloc <= 0) { setDeployError('Allocation must be a positive number'); return; }
    if (deployingToLive && !deployLiveConfirmed) {
      setDeployError('Confirm you intend to deploy to a real-money account.');
      return;
    }
    setDeployStatus('deploying');
    try {
      const res = await tradingService.deployFromCombo({
        run_id: runId,
        combo_index: deployingCombo._combo_index,
        name: deployName.trim(),
        allocation_usd: alloc,
        ...(deployAccountId ? { account_id: deployAccountId } : {}),
      });
      setDeployedName(res.data.name);
      setDeployStatus('done');
      setTimeout(() => { setDeployingCombo(null); setDeployStatus(null); }, 2000);
    } catch (e) {
      setDeployError(e.response?.data?.error || e.message);
      setDeployStatus('error');
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTabLoading(true);
      try {
        const rawSort = `-${nsr.metric || 'sharpe_ratio'}`;
        // Server-side filter: pass min_num_trades to the DB so "top 10 with
        // ≥N trades" is correct (a client-side scan of top-100 would miss the
        // true best if it's ranked #101+). skip_count=true prevents the slow
        // COUNT(*) on 2M rows — the leaderboard only needs the top 10 rows.
        const filterBase = minTrades > 0 ? { min_num_trades: minTrades } : {};
        // Fetch independently so a 500 on NSR (not yet run) doesn't wipe out
        // the IS metric leaderboard. Promise.allSettled returns fulfilled/rejected
        // per request rather than short-circuiting on first failure.
        const [nsrRes, sharpeRes] = await Promise.allSettled([
          tradingService.filterOptimizationCombos(runId, { ...filterBase, limit: 10, offset: 0, sort: '-nsr_score', skip_count: true }),
          tradingService.filterOptimizationCombos(runId, { ...filterBase, limit: 10, offset: 0, sort: rawSort, skip_count: true }),
        ]);
        if (cancelled) return;
        setTopByNsr(nsrRes.status === 'fulfilled' ? (nsrRes.value.data.combos || []) : []);
        setTopBySharpe(sharpeRes.status === 'fulfilled' ? (sharpeRes.value.data.combos || []) : []);
      } finally {
        if (!cancelled) setTabLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [runId, nsr.status, nsr.metric, minTrades]);

  const summary = nsr.summary || {};
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22] p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex flex-wrap items-baseline gap-3">
            <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">Neighborhood Sharpe Robustness</h3>
            <span className="text-[10px] uppercase tracking-wider text-gray-500">
              metric: <span className="text-brand-gold font-mono">{nsr.metric || '—'}</span>
              {' · '}λ: <span className="text-brand-gold font-mono">{nsr.lambda?.toFixed(2) ?? '—'}</span>
            </span>
          </div>
          {/* Min trades filter — combos with fewer trades are statistically meaningless
              (indistinguishable from buy-and-hold, high backward-looking bias risk). */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] uppercase tracking-wider text-gray-500">Min trades</span>
            <input
              type="number"
              min="0"
              max="999"
              value={minTradesInput}
              onChange={e => setMinTradesInput(e.target.value)}
              onBlur={() => {
                const v = parseInt(minTradesInput, 10);
                const clamped = isNaN(v) ? 0 : Math.max(0, Math.min(999, v));
                setMinTradesInput(String(clamped));
                setMinTrades(clamped);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') e.target.blur();
              }}
              className="w-14 px-2 py-0.5 text-[11px] font-mono text-center rounded border
                         border-gray-300 dark:border-gray-600
                         bg-white dark:bg-[#0d1117]
                         text-gray-900 dark:text-gray-100
                         focus:outline-none focus:border-brand-gold"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <KpiTile label="Best NSR" value={fmtNum(summary.best_nsr_score)} color={valColor(summary.best_nsr_score)} />
          <KpiTile label="Avg Neighbor Std" value={fmtNum(summary.avg_neighbor_std)} color="text-gray-700 dark:text-gray-300" />
          <KpiTile label="Avg Neighbors" value={summary.avg_neighbor_count?.toFixed(1) ?? '—'} color="text-gray-700 dark:text-gray-300" />
          <KpiTile label="With Neighbors" value={`${summary.combos_with_neighbors?.toLocaleString() ?? '—'} / ${totalInRun.toLocaleString()}`} color="text-gray-500" />
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
          NSR Score = <span className="font-mono">metric − λ × neighbor_std</span>. Combos on a wide plateau
          (low neighbor std) keep most of their score; isolated spikes (high std = fragile) drop hard.
          {minTrades > 0 && (
            <span className="ml-1 text-amber-400/80">
              · showing only combos with ≥{minTrades} trades
            </span>
          )}
        </p>
      </div>

      {/* Empty state when min trades filter is too aggressive */}
      {!tabLoading && minTrades > 0 && topByNsr.length === 0 && topBySharpe.length === 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-center">
          <p className="text-[12px] text-amber-400/90 mb-2">
            No combos with ≥{minTrades} trades found in this run.
          </p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
            This run's best combo has {run?.best_num_trades ?? 'few'} trades — the strategy is selective
            by design. Try a lower threshold, or set to 0 to see all.
          </p>
          <button
            onClick={() => { setMinTrades(0); setMinTradesInput('0'); }}
            className="text-[11px] text-brand-gold hover:underline"
          >
            Reset to 0 (show all)
          </button>
        </div>
      )}

      {/* Head-to-head: #1 raw metric vs #1 NSR — only when both are loaded */}
      {!tabLoading && topBySharpe.length > 0 && topByNsr.length > 0 && (
        <ComboCompareCard
          rawBest={topBySharpe[0]}
          nsrBest={topByNsr[0]}
          metric={nsr.metric}
          run={run}
          runId={runId}
          minTrades={minTrades}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <NsrLeaderboard
          title={`Top 10 by ${NSR_METRIC_LABEL[nsr.metric] || 'Raw Metric'}`}
          subtitle="best on the optimize metric, no robustness penalty"
          combos={topBySharpe} loading={tabLoading}
          primaryKey={nsr.metric || 'sharpe_ratio'} secondaryKey="nsr_score"
          highlight={false}
          onSave={openSave}
          onDeploy={openDeploy}
          onRowClick={openDetail}
        />
        <NsrLeaderboard
          title="Top 10 by NSR Score"
          subtitle="optimize metric minus fragility penalty — deploy these"
          combos={topByNsr} loading={tabLoading}
          primaryKey="nsr_score" secondaryKey={nsr.metric || 'sharpe_ratio'}
          highlight={true}
          onSave={openSave}
          onDeploy={openDeploy}
          onRowClick={openDetail}
        />
      </div>

      {/* ── Deploy Candidates ─────────────────────────────────────────────── */}
      {/* pipeline_shortlist = combos that cleared IS quality gate + OOS quality
          gate + degradation gate, then sorted by NSR. This is the gold-standard
          output — combos ready for live deployment. Only visible after OOS runs. */}
      {/* Empty shortlist notice — shown when pipeline ran but 0 combos passed */}
      {run?.pipeline_shortlist != null && run.pipeline_shortlist.length === 0 && nsr.status === 'complete' && (
        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-3 py-2.5 text-[11px] text-yellow-600 dark:text-yellow-400">
          <span className="font-medium">0 combos cleared all pipeline gates.</span>
          {' '}The IS quality gate requires a combo to exceed {(((run?.pipeline_config?.is_quality_mult ?? 0.8) * 100).toFixed(0))}% of B&amp;H Sharpe, and min {run?.pipeline_config?.min_trades ?? 20} trades.
          {run?.best_result?.num_trades != null && run.best_result.num_trades < (run?.pipeline_config?.min_trades ?? 20) && (
            <span className="ml-1">
              Best combo has only <strong>{run.best_result.num_trades} trades</strong> — strategy trades too infrequently to pass the trades gate.
            </span>
          )}
        </div>
      )}
      {run?.pipeline_shortlist && run.pipeline_shortlist.length > 0 && (() => {
        const pc = run?.pipeline_config || {};
        const isMult  = pc.is_quality_mult  ?? 0.8;
        const oosMult = pc.oos_quality_mult ?? 0.8;
        const minDeg  = pc.min_degradation  ?? 0.5;
        const minTr   = pc.min_trades       ?? 20;
        return (
          <div className="rounded-xl border border-green-500/40 bg-white dark:bg-[#161b22] p-3">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
              <div>
                <div className="text-sm font-medium text-green-400">⚡ Deploy Candidates</div>
                <div className="text-[10px] text-gray-500">
                  passed IS quality + OOS quality + degradation gates · sorted by NSR
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800/80 text-gray-400 whitespace-nowrap">
                  IS ≥ {isMult}×B&amp;H
                </span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800/80 text-gray-400 whitespace-nowrap">
                  OOS ≥ {oosMult}×OOS B&amp;H
                </span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800/80 text-gray-400 whitespace-nowrap">
                  Degrad ≥ {minDeg}
                </span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800/80 text-gray-400 whitespace-nowrap">
                  Trades ≥ {minTr}
                </span>
              </div>
            </div>
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-[11px] min-w-[620px]">
                <thead className="text-gray-400 uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-1 py-1 w-12"># / ID</th>
                    <th className="px-1 py-1"></th>
                    <th className="text-left px-1 py-1">Params</th>
                    <th className="text-right px-1 py-1">NSR</th>
                    <th className="text-right px-1 py-1">IS Blend</th>
                    <th className="text-right px-1 py-1">OOS Blend</th>
                    <th className="text-right px-1 py-1">Degrad</th>
                    <th className="text-right px-1 py-1">IS Ret%</th>
                    <th className="text-right px-1 py-1">MaxDD</th>
                    <th className="text-right px-1 py-1">Trades</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800/70">
                  {run.pipeline_shortlist.map((c, i) => {
                    // Normalize: pipeline_shortlist uses combo_index, but openSave /
                    // openDeploy handlers read _combo_index and pass it to the API.
                    const normalized = { ...c, _combo_index: c.combo_index };
                    const degradColor = c.oos_degradation_ratio == null
                      ? 'text-gray-500'
                      : c.oos_degradation_ratio >= 0.8
                        ? 'text-green-400'
                        : c.oos_degradation_ratio >= 0.5
                          ? 'text-amber-400'
                          : 'text-red-400';
                    return (
                      <tr
                        key={c.combo_index ?? i}
                        className={`cursor-pointer hover:bg-gray-50/80 dark:hover:bg-gray-800/40 ${i === 0 ? 'bg-green-500/5' : ''}`}
                        onClick={() => openDetail(normalized)}
                      >
                        <td className="px-1 py-1">
                          <div className="text-gray-500">{i + 1}</div>
                          <div className="text-[9px] text-gray-600 dark:text-gray-600 font-mono">C{c.combo_index ?? '?'}</div>
                        </td>
                        <td className="px-1 py-1" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openDeploy(normalized)}
                              className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-green-600/20 border border-green-500/40 text-green-400 hover:bg-green-600/30 transition-colors whitespace-nowrap"
                              title="Deploy as a live trading strategy instance"
                            >Deploy</button>
                            <button
                              onClick={() => openSave(normalized)}
                              className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border border-brand-gold/50 text-brand-gold hover:bg-brand-gold/10 transition-colors whitespace-nowrap"
                            >Save</button>
                          </div>
                        </td>
                        <td className="px-1 py-1"><ParamChips params={c.parameters || {}} max={3} /></td>
                        <td className={`px-1 py-1 text-right tabular-nums font-mono ${valColor(c.nsr_score)}`}>{fmtNum(c.nsr_score)}</td>
                        <td className={`px-1 py-1 text-right tabular-nums font-mono ${valColor(c.blended_sharpe)}`}>{fmtNum(c.blended_sharpe)}</td>
                        <td className={`px-1 py-1 text-right tabular-nums font-mono ${valColor(c.oos_blended_sharpe)}`}>
                          {c.oos_blended_sharpe != null ? fmtNum(c.oos_blended_sharpe) : '—'}
                        </td>
                        <td className={`px-1 py-1 text-right tabular-nums font-mono ${degradColor}`}>
                          {c.oos_degradation_ratio != null ? fmtNum(c.oos_degradation_ratio) : '—'}
                        </td>
                        <td className={`px-1 py-1 text-right tabular-nums font-mono ${valColor(c.total_return)}`}>{fmtPct(c.total_return)}</td>
                        <td className="px-1 py-1 text-right tabular-nums font-mono text-red-400">{fmtPct(c.max_drawdown)}</td>
                        <td className="px-1 py-1 text-right tabular-nums font-mono text-gray-500">{c.num_trades ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-gray-500 mt-2">
              {run.pipeline_shortlist.length} combo{run.pipeline_shortlist.length !== 1 ? 's' : ''} cleared all gates.
              IS gate uses best combo's blended Sharpe as baseline. OOS gate uses OOS B&amp;H blended Sharpe.
            </p>
          </div>
        );
      })()}

      <button
        onClick={() => setFilters(f => ({
          ...f,
          sort: '-nsr_score',
          ...(minTrades > 0 ? { min_num_trades: minTrades } : {}),
        }))}
        className="text-[11px] text-brand-gold hover:underline"
      >
        → Sort the full table by NSR Score{minTrades > 0 ? ` (≥${minTrades} trades)` : ''}
      </button>

      {/* Inline save modal — triggered by Save button on any leaderboard row */}
      {savingCombo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSavingCombo(null)}>
          <div className="bg-white dark:bg-[#161b22] rounded-2xl border border-gray-200 dark:border-gray-700 p-6 w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100 mb-1">Save to Analytics</h3>
            <p className="text-[11px] text-gray-500 mb-4">
              NSR {fmtNum(savingCombo.nsr_score)} · Sharpe {fmtNum(savingCombo.sharpe_ratio)} · Return {fmtPct(savingCombo.total_return)} · {savingCombo.num_trades} trades
            </p>
            {saveStatus !== 'done' ? (
              <>
                <input
                  autoFocus
                  type="text"
                  value={saveName}
                  onChange={e => setSaveName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                  placeholder="Strategy name"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-brand-gold mb-4"
                />
                {saveStatus === 'error' && (
                  <p className="text-[11px] text-red-400 mb-3">{saveError}</p>
                )}
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setSavingCombo(null)} className="px-4 py-1.5 rounded-lg text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Cancel</button>
                  <button
                    onClick={handleSave}
                    disabled={!saveName.trim() || saveStatus === 'saving'}
                    className="px-4 py-1.5 rounded-lg text-sm bg-brand-gold text-white disabled:opacity-50 hover:bg-brand-gold/90 transition-colors"
                  >
                    {saveStatus === 'saving' ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <p className="text-green-400 text-sm">✓ Saved to Analytics</p>
            )}
          </div>
        </div>
      )}

      {/* Combo detail modal — opened by clicking any row in the three tables */}
      {detailCombo && (
        <ComboDetailModal
          combo={detailCombo}
          run={run}
          runId={runId}
          onClose={() => setDetailCombo(null)}
        />
      )}

      {/* Deploy-to-live-trading modal */}
      {deployingCombo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setDeployingCombo(null)}>
          <div className="bg-white dark:bg-[#161b22] rounded-2xl border border-green-500/30 p-6 w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-green-400 text-lg">⚡</span>
              <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">Deploy to Live Trading</h3>
            </div>
            <p className="text-[11px] text-gray-500 mb-1">
              {run?.strategy_name} · {run?.symbol} · idle: {run?.idle_deployment || 'cash'}
            </p>
            <p className="text-[11px] text-gray-500 mb-4">
              NSR {fmtNum(deployingCombo.nsr_score)} · Sharpe {fmtNum(deployingCombo.sharpe_ratio)} · {deployingCombo.num_trades} trades
            </p>
            {deployStatus === 'done' ? (
              <p className="text-green-400 text-sm">✓ "{deployedName}" created — go to Strategies tab to activate it</p>
            ) : (
              <>
                <div className="space-y-3 mb-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 block">Strategy name</label>
                    <input
                      autoFocus
                      type="text"
                      value={deployName}
                      onChange={e => setDeployName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleDeploy()}
                      placeholder="NVDA TDB Primary"
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-green-500"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">
                      Deploy the same combo multiple times with different names to run parallel instances.
                    </p>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 block">Account</label>
                    <select
                      value={deployAccountId ?? ''}
                      onChange={e => { setDeployAccountId(Number(e.target.value)); setDeployLiveConfirmed(false); }}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-green-500"
                    >
                      {accounts.length === 0 && <option value="">No accounts found</option>}
                      {accounts.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.name} · {a.account_id} {a.is_model ? '(EXT BOOK)' : a.is_paper ? '(Paper)' : '(LIVE)'}{a.is_default ? ' — default' : ''}
                        </option>
                      ))}
                    </select>
                    {deployingToLive && (
                      <label className="mt-2 flex items-start gap-2 text-[11px] text-red-500 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={deployLiveConfirmed}
                          onChange={e => setDeployLiveConfirmed(e.target.checked)}
                          className="mt-0.5"
                        />
                        <span>This is a <strong>real-money</strong> account ({selectedAccount.account_id}). Orders placed by this strategy will use real funds.</span>
                      </label>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 block">Allocation (USD)</label>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-400 text-sm">$</span>
                      <input
                        type="number"
                        min="100"
                        step="1000"
                        value={deployAlloc}
                        onChange={e => setDeployAlloc(e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-green-500"
                      />
                    </div>
                  </div>
                </div>
                {deployStatus === 'error' && (
                  <p className="text-[11px] text-red-400 mb-3">{deployError}</p>
                )}
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setDeployingCombo(null)} className="px-4 py-1.5 rounded-lg text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Cancel</button>
                  <button
                    onClick={handleDeploy}
                    disabled={!deployName.trim() || deployStatus === 'deploying' || (deployingToLive && !deployLiveConfirmed)}
                    className={`px-4 py-1.5 rounded-lg text-sm text-white disabled:opacity-50 transition-colors ${deployingToLive ? 'bg-red-600 hover:bg-red-500' : 'bg-green-600 hover:bg-green-500'}`}
                  >
                    {deployStatus === 'deploying' ? 'Deploying…' : (deployingToLive ? '⚡ Deploy to LIVE' : '⚡ Deploy')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Human-readable labels for NSR metric column names.
const NSR_METRIC_LABEL = {
  sharpe_ratio:   'Sharpe',
  blended_sharpe: 'Blend Sharpe',
  total_return:   'Return',
  blended_return: 'Blend Return',
  sortino_ratio:  'Sortino',
  calmar_ratio:      'Calmar',
  blended_sortino:   'Blend Sortino',
  blended_calmar:    'Blend Calmar',
  profit_factor:     'Profit Factor',
  win_rate:       'Win Rate',
  nsr_score:      'NSR',
};

// Full stat rows used in the head-to-head compare card.
const COMPARE_STAT_ROWS = [
  // NSR robustness section
  { label: 'NSR Score',     key: 'nsr_score',          fmt: v => fmtNum(v),                             win: 'higher' },
  { label: 'Neighbor Std',  key: 'nsr_neighbor_std',   fmt: v => fmtNum(v),                             win: 'lower'  },
  { label: 'Neighbors',     key: 'nsr_neighbor_count', fmt: v => v ?? '—',                              win: null     },
  // Core risk-adjusted
  { label: 'Sharpe',        key: 'sharpe_ratio',        fmt: v => fmtNum(v),                            win: 'higher' },
  { label: 'Sortino',       key: 'sortino_ratio',       fmt: v => fmtNum(v),                            win: 'higher' },
  { label: 'Calmar',        key: 'calmar_ratio',        fmt: v => fmtNum(v),                            win: 'higher' },
  // Return / drawdown
  { label: 'Total Return',  key: 'total_return',        fmt: v => fmtPct(v),                            win: 'higher' },
  { label: 'Max Drawdown',  key: 'max_drawdown',        fmt: v => fmtPct(v),                            win: 'higher' }, // less-negative = better
  // Trade stats
  { label: 'Win Rate',      key: 'win_rate',            fmt: v => v != null ? `${v.toFixed(0)}%` : '—', win: 'higher' },
  { label: 'Profit Factor', key: 'profit_factor',       fmt: v => fmtNum(v),                            win: 'higher' },
  { label: '# Trades',      key: 'num_trades',          fmt: v => v ?? '—',                             win: null     },
  // Blended (idle-deployment adjusted)
  { label: 'Blend Return',  key: 'blended_return',      fmt: v => fmtPct(v),                            win: 'higher' },
  { label: 'Blend Sharpe',  key: 'blended_sharpe',      fmt: v => fmtNum(v),                            win: 'higher' },
  { label: 'Blend Sortino', key: 'blended_sortino',     fmt: v => fmtNum(v),                            win: 'higher' },
  { label: 'Blend Calmar',  key: 'blended_calmar',      fmt: v => fmtNum(v),                            win: 'higher' },
  { label: 'Blend MaxDD',   key: 'blended_max_drawdown',fmt: v => fmtPct(v),                            win: 'higher' },
];

// Which run.bh_* field maps to each COMPARE_STAT_ROW key.
const BH_FIELD_MAP = {
  sharpe_ratio:  'bh_sharpe_ratio',
  total_return:  'bh_total_return',
  max_drawdown:  'bh_max_drawdown',
};
const BH_IDLE_FIELD_MAP = {
  sharpe_ratio:  'bh_idle_sharpe_ratio',
  total_return:  'bh_idle_total_return',
  max_drawdown:  'bh_idle_max_drawdown',
};

// OOS-only mode reads a PARTIAL set from the stored combo (run_oos_task only
// writes these fields — no oos_sortino/calmar/win_rate/profit_factor exist, and
// NSR/neighbor stats are IS-only concepts). Any COMPARE_STAT_ROW key not mapped
// here resolves to undefined → null → dashed cell. This is deliberate: we show
// the honest OOS numbers we actually have rather than fabricating the rest.
const OOS_FIELD_MAP = {
  sharpe_ratio:   'oos_sharpe_ratio',
  total_return:   'oos_total_return',
  max_drawdown:   'oos_max_drawdown',
  num_trades:     'oos_num_trades',
  blended_sharpe: 'oos_blended_sharpe',
};
// B&H over the OOS window — only Sharpe is persisted (bh_oos_sharpe_ratio /
// bh_idle_oos_sharpe_ratio). Every other B&H row dashes in OOS mode.
const BH_OOS_FIELD_MAP = { sharpe_ratio: 'bh_oos_sharpe_ratio' };
const BH_IDLE_OOS_FIELD_MAP = { sharpe_ratio: 'bh_idle_oos_sharpe_ratio' };

// Metric options for the "raw" column dropdown in ComboCompareCard.
// Each entry: { value: sort key, label: display label }
const RAW_METRIC_OPTIONS = [
  { value: 'sharpe_ratio',       label: 'Sharpe' },
  { value: 'blended_sharpe',     label: 'Blend Sharpe',  idle: true },
  { value: 'total_return',       label: 'Total Return' },
  { value: 'blended_return',     label: 'Blend Return',  idle: true },
  { value: 'max_drawdown',       label: 'Max Drawdown',  asc: true },
  { value: 'blended_max_drawdown', label: 'Blend MaxDD', idle: true, asc: true },
  { value: 'win_rate',           label: 'Win Rate' },
  { value: 'profit_factor',      label: 'Profit Factor' },
  { value: 'sortino_ratio',      label: 'Sortino' },
  { value: 'calmar_ratio',       label: 'Calmar' },
  { value: 'blended_sortino',    label: 'Blend Sortino', idle: true },
  { value: 'blended_calmar',     label: 'Blend Calmar',  idle: true },
];

// Head-to-head comparison: #1 raw metric | #1 NSR Score | B&H security | B&H idle.
const ComboCompareCard = ({ rawBest: initialRawBest, nsrBest: initialNsrBest, metric, run, runId, minTrades = 0 }) => {
  const hasIdle = run?.idle_deployment && run.idle_deployment !== 'cash';

  // Which metric the "raw" column is sorted/ranked by — starts at the run's optimize metric.
  const [selectedRawMetric, setSelectedRawMetric] = useState(metric || 'sharpe_ratio');
  const [rawBest, setRawBest] = useState(initialRawBest);
  // Local NSR best — kept in sync via re-fetch so it's always as fresh as rawBest.
  const [nsrBest, setNsrBest] = useState(initialNsrBest);
  const [rawLoading, setRawLoading] = useState(false);
  // Trades panel: which combo's trades are showing ('raw' | 'nsr' | null), the data, and loading state.
  const [tradesFor, setTradesFor] = useState(null);   // null = hidden
  const [trades, setTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);

  // When user picks a different raw metric OR min_trades threshold changes, fetch fresh
  // #1 combos for BOTH columns in parallel — always respecting the min-trades filter so
  // the card never shows a sub-threshold combo after the user raises the threshold.
  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    (async () => {
      setRawLoading(true);
      try {
        const opt = RAW_METRIC_OPTIONS.find(o => o.value === selectedRawMetric);
        const sort = opt?.asc ? selectedRawMetric : `-${selectedRawMetric}`;
        const tradeFilter = minTrades > 0 ? { min_num_trades: minTrades } : {};
        const [rawRes, nsrRes] = await Promise.all([
          tradingService.filterOptimizationCombos(runId, { ...tradeFilter, limit: 1, offset: 0, sort, skip_count: true }),
          tradingService.filterOptimizationCombos(runId, { ...tradeFilter, limit: 1, offset: 0, sort: '-nsr_score', skip_count: true }),
        ]);
        if (!cancelled) {
          const rawCombos = rawRes?.data?.combos || [];
          const nsrCombos = nsrRes?.data?.combos || [];
          if (rawCombos.length > 0) setRawBest(rawCombos[0]);
          if (nsrCombos.length > 0) setNsrBest(nsrCombos[0]);
        }
      } catch (_) { /* swallow — keep showing previous best */ }
      finally { if (!cancelled) setRawLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [runId, selectedRawMetric, minTrades]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trades panel meta: stored count + idle asset + engine + metric comparison.
  const [tradesMeta, setTradesMeta] = useState({ numStored: null, idleDeployment: null, engine: null, stored: null, rerun: null });

  // Fetch trades for a combo. forceEngine=null uses auto (vectorbt preferred).
  const _fetchTrades = async (comboId, forceEngine = null) => {
    setTrades([]);
    setTradesLoading(true);
    try {
      const res = await tradingService.getComboTrades(runId, comboId, forceEngine);
      setTrades(res?.data?.trades || []);
      setTradesMeta({
        numStored: res?.data?.num_trades_stored ?? null,
        numRerun: res?.data?.num_trades_rerun ?? null,
        idleDeployment: res?.data?.idle_deployment ?? null,
        engine: res?.data?.engine ?? null,
        stored: res?.data?.stored ?? null,   // { total_return, sharpe_ratio, blended_return, blended_sharpe }
        rerun:  res?.data?.rerun  ?? null,   // { total_return, sharpe_ratio }
      });
    } catch (_) {
      setTrades([]);
    } finally {
      setTradesLoading(false);
    }
  };

  // Toggle trades panel for a column ('raw' or 'nsr').
  const handleTradesClick = (which) => {
    const combo = which === 'raw' ? rawBest : nsrBest;
    if (!combo?._id || !runId) return;
    if (tradesFor === which) { setTradesFor(null); setTrades([]); return; }
    setTradesFor(which);
    _fetchTrades(combo._id);
  };

  // Re-run the currently-visible trades using a specific engine (for comparison).
  const handleEngineRerun = (engine) => {
    if (!tradesFor || tradesLoading) return;
    const combo = tradesFor === 'raw' ? rawBest : nsrBest;
    if (!combo?._id) return;
    _fetchTrades(combo._id, engine);
  };

  // Sample toggle: 'is' (IS-only — original cached IS metrics) vs
  // 'full' (full IS+OOS — from run.full_sample_metrics computed at NSR-finalize).
  // The full-sample mode uses cached top_blend_sharpe / top_nsr picks (because
  // those are what we snapshot at finalize time); the raw-metric dropdown is
  // ignored in that mode.
  const [sample, setSample] = useState('is');
  const fsm = run?.full_sample_metrics || null;
  const fsmAvailable = !!(fsm && (fsm.top_blend_sharpe || fsm.top_nsr));
  // OOS-only mode is available once the run had a hold-out split AND run_oos_task
  // has written oos_* onto the combos (verified via the fetched best combos).
  const oosAvailable = !!(
    run?.oos_split_pct > 0 &&
    (rawBest?.oos_sharpe_ratio != null || nsrBest?.oos_sharpe_ratio != null)
  );
  // Failsafe: if the active mode's data isn't available (e.g. navigating to a
  // run without an OOS split, or before full-sample metrics finalize), fall
  // back to IS so the table never renders an all-dashed dead state.
  useEffect(() => {
    if (sample === 'oos' && !oosAvailable) setSample('is');
    else if (sample === 'full' && !fsmAvailable) setSample('is');
  }, [sample, oosAvailable, fsmAvailable]);
  // Effective best objects based on sample mode. In 'full' mode we use the
  // cached full-sample picks, which are fixed (top_blend_sharpe vs top_nsr).
  const effectiveRaw = sample === 'full' && fsm?.top_blend_sharpe
    ? fsm.top_blend_sharpe : rawBest;
  const effectiveNsr = sample === 'full' && fsm?.top_nsr
    ? fsm.top_nsr : nsrBest;
  const isSame = sample === 'full'
    ? (fsm?.top_blend_sharpe?.combo_index === fsm?.top_nsr?.combo_index)
    : (rawBest?._id === nsrBest?._id);
  const rawLabel = sample === 'full'
    ? 'Blend Sharpe'  // full-sample only cached top by blended_sharpe
    : (NSR_METRIC_LABEL[selectedRawMetric] ||
       RAW_METRIC_OPTIONS.find(o => o.value === selectedRawMetric)?.label ||
       selectedRawMetric);
  const symbol = run?.symbol || 'Security';
  const idle = run?.idle_deployment;
  // B&H values — pull from full_sample_metrics in 'full' mode, run.bh_* in 'is' mode.
  const fsmBhSym  = (fsm?.bh_symbol  || {});
  const fsmBhIdle = (fsm?.bh_idle    || {});
  const hasBh = sample === 'full'
    ? (fsmBhSym.total_return != null || fsmBhSym.sharpe_ratio != null)
    : sample === 'oos'
    ? (run?.bh_oos_sharpe_ratio != null)
    : (run?.bh_total_return != null || run?.bh_sharpe_ratio != null);
  const hasIdleBh = sample === 'full'
    ? hasIdle && (fsmBhIdle.total_return != null || fsmBhIdle.sharpe_ratio != null)
    : sample === 'oos'
    ? hasIdle && (run?.bh_idle_oos_sharpe_ratio != null)
    : hasIdle && (run?.bh_idle_total_return != null || run?.bh_idle_sharpe_ratio != null);

  // Dropdown options — filter out idle-only options when no idle deployment
  const metricOptions = RAW_METRIC_OPTIONS.filter(o => !o.idle || hasIdle);

  // Param sections — how many columns the param header needs
  const paramCols = isSame ? 1 : 2;

  if (!effectiveRaw || !effectiveNsr) return null;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22] overflow-hidden">
      {/* Title bar with sample toggle + raw-metric dropdown */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700/60 flex flex-wrap items-center gap-3">
        <h3 className="font-serif text-base text-gray-900 dark:text-gray-100">Head-to-Head Comparison</h3>
        {/* Sample toggle: IS Only vs Full IS+OOS. Disabled when full_sample_metrics
            hasn't been computed yet (older opts, or run still in progress). */}
        <div className="flex items-center gap-1 rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
          <button
            onClick={() => setSample('is')}
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 transition-colors
              ${sample === 'is'
                ? 'bg-brand-gold/20 text-brand-gold font-semibold'
                : 'text-gray-500 hover:text-gray-300'}`}
            title="In-Sample metrics — what the optimizer trained on"
          >IS Only</button>
          <button
            onClick={() => fsmAvailable && setSample('full')}
            disabled={!fsmAvailable}
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 transition-colors
              ${sample === 'full'
                ? 'bg-brand-gold/20 text-brand-gold font-semibold'
                : fsmAvailable ? 'text-gray-500 hover:text-gray-300' : 'text-gray-700 cursor-not-allowed'}`}
            title={fsmAvailable
              ? 'Full IS+OOS sample — honest end-to-end performance (re-run on full date range)'
              : 'Full-sample metrics not yet computed — re-run NSR or wait for finalize'}
          >Full IS+OOS</button>
          <button
            onClick={() => oosAvailable && setSample('oos')}
            disabled={!oosAvailable}
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 transition-colors border-l border-gray-300 dark:border-gray-600
              ${sample === 'oos'
                ? 'bg-brand-gold/20 text-brand-gold font-semibold'
                : oosAvailable ? 'text-gray-500 hover:text-gray-300' : 'text-gray-700 cursor-not-allowed'}`}
            title={oosAvailable
              ? 'Out-of-sample window only — how the IS-selected combos held up on the hold-out. Partial metric set (Sharpe / Total Return / Max DD / Trades).'
              : 'No out-of-sample data — this run had no OOS split, or OOS validation has not completed.'}
          >OOS Only</button>
        </div>
        {/* Dropdown to switch which metric the "raw" column ranks by.
            Hidden in 'full' mode because full-sample only caches top_blend_sharpe. */}
        {sample === 'is' && (
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="text-[10px] uppercase tracking-wider text-gray-500">Raw rank by</span>
            <select
              value={selectedRawMetric}
              onChange={e => setSelectedRawMetric(e.target.value)}
              className="text-[11px] bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-2 py-0.5 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-brand-gold/50 cursor-pointer"
            >
              {metricOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {rawLoading && <span className="text-[10px] text-gray-500 animate-pulse">…</span>}
          </div>
        )}
        {sample === 'full' && (
          <span className="text-[10px] text-gray-500 ml-auto italic">
            Re-computed on full IS+OOS via single-combo backtests
          </span>
        )}
        {sample === 'oos' && (
          <span className="text-[10px] text-gray-500 ml-auto italic">
            Out-of-sample hold-out only · partial metric set (unavailable rows shown as —)
          </span>
        )}
        {isSame && (
          <span className="text-[11px] text-green-400 font-medium whitespace-nowrap">
            ✓ Same combo — your best is also your most robust
          </span>
        )}
      </div>

      {/* Parameters — flex-wrap grid above the table, one panel per combo */}
      <div className={`grid grid-cols-${paramCols} divide-x divide-gray-100 dark:divide-gray-700/60 border-b border-gray-100 dark:border-gray-700/60`}>
        <div className="px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">#1 Raw {rawLabel}</div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(effectiveRaw.parameters || {}).map(([k, v]) => (
              <ParamChip key={k} name={k} value={v} />
            ))}
          </div>
        </div>
        {!isSame && (
          <div className="px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-brand-gold mb-1.5">#1 NSR Score</div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(effectiveNsr.parameters || {}).map(([k, v]) => (
                <ParamChip key={k} name={k} value={v} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Stats table — 3-5 columns: metric | raw | nsr | [bh symbol] | [bh idle] */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-[11px] table-fixed"
          style={{ minWidth: hasIdleBh ? 500 : hasBh ? 440 : 360 }}>
          <colgroup>
            {hasIdleBh ? (
              // 5 cols: metric + raw + nsr + bh + bh-idle
              <>
                <col style={{ width: '20%' }} /><col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} /><col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
              </>
            ) : hasBh ? (
              // 4 cols: metric + raw + nsr + bh
              <>
                <col style={{ width: '22%' }} /><col style={{ width: '26%' }} />
                <col style={{ width: '26%' }} /><col style={{ width: '26%' }} />
              </>
            ) : (
              // 3 cols: metric + raw + nsr (no B&H data)
              <>
                <col style={{ width: '28%' }} /><col style={{ width: '36%' }} />
                <col style={{ width: '36%' }} />
              </>
            )}
          </colgroup>
          <thead>
            <tr className="border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/20">
              <th className="text-left px-3 py-2 text-gray-400 uppercase tracking-wider font-medium">Metric</th>
              <th className="text-right px-3 py-2 text-gray-600 dark:text-gray-300 font-medium">#1 Raw {rawLabel}</th>
              <th className="text-right px-3 py-2 text-brand-gold font-medium">#1 NSR Score</th>
              {hasBh && (
                <th className="text-right px-3 py-2 text-blue-400 font-medium">B&amp;H {symbol}</th>
              )}
              {hasIdleBh && (
                <th className="text-right px-3 py-2 text-purple-400 font-medium">B&amp;H {idle}</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-gray-800/40">
            {COMPARE_STAT_ROWS.map(({ label, key, fmt, win }) => {
              // Combo values: 'is'/'full' read the metric key directly; 'oos'
              // remaps to the partial oos_* field set (unmapped keys → dash).
              const rv = sample === 'oos'
                ? (effectiveRaw[OOS_FIELD_MAP[key]] ?? null) : effectiveRaw[key];
              const nv = sample === 'oos'
                ? (effectiveNsr[OOS_FIELD_MAP[key]] ?? null) : effectiveNsr[key];
              // B&H values: 'full' → full_sample_metrics.bh_* (full date range);
              // 'oos' → run.bh_oos_* (Sharpe only, rest dash); 'is' → run.bh_*
              // (IS window only, populated at IS-finalize).
              let bv = null, biv = null;
              if (sample === 'full') {
                bv  = hasBh     ? (fsmBhSym[key]  ?? null) : null;
                biv = hasIdleBh ? (fsmBhIdle[key] ?? null) : null;
              } else if (sample === 'oos') {
                bv  = hasBh     ? (run[BH_OOS_FIELD_MAP[key]]      ?? null) : null;
                biv = hasIdleBh ? (run[BH_IDLE_OOS_FIELD_MAP[key]] ?? null) : null;
              } else {
                bv  = hasBh     ? (run[BH_FIELD_MAP[key]]      ?? null) : null;
                biv = hasIdleBh ? (run[BH_IDLE_FIELD_MAP[key]] ?? null) : null;
              }
              let rawWins = false, nsrWins = false;
              if (!isSame && win === 'higher' && rv != null && nv != null) {
                rawWins = rv > nv; nsrWins = nv > rv;
              } else if (!isSame && win === 'lower' && rv != null && nv != null) {
                rawWins = rv < nv; nsrWins = nv < rv;
              }
              // Skip blended rows when both combos have no blended data
              if (key.startsWith('blended') && rv == null && nv == null) return null;
              // Only show the trades-click button in IS mode — the trades API
              // takes the IS-cached _id, which doesn't necessarily match the
              // full-sample pick's combo_index.
              const isTradesRow = key === 'num_trades' && sample === 'is';
              return (
                <tr key={key} className="hover:bg-gray-50/70 dark:hover:bg-gray-800/20">
                  <td className="px-3 py-1.5 text-gray-400">{label}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums font-mono
                    ${rawLoading ? 'opacity-40' : ''} ${rawWins ? 'text-green-400 font-semibold' : valColor(rv)}`}>
                    {isTradesRow && rv != null ? (
                      <button
                        onClick={() => handleTradesClick('raw')}
                        className={`underline decoration-dotted hover:decoration-solid cursor-pointer transition-colors
                          ${tradesFor === 'raw' ? 'text-brand-gold' : 'text-gray-300 hover:text-brand-gold'}`}
                        title="Show trade list"
                      >{fmt(rv)}</button>
                    ) : (<>{fmt(rv)}{rawWins ? ' ↑' : ''}</>)}
                  </td>
                  <td className={`px-3 py-1.5 text-right tabular-nums font-mono
                    ${isSame ? 'text-gray-400' : nsrWins ? 'text-green-400 font-semibold' : valColor(nv)}`}>
                    {isSame ? '↑ same' : isTradesRow && nv != null ? (
                      <button
                        onClick={() => handleTradesClick('nsr')}
                        className={`underline decoration-dotted hover:decoration-solid cursor-pointer transition-colors
                          ${tradesFor === 'nsr' ? 'text-brand-gold' : 'text-gray-300 hover:text-brand-gold'}`}
                        title="Show trade list"
                      >{fmt(nv)}</button>
                    ) : (<>{fmt(nv)}{!isSame && nsrWins ? ' ↑' : ''}</>)}
                  </td>
                  {hasBh && (
                    <td className={`px-3 py-1.5 text-right tabular-nums font-mono
                      ${bv != null ? (key === 'max_drawdown' ? 'text-red-400' : valColor(bv)) : 'text-gray-600 dark:text-gray-600'}`}>
                      {bv != null ? fmt(bv) : '—'}
                    </td>
                  )}
                  {hasIdleBh && (
                    <td className={`px-3 py-1.5 text-right tabular-nums font-mono
                      ${biv != null ? (key === 'max_drawdown' ? 'text-red-400' : valColor(biv)) : 'text-gray-600 dark:text-gray-600'}`}>
                      {biv != null ? fmt(biv) : '—'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Inline trades panel ── */}
      {tradesFor && (
        <div className="border-t border-gray-100 dark:border-gray-700/60">
          <div className="px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 bg-gray-50/60 dark:bg-gray-800/20">
            <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
              Trades — {tradesFor === 'raw' ? `#1 Raw ${rawLabel}` : '#1 NSR Score'}
            </span>
            <span className="text-[10px] text-gray-400">{symbol}</span>
            {tradesMeta.idleDeployment && !tradesLoading && (
              <span className="text-[10px] text-gray-500">incl. {tradesMeta.idleDeployment} idle periods</span>
            )}
            {!tradesLoading && tradesMeta.engine === 'vectorbt' && tradesMeta.numStored != null && tradesMeta.numRerun != null && (
              tradesMeta.numRerun === tradesMeta.numStored
                ? <span className="text-[10px] text-green-500/70" title="Trade log re-run using the exact same vectorbt engine as the optimization — active trade count matches">✓ exact match ({tradesMeta.numRerun} trades)</span>
                : <span className="text-[10px] text-yellow-500/80" title="Active trade count differs despite using the same engine — price data may have changed since the optimization ran">⚠ {tradesMeta.numStored} stored, {tradesMeta.numRerun} re-run</span>
            )}
            {!tradesLoading && tradesMeta.engine === 'backtesting.py' && tradesMeta.numStored != null && tradesMeta.numRerun != null && tradesMeta.numRerun !== tradesMeta.numStored && (
              <span className="text-[10px] text-yellow-500/80" title="Approximate trade log (backtesting.py engine). Count may differ slightly from the vectorbt optimization run.">
                ⚠ {tradesMeta.numStored} stored, {tradesMeta.numRerun} re-run (approx.)
              </span>
            )}
            {/* Engine badge + cross-check button */}
            {!tradesLoading && tradesMeta.engine && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                tradesMeta.engine === 'vectorbt'
                  ? 'bg-purple-500/10 text-purple-400'
                  : 'bg-orange-500/10 text-orange-400'
              }`}>{tradesMeta.engine}</span>
            )}
            {!tradesLoading && trades.length > 0 && (
              tradesMeta.engine === 'vectorbt'
                ? <button
                    onClick={() => handleEngineRerun('backtesting.py')}
                    className="text-[10px] text-gray-500 hover:text-orange-400 border border-gray-700/50 hover:border-orange-400/40 px-1.5 py-0.5 rounded transition-colors"
                    title="Re-run using backtesting.py for cross-verification"
                  >compare bt.py ↺</button>
                : <button
                    onClick={() => handleEngineRerun(null)}
                    className="text-[10px] text-gray-500 hover:text-purple-400 border border-gray-700/50 hover:border-purple-400/40 px-1.5 py-0.5 rounded transition-colors"
                    title="Switch back to vectorbt (exact match)"
                  >back to vectorbt ↺</button>
            )}
            {tradesLoading && <span className="text-[10px] text-gray-500 animate-pulse">
              {tradesMeta.engine ? `switching to ${tradesMeta.engine === 'vectorbt' ? 'bt.py' : 'vectorbt'}…` : 're-running…'}
            </span>}
            <button
              onClick={() => { setTradesFor(null); setTrades([]); }}
              className="ml-auto text-[10px] text-gray-400 hover:text-gray-200 leading-none"
              title="Close"
            >✕</button>
          </div>
          {/* ── Metric verification row (vectorbt only) ── */}
          {!tradesLoading && tradesMeta.engine === 'vectorbt' && tradesMeta.stored && tradesMeta.rerun && (() => {
            const fmtPct = v => v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(1)}%` : '—';
            const fmtN   = v => v != null ? v.toFixed(2) : '—';
            const s = tradesMeta.stored;
            const r = tradesMeta.rerun;
            const totalOk  = s.total_return  != null && r.total_return  != null && Math.abs(s.total_return  - r.total_return)  < 1;
            const sharpeOk = s.sharpe_ratio  != null && r.sharpe_ratio  != null && Math.abs(s.sharpe_ratio  - r.sharpe_ratio)  < 0.01;
            const allMatch = totalOk && sharpeOk;
            return (
              <div className={`px-4 py-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] border-b ${allMatch ? 'border-green-800/20 bg-green-950/10' : 'border-yellow-800/20 bg-yellow-950/10'}`}>
                <span className={`font-medium ${allMatch ? 'text-green-500/70' : 'text-yellow-500/70'}`}>
                  {allMatch ? '✓ re-run metrics match stored' : '⚠ re-run metrics differ from stored (price data may have changed)'}
                </span>
                <span className="text-gray-500">Total Ret: <span className="text-gray-300">{fmtPct(s.total_return)}</span> stored / <span className={totalOk ? 'text-green-400' : 'text-yellow-400'}>{fmtPct(r.total_return)}</span> re-run</span>
                <span className="text-gray-500">Sharpe: <span className="text-gray-300">{fmtN(s.sharpe_ratio)}</span> stored / <span className={sharpeOk ? 'text-green-400' : 'text-yellow-400'}>{fmtN(r.sharpe_ratio)}</span> re-run</span>
                {s.blended_return != null && <span className="text-gray-500">Blend Ret: <span className="text-gray-300">{fmtPct(s.blended_return)}</span> stored</span>}
              </div>
            );
          })()}
          {tradesLoading ? (
            <div className="px-4 py-6 text-center text-[11px] text-gray-500">Loading trades…</div>
          ) : trades.length === 0 ? (
            <div className="px-4 py-6 text-center text-[11px] text-gray-500">
              No trades found on current data.
              {tradesMeta.numStored != null && tradesMeta.numStored > 0 && (
                <span className="block text-[10px] text-gray-600 mt-1">
                  Optimization recorded {tradesMeta.numStored} trade{tradesMeta.numStored !== 1 ? 's' : ''} — price data may have been updated since then.
                </span>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto scrollbar-thin">
              {(() => {
                // Pre-compute running equity starting at $100, compounding through
                // ALL trades in order (active + idle). Used for the "Growth of $100" column.
                let eq = 100;
                const growthArr = trades.map(t => {
                  const ret = typeof t.return_pct === 'number' ? t.return_pct : 0;
                  eq = eq * (1 + ret / 100);
                  return eq;
                });
                return (
                  <table className="w-full text-[11px] table-auto min-w-[820px]">
                    <thead className="bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-700/40">
                      <tr className="text-[10px] uppercase tracking-wider text-gray-400">
                        <th className="px-3 py-1.5 text-left">#</th>
                        <th className="px-3 py-1.5 text-left">Asset</th>
                        <th className="px-3 py-1.5 text-left">Entry</th>
                        <th className="px-3 py-1.5 text-left">Exit</th>
                        <th className="px-3 py-1.5 text-right">Bars</th>
                        <th className="px-3 py-1.5 text-right">Entry $</th>
                        <th className="px-3 py-1.5 text-right">Exit $</th>
                        <th className="px-3 py-1.5 text-right">Return %</th>
                        <th className="px-3 py-1.5 text-right" title="Running portfolio value if you started with $100 at the first trade">$100 →</th>
                        <th className="px-3 py-1.5 text-right">MAE %</th>
                        <th className="px-3 py-1.5 text-right">MFE %</th>
                        <th className="px-3 py-1.5 text-left">Exit Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-gray-800/30">
                      {trades.map((t, i) => {
                        const isIdle = t.asset && t.asset !== symbol;
                        const ret = typeof t.return_pct === 'number' ? t.return_pct : null;
                        const retColor = ret == null ? 'text-gray-400' : ret > 0 ? 'text-green-400' : ret < 0 ? 'text-red-400' : 'text-gray-400';
                        const maeColor = t.mae_pct != null ? (t.mae_pct < -5 ? 'text-red-400' : 'text-gray-400') : 'text-gray-600';
                        const mfeColor = t.mfe_pct != null ? (t.mfe_pct > 10 ? 'text-green-400' : 'text-gray-400') : 'text-gray-600';
                        const entryDate = t.entry_date ? t.entry_date.slice(0, 10) : '—';
                        const exitDate = t.is_open
                          ? (t.exit_date
                              ? <span className="font-mono">{t.exit_date.slice(0, 10)}<span className="ml-1 text-[8px] uppercase tracking-wide text-amber-400/80 font-semibold not-italic">mtm</span></span>
                              : <span className="text-yellow-400">open</span>)
                          : (t.exit_date ? t.exit_date.slice(0, 10) : '—');
                        const growth = growthArr[i];
                        const growthColor = growth >= 100 ? 'text-green-400' : 'text-red-400';
                        const growthFmt = growth >= 1000
                          ? `$${(growth / 1000).toFixed(2)}k`
                          : `$${growth.toFixed(2)}`;
                        return (
                          <tr key={i} className={`hover:bg-gray-50/50 dark:hover:bg-gray-800/20 ${isIdle ? 'opacity-50' : ''} ${t.is_open ? 'italic' : ''}`}>
                            <td className="px-3 py-1.5 text-gray-500 tabular-nums">{i + 1}</td>
                            <td className="px-3 py-1.5">
                              <span className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded ${
                                isIdle
                                  ? 'bg-gray-700/40 text-gray-400'
                                  : 'bg-brand-gold/10 text-brand-gold'
                              }`}>
                                {t.asset || symbol}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 text-gray-400 font-mono">{entryDate}</td>
                            <td className="px-3 py-1.5 text-gray-400 font-mono">{exitDate}</td>
                            <td className="px-3 py-1.5 text-right text-gray-500 tabular-nums">{t.bars_held ?? '—'}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums font-mono text-gray-400">{t.entry_price != null ? `$${t.entry_price.toFixed(2)}` : '—'}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums font-mono text-gray-400">{t.exit_price != null ? `$${t.exit_price.toFixed(2)}` : '—'}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums font-mono font-semibold ${retColor}`}>
                              {ret != null ? `${ret >= 0 ? '+' : ''}${ret.toFixed(2)}%` : '—'}
                            </td>
                            <td className={`px-3 py-1.5 text-right tabular-nums font-mono font-semibold ${growthColor}`}>
                              {growthFmt}
                            </td>
                            <td className={`px-3 py-1.5 text-right tabular-nums font-mono ${maeColor}`}>
                              {t.mae_pct != null ? `${t.mae_pct.toFixed(1)}%` : '—'}
                            </td>
                            <td className={`px-3 py-1.5 text-right tabular-nums font-mono ${mfeColor}`}>
                              {t.mfe_pct != null ? `+${t.mfe_pct.toFixed(1)}%` : '—'}
                            </td>
                            <td className="px-3 py-1.5 text-[10px]">
                              {t.exit_reason === 'data_end'
                                ? <span className="text-amber-400/70 italic">mark-to-mkt</span>
                                : <span className="text-gray-500">{t.exit_reason || '—'}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const NsrLeaderboard = ({ title, subtitle, combos, loading, primaryKey, secondaryKey, highlight, onSave, onDeploy, onRowClick }) => (
  <div className={`rounded-xl border ${highlight ? 'border-brand-gold/40' : 'border-gray-200 dark:border-gray-800'} bg-white dark:bg-[#161b22] p-3`}>
    <div className="mb-2">
      <div className={`text-sm font-medium ${highlight ? 'text-brand-gold' : 'text-gray-700 dark:text-gray-300'}`}>{title}</div>
      <div className="text-[10px] text-gray-500">{subtitle}</div>
    </div>
    {loading ? (
      <div className="text-xs text-gray-500 py-6 text-center">loading…</div>
    ) : (
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-[11px] min-w-[520px]">
          <thead className="text-gray-400 uppercase tracking-wider">
            <tr>
              <th className="text-left px-1 py-1 w-12"># / ID</th>
              {(onSave || onDeploy) && <th className="px-1 py-1"></th>}
              <th className="text-left px-1 py-1">Params</th>
              <th className="text-right px-1 py-1">{NSR_METRIC_LABEL[primaryKey] || primaryKey}</th>
              <th className="text-right px-1 py-1">{NSR_METRIC_LABEL[secondaryKey] || secondaryKey}</th>
              <th className="text-right px-1 py-1">NbrStd</th>
              <th className="text-right px-1 py-1">Ret%</th>
              <th className="text-right px-1 py-1">BlendRet</th>
              <th className="text-right px-1 py-1">MaxDD</th>
              <th className="text-right px-1 py-1">Win%</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800/70">
            {combos.map((c, i) => (
              <tr
                key={c._combo_index ?? i}
                className={`cursor-pointer hover:bg-gray-50/80 dark:hover:bg-gray-800/40 ${i === 0 && highlight ? 'bg-brand-gold/5' : ''}`}
                onClick={() => onRowClick?.(c)}
              >
                {/* Rank + combo_index — consistent cross-table identifier */}
                <td className="px-1 py-1">
                  <div className="text-gray-500">{i + 1}</div>
                  <div className="text-[9px] text-gray-600 dark:text-gray-600 font-mono">C{c._combo_index ?? '?'}</div>
                </td>
                {(onSave || onDeploy) && (
                  <td className="px-1 py-1" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      {onDeploy && (
                        <button
                          onClick={() => onDeploy(c)}
                          className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-green-600/20 border border-green-500/40 text-green-400 hover:bg-green-600/30 transition-colors whitespace-nowrap"
                          title="Deploy as a live trading strategy instance"
                        >
                          Deploy
                        </button>
                      )}
                      {onSave && (
                        <button
                          onClick={() => onSave(c)}
                          className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border border-brand-gold/50 text-brand-gold hover:bg-brand-gold/10 transition-colors whitespace-nowrap"
                        >
                          Save
                        </button>
                      )}
                    </div>
                  </td>
                )}
                <td className="px-1 py-1"><ParamChips params={c.parameters || {}} max={2} /></td>
                <td className={`px-1 py-1 text-right tabular-nums font-mono ${valColor(c[primaryKey])}`}>{fmtNum(c[primaryKey])}</td>
                <td className={`px-1 py-1 text-right tabular-nums font-mono ${valColor(c[secondaryKey])}`}>{fmtNum(c[secondaryKey])}</td>
                <td className="px-1 py-1 text-right tabular-nums font-mono text-gray-500">{fmtNum(c.nsr_neighbor_std)}</td>
                <td className={`px-1 py-1 text-right tabular-nums font-mono ${valColor(c.total_return)}`}>{fmtPct(c.total_return)}</td>
                <td className={`px-1 py-1 text-right tabular-nums font-mono ${valColor(c.blended_return)}`}>
                  {c.blended_return != null ? fmtPct(c.blended_return) : '—'}
                </td>
                <td className="px-1 py-1 text-right tabular-nums font-mono text-red-400">{fmtPct(c.max_drawdown)}</td>
                <td className="px-1 py-1 text-right tabular-nums font-mono text-gray-500">
                  {c.win_rate != null ? `${c.win_rate.toFixed(0)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const RunDetail = ({ runId, onBack }) => {
  const [run, setRun] = useState(null);
  const [combos, setCombos] = useState([]);
  const [totalMatching, setTotalMatching] = useState(0);
  const [totalInRun, setTotalInRun] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [activeViz, setActiveViz] = useState('nsr');
  // OOS full-grid run state — mirrors run.oos_status / run.oos_progress but
  // kept local so the button updates immediately after triggering.
  const [oosStatus, setOosStatus] = useState(null);
  const [oosProgress, setOosProgress] = useState(null);
  const [oosError, setOosError] = useState(null);
  const [oosTriggering, setOosTriggering] = useState(false);

  // NSR state. status: null/pending/running/complete/failed; progress: 'X/Y' while running;
  // summary populated once status === 'complete' so the NSR tab can render aggregates.
  const [nsr, setNsr] = useState({ status: null, progress: null, metric: null, lambda: null, error: null, summary: null });
  const [nsrTriggering, setNsrTriggering] = useState(false);

  // Full-pipeline state: stages detected + dispatch status
  const [pipelineTriggering, setPipelineTriggering] = useState(false);
  const [pipelineStages,     setPipelineStages]     = useState(null);  // null = unknown, [] = done, [...] = pending
  const [pipelineError,      setPipelineError]       = useState(null);
  const [pipelineQueued,     setPipelineQueued]      = useState(false);

  // Cancel state — inline confirm within the detail header (no separate modal)
  const [cancelConfirming, setCancelConfirming] = useState(false);
  const [cancellingDetail, setCancellingDetail] = useState(false);
  const [cancelDetailError, setCancelDetailError] = useState(null);

  const [filters, setFilters] = useState({
    min_sharpe: '', min_blended_sharpe: '', min_return: '',
    max_drawdown_abs: '', min_num_trades: '', min_win_rate: '',
    min_profit_factor: '',
    // OOS pipeline filters (only active after OOS task completes)
    min_degradation_ratio: '', min_oos_blended_sharpe: '',
    sort: '-sharpe_ratio',
  });
  const [paramFilters, setParamFilters] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await tradingService.getOptimizationStatus(runId);
        if (!cancelled) setRun(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [runId]);

  // Sync OOS status from run object on initial load, then poll while running/pending.
  useEffect(() => {
    if (!run) return;
    setOosStatus(run.oos_status || null);
    setOosProgress(run.oos_progress || null);
    setOosError(run.oos_error || null);
  }, [run]);

  useEffect(() => {
    if (!runId) return;
    if (!['running', 'pending'].includes(oosStatus)) return;
    let cancelled = false;
    const poll = setInterval(async () => {
      try {
        const res = await tradingService.getOptimizationStatus(runId);
        if (cancelled) return;
        const d = res.data;
        setOosStatus(d.oos_status || null);
        setOosProgress(d.oos_progress || null);
        setOosError(d.oos_error || null);
        if (!['running', 'pending'].includes(d.oos_status)) clearInterval(poll);
      } catch (_) {}
    }, 5000);
    return () => { cancelled = true; clearInterval(poll); };
  }, [runId, oosStatus]);

  // Fetch NSR status on mount + poll while running. Stops polling once
  // status settles (complete/failed/null).
  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    let intervalId = null;
    const fetchNsr = async () => {
      try {
        const res = await tradingService.getOptimizationNSR(runId);
        if (cancelled) return;
        setNsr(res.data);
        // Auto-refresh combos when NSR finishes so the new columns populate.
        if (res.data.status === 'complete' && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
          fetchCombos();
        }
      } catch (e) {
        if (!cancelled) setNsr(n => ({ ...n, error: e.message }));
      }
    };
    fetchNsr();
    intervalId = setInterval(() => {
      if (['running', 'pending'].includes(nsr.status)) fetchNsr();
    }, 4000);
    return () => { cancelled = true; if (intervalId) clearInterval(intervalId); };
    // eslint-disable-next-line
  }, [runId, nsr.status]);

  const triggerNsr = async () => {
    if (nsrTriggering) return;
    setNsrTriggering(true);
    try {
      await tradingService.computeOptimizationNSR(runId, { metric: run?.maximize, lambda: 0.5 });
      // Optimistic flip — the backend has already set pending, but updating
      // local state immediately gives the button a visual response.
      setNsr(n => ({ ...n, status: 'pending', error: null }));
    } catch (e) {
      setNsr(n => ({ ...n, error: e.response?.data?.error || e.message }));
    } finally {
      setNsrTriggering(false);
    }
  };

  const triggerOos = async () => {
    if (oosTriggering) return;
    setOosTriggering(true);
    try {
      await tradingService.runOptimizationOos(runId);
      setOosStatus('pending');
      setOosError(null);
    } catch (e) {
      setOosError(e.response?.data?.error || e.message);
    } finally {
      setOosTriggering(false);
    }
  };

  const triggerFullPipeline = async () => {
    if (pipelineTriggering) return;
    setPipelineTriggering(true);
    setPipelineError(null);
    setPipelineQueued(false);
    try {
      const res = await tradingService.runFullPipeline(runId);
      const data = res.data;
      if (data.stages_pending && data.stages_pending.length === 0) {
        // Already complete
        setPipelineStages([]);
      } else {
        setPipelineStages(data.stages_pending || []);
        setPipelineQueued(true);
        // Optimistic: if 'nsr' is in stages, flip NSR to pending
        if (data.stages_pending?.includes('nsr')) {
          setNsr(n => ({ ...n, status: 'pending', error: null }));
        }
        // If 'oos' is in stages, flip OOS to pending
        if (data.stages_pending?.includes('oos')) {
          setOosStatus('pending');
          setOosError(null);
        }
      }
    } catch (e) {
      setPipelineError(e.response?.data?.error || e.message || 'Pipeline dispatch failed');
    } finally {
      setPipelineTriggering(false);
    }
  };

  // OOS validate — runs selected combos (or top-10) on the held-out OOS window.
  const [oosValidating, setOosValidating] = useState(false);
  const [oosResult, setOosResult] = useState(null); // { validated, oos_window, errors }

  const validateOos = async (topN = null) => {
    if (oosValidating) return;
    setOosValidating(true);
    setOosResult(null);
    try {
      const body = topN ? { top_n: topN } : { combo_ids: [...selected].map(idx => {
        const c = combos.find(x => x._combo_index === idx);
        return c?._id;
      }).filter(Boolean) };
      const res = await tradingService.oosValidate(runId, body);
      setOosResult(res.data);
      // Refresh combos to show OOS columns
      fetchCombos();
    } catch (e) {
      setOosResult({ error: e.response?.data?.error || e.message });
    } finally {
      setOosValidating(false);
    }
  };

  const paramOptions = useMemo(() => {
    if (!run?.results || run.results.length === 0) return {};
    const opts = {};
    for (const c of run.results.slice(0, 2000)) {
      const p = c.parameters || {};
      for (const [k, v] of Object.entries(p)) {
        if (!opts[k]) opts[k] = new Set();
        opts[k].add(v);
      }
    }
    const sorted = {};
    for (const [k, set] of Object.entries(opts)) {
      sorted[k] = Array.from(set).sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') return a - b;
        return String(a).localeCompare(String(b));
      });
    }
    return sorted;
  }, [run]);

  const fetchCombos = async () => {
    // combos_streamed runs have millions of combos — never load without filters.
    // The NSR tab makes its own focused top-10 requests independently.
    if (run?.combos_streamed) return;
    setLoading(true);
    setError(null);
    try {
      const body = { limit: 1000, offset: 0, sort: filters.sort };
      ['min_sharpe', 'min_blended_sharpe', 'min_return', 'max_drawdown_abs',
       'min_num_trades', 'min_win_rate', 'min_profit_factor',
       'min_degradation_ratio', 'min_oos_blended_sharpe'].forEach(k => {
        if (filters[k] !== '' && filters[k] != null) {
          const v = parseFloat(filters[k]);
          if (!isNaN(v)) body[k] = v;
        }
      });
      const pf = {};
      for (const [k, set] of Object.entries(paramFilters)) {
        if (set.size > 0 && set.size < (paramOptions[k]?.length || 0)) {
          pf[k] = { in: Array.from(set) };
        }
      }
      if (Object.keys(pf).length) body.param_filters = pf;
      const res = await tradingService.filterOptimizationCombos(runId, body);
      setCombos(res.data.combos || []);
      setTotalMatching(res.data.total_matching || 0);
      setTotalInRun(res.data.total_in_run || 0);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // combos_streamed runs have 5K–12M combos — never auto-fetch with empty
    // filters (would load the entire S3 parquet or scan the full DB table,
    // OOM-ing the backend gunicorn process and taking down the EC2).
    // The NSR tab makes its own focused requests (top-10, skip_count=true).
    if (run?.combos_streamed) return;
    if (run && run.status === 'complete') fetchCombos();
    // eslint-disable-next-line
  }, [runId, run?.status, filters, paramFilters]);

  const toggleSelected = (idx) => {
    const next = new Set(selected);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    setSelected(next);
  };
  const selectAllVisible = () => {
    setSelected(new Set(combos.map(c => c._combo_index).filter(i => i != null)));
  };
  const clearSelection = () => setSelected(new Set());

  const toggleParamValue = (param, val) => {
    setParamFilters(prev => {
      const next = { ...prev };
      const cur = new Set(next[param] || paramOptions[param] || []);
      if (cur.has(val)) cur.delete(val); else cur.add(val);
      next[param] = cur;
      return next;
    });
  };

  const resetFilters = () => {
    setFilters({
      min_sharpe: '', min_blended_sharpe: '', min_return: '',
      max_drawdown_abs: '', min_num_trades: '', min_win_rate: '',
      min_profit_factor: '',
      min_degradation_ratio: '', min_oos_blended_sharpe: '',
      sort: '-sharpe_ratio',
    });
    setParamFilters({});
  };

  const cancelDetailRun = async () => {
    if (cancellingDetail) return;
    setCancellingDetail(true);
    setCancelDetailError(null);
    try {
      await tradingService.cancelOptimization(runId);
      // Reflect the new status locally so UI updates instantly
      setRun(r => r ? { ...r, status: 'failed' } : r);
      setCancelConfirming(false);
    } catch (e) {
      setCancelDetailError(e.response?.data?.error || e.message || 'Cancel failed');
    } finally {
      setCancellingDetail(false);
    }
  };

  const hasIdle = run?.idle_deployment && run.idle_deployment !== 'cash';
  const br = run?.best_result || {};

  // ── B&H benchmark window MUST match the strategy-KPI window ────────────────
  // The headline "Best *" KPIs below come from best_result. For runs finalized
  // after the full-sample feature these are the FULL IS+OOS pick (br._is_full_sample),
  // recomputed over start_date→data_end_date. The legacy run.bh_* scalars, however,
  // are IS-ONLY whenever the run reserved an OOS hold-out — tasks.py trims base_data
  // to is_end_date BEFORE the finalize B&H, so run.bh_total_return covers only the IS
  // window. Pairing a full-sample strategy return with an IS-only B&H silently
  // understates the passive floor (WDC #585: strategy +2798% full vs B&H +40% IS-only,
  // when the true full-sample B&H is +1542%). full_sample_metrics carries the
  // correctly-windowed B&H (bh_symbol/bh_idle, computed over the same full window at
  // NSR-finalize); prefer it when the KPIs are full-sample, else fall back to legacy.
  const fsm = run?.full_sample_metrics || null;
  const useFullBh = !!(br._is_full_sample && fsm && fsm.bh_symbol);
  const bhSym = useFullBh ? fsm.bh_symbol : null;
  const bhIdleFs = useFullBh ? (fsm.bh_idle || null) : null;
  // Window label so a full-sample vs IS-only benchmark is never ambiguous again.
  const bhWinSfx = useFullBh ? ', Full' : (run?.oos_split_pct > 0 ? ', IS' : '');
  const bhReturn = bhSym ? bhSym.total_return : run?.bh_total_return;
  const bhSharpe = bhSym ? bhSym.sharpe_ratio : run?.bh_sharpe_ratio;
  const bhMaxDd  = bhSym ? bhSym.max_drawdown : run?.bh_max_drawdown;
  const bhIdleReturn = bhIdleFs ? bhIdleFs.total_return : run?.bh_idle_total_return;
  const bhIdleSharpe = bhIdleFs ? bhIdleFs.sharpe_ratio : run?.bh_idle_sharpe_ratio;
  const bhIdleMaxDd  = bhIdleFs ? bhIdleFs.max_drawdown : run?.bh_idle_max_drawdown;

  if (!run) {
    return (
      <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">
        {error ? <span className="text-red-500">{error}</span> : 'Loading run…'}
      </div>
    );
  }

  return (
    <div className="px-3 md:px-5 py-4 max-w-7xl mx-auto">
      <button onClick={onBack} className="text-xs text-gray-500 dark:text-gray-400 hover:text-brand-gold mb-2 inline-flex items-center gap-1">
        <Glyph name="chevron" className="w-3 h-3 rotate-180" /> Back to Optimizations
      </button>

      <header className="mb-4 pb-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex flex-wrap items-baseline gap-2 mb-2">
          <IdBadge id={run.id} kind={run.kind || 'standard'} />
          <h2 className="text-2xl font-serif text-gray-900 dark:text-gray-100">{run.symbol}</h2>
          <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">{run.strategy_name}</span>
          <IdleTag idle={run.idle_deployment} />
          {run.oos_split_pct > 0 && <OosTag oosSplitPct={run.oos_split_pct} isEndDate={run.is_end_date} />}
          <StatusBadge status={run.status} />
          {/* OOS full-grid run button. Only shown for runs with an OOS split
              that have completed IS — triggers run_oos_task on EC2. */}
          {run.combos_streamed && run.status === 'complete' && run.oos_split_pct > 0 && oosStatus !== 'complete' && (
            <OosButton
              oosStatus={oosStatus}
              oosProgress={oosProgress}
              oosError={oosError}
              triggering={oosTriggering}
              onTrigger={triggerOos}
            />
          )}
          {run.combos_streamed && run.status === 'complete' && run.oos_split_pct > 0 && oosStatus === 'complete' && (
            <OosButton oosStatus="complete" />
          )}
          {/* NSR (post-hoc robustness) action button. Only shown for streamed
              runs that have completed — NSR walks per-combo data. The button
              text + colour reflects the latest nsr_status. */}
          {run.combos_streamed && run.status === 'complete' && (
            <NsrButton
              status={nsr.status}
              progress={nsr.progress}
              error={nsr.error}
              triggering={nsrTriggering}
              onTrigger={triggerNsr}
              metric={nsr.metric}
              summary={nsr.summary}
            />
          )}
          {/* Full Pipeline button — one-click OOS → NSR → Leaderboards.
              Smart resume: detects which stages are still pending and queues
              only what's missing. Only for completed S3-streamed runs. */}
          {run.combos_streamed && run.status === 'complete' && (
            <FullPipelineButton
              stages={pipelineStages}
              triggering={pipelineTriggering}
              queued={pipelineQueued}
              error={pipelineError}
              onTrigger={triggerFullPipeline}
            />
          )}
          {/* Cancel button — only shown while run is in-progress */}
          {run.status === 'running' && (
            cancelConfirming ? (
              <span className="inline-flex items-center gap-1.5 text-xs">
                <span className="text-orange-500 dark:text-orange-400">Stop this run?</span>
                <button
                  onClick={cancelDetailRun}
                  disabled={cancellingDetail}
                  className="px-2 py-0.5 rounded bg-orange-500/90 hover:bg-orange-500 text-white text-[11px] font-medium disabled:opacity-50"
                >
                  {cancellingDetail ? 'Stopping…' : 'Yes, stop'}
                </button>
                <button
                  onClick={() => { setCancelConfirming(false); setCancelDetailError(null); }}
                  disabled={cancellingDetail}
                  className="px-2 py-0.5 rounded border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 text-[11px] disabled:opacity-50"
                >
                  Keep going
                </button>
                {cancelDetailError && (
                  <span className="text-red-500 text-[11px]">{cancelDetailError}</span>
                )}
              </span>
            ) : (
              <button
                onClick={() => setCancelConfirming(true)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-orange-400/60 text-orange-500 dark:text-orange-400 text-[11px] hover:bg-orange-500/10 transition-colors"
                title="Stop this optimization"
              >
                <Glyph name="x" className="w-3 h-3" /> Cancel run
              </button>
            )
          )}
          <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500 font-mono">
            {br.code_hash ? `code ${br.code_hash.slice(0, 8)}` : ''}
            {br.strategy_version ? ` · ${br.strategy_version}` : ''}
          </span>
        </div>

        {/* Best Strategy provenance badge — clarifies whether the headline KPIs
            below are from the deployable (post-NSR+alpha-degrad gates) pick on
            the FULL sample, or just the IS-ranked top. New runs (after the
            full-sample feature shipped) write br._source='pipeline_shortlist[0]'
            + br._is_full_sample=true at NSR-finalize. */}
        {(br._source || br._is_full_sample) && (
          <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]">
            {br._is_full_sample && (
              <span className="px-2 py-0.5 rounded bg-brand-gold/15 text-brand-gold border border-brand-gold/30 font-medium">
                Full IS+OOS sample
              </span>
            )}
            {br._source === 'pipeline_shortlist[0]' && (
              <span className="px-2 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/30 font-medium">
                ✓ Passes all filters (deployable)
              </span>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <KpiTile label="Best Sharpe" value={fmtNum(br.sharpe_ratio)} color={valColor(br.sharpe_ratio)} />
          <KpiTile label="Best Return" value={fmtPct(br.total_return)} color={valColor(br.total_return)} />
          <KpiTile label="Best Drawdown" value={fmtPct(br.max_drawdown)} color="text-red-400" />
          {/* A complete run processed every combo by definition — never show a
              drifted completed_combos (a churn-era coordinator can reset it to 0
              from a transient S3 slice listing). Trust total_combos when done. */}
          <KpiTile label="Combos" value={`${(run.status === 'complete' ? run.total_combos : run.completed_combos) || 0} / ${run.total_combos}`} color="text-gray-700 dark:text-gray-300" />
          {hasIdle && (
            <>
              <KpiTile label={`Sharpe w/ ${run.idle_deployment}`} value={fmtNum(br.blended_sharpe)} color={valColor(br.blended_sharpe)} accent />
              <KpiTile label={`Sortino w/ ${run.idle_deployment}`} value={fmtNum(br.blended_sortino)} color={valColor(br.blended_sortino)} accent />
              <KpiTile label={`Calmar w/ ${run.idle_deployment}`} value={fmtNum(br.blended_calmar)} color={valColor(br.blended_calmar)} accent />
              <KpiTile label={`Return w/ ${run.idle_deployment}`} value={fmtPct(br.blended_return)} color={valColor(br.blended_return)} accent />
              <KpiTile label={`Drawdown w/ ${run.idle_deployment}`} value={fmtPct(br.blended_max_drawdown)} color="text-red-400" accent />
              <KpiTile label="Best Trades" value={br.num_trades ?? '—'} color="text-gray-700 dark:text-gray-300" />
            </>
          )}
          {/* Buy-and-hold benchmark — WINDOWED TO MATCH the strategy KPIs above.
              Lets the user instantly see "did this strategy beat passive holding?"
              The best Sharpe / Return / DD KPIs above answer "best of all combos
              tested"; these answer "the floor every combo has to beat to be worth
              running". When the "Best *" KPIs are the full IS+OOS pick these read
              the full-sample B&H (full_sample_metrics.bh_symbol); otherwise they
              fall back to the legacy run.bh_* scalars (IS-only for OOS runs). The
              window is labelled in each tile (Full / IS) so a full-sample strategy
              return is never silently compared against an IS-only passive floor. */}
          {bhReturn != null && (
            <>
              <KpiTile label={`B&H Sharpe (${run.symbol}${bhWinSfx})`} value={fmtNum(bhSharpe)} color={valColor(bhSharpe)} />
              <KpiTile label={`B&H Return (${run.symbol}${bhWinSfx})`} value={fmtPct(bhReturn)} color={valColor(bhReturn)} />
              <KpiTile label={`B&H Drawdown (${run.symbol}${bhWinSfx})`} value={fmtPct(bhMaxDd)} color="text-red-400" />
            </>
          )}
          {bhIdleReturn != null && run.idle_deployment && run.idle_deployment !== 'cash' && (
            <>
              <KpiTile label={`B&H Sharpe (${run.idle_deployment}${bhWinSfx})`} value={fmtNum(bhIdleSharpe)} color={valColor(bhIdleSharpe)} />
              <KpiTile label={`B&H Return (${run.idle_deployment}${bhWinSfx})`} value={fmtPct(bhIdleReturn)} color={valColor(bhIdleReturn)} />
              <KpiTile label={`B&H Drawdown (${run.idle_deployment}${bhWinSfx})`} value={fmtPct(bhIdleMaxDd)} color="text-red-400" />
            </>
          )}
          {/* OOS reservation window — shown for runs with oos_split_pct > 0 */}
          {run.oos_split_pct > 0 && (
            <>
              <KpiTile
                label="IS Window (training)"
                value={`${run.start_date || '—'} → ${run.is_end_date || '—'}`}
                color="text-purple-500 dark:text-purple-400"
              />
              <KpiTile
                label={`OOS Window (${run.oos_split_pct}% hold-out)`}
                value={`${run.is_end_date || '—'} → ${run.end_date || 'latest'}`}
                color="text-purple-400 dark:text-purple-300"
              />
            </>
          )}
        </div>

        {br.parameters && Object.keys(br.parameters).length > 0 && (
          <div className="mt-2 flex items-center gap-2 overflow-x-auto scrollbar-thin">
            <span className="text-[10px] uppercase tracking-wider text-brand-gold whitespace-nowrap">Best params</span>
            <ParamChips params={br.parameters} />
          </div>
        )}
      </header>

      {/* NSR-only view for all runs — no tab nav needed for current workflow.
          Optimize → OOS → NSR → save best combo → deploy. That's it. */}
      {nsr.status === 'complete' ? (
        <NsrTab runId={run.id} run={run} nsr={nsr} combos={combos} totalInRun={totalInRun || run.completed_combos || 0} setFilters={setFilters} />
      ) : (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22] p-8 text-center text-gray-400 dark:text-gray-500 text-sm">
          {nsr.status === 'running' || nsr.status === 'pending'
            ? '⏳ NSR is running — results will appear here when ready.'
            : run.oos_status === 'complete'
              ? '🔬 OOS complete — click "NSR Retry" above to compute statistical ranking.'
              : run.status === 'complete'
                ? (run.combos_streamed
                    ? '▶ Click "Run OOS" above — full pipeline (OOS → NSR) runs on the spot worker.'
                    : '▶ Click "Run OOS" above to validate and rank results.')
                : '⏳ Optimization in progress…'}
        </div>
      )}

      {false && !run.combos_streamed && activeViz === 'table' && (
      /* min-w-0 on the implicit grid items below — without it the 1fr track
          has min-width:auto, so a table wider than the track would EXPAND
          the track instead of scrolling. The overflow-x-auto on the table
          container only triggers when the parent grid item has min-w:0. */
      <div className="grid grid-cols-1 lg:grid-cols-[280px,minmax(0,1fr)] gap-4">
        <aside className="space-y-3">
          <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#161b22]">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">IS Metric thresholds</div>
            <div className="space-y-2">
              <ThreshInput label="Min Sharpe" value={filters.min_sharpe} onChange={v => setFilters(f => ({ ...f, min_sharpe: v }))} step="0.1" />
              {hasIdle && <ThreshInput label={`Min ${run.idle_deployment} Blended Sharpe`} value={filters.min_blended_sharpe} onChange={v => setFilters(f => ({ ...f, min_blended_sharpe: v }))} step="0.1" />}
              <ThreshInput label="Min Return %" value={filters.min_return} onChange={v => setFilters(f => ({ ...f, min_return: v }))} step="1" />
              <ThreshInput label="Max |Drawdown| %" value={filters.max_drawdown_abs} onChange={v => setFilters(f => ({ ...f, max_drawdown_abs: v }))} step="1" />
              <ThreshInput label="Min Trades" value={filters.min_num_trades} onChange={v => setFilters(f => ({ ...f, min_num_trades: v }))} step="1" />
              <ThreshInput label="Min Win Rate %" value={filters.min_win_rate} onChange={v => setFilters(f => ({ ...f, min_win_rate: v }))} step="1" />
              <ThreshInput label="Min Profit Factor" value={filters.min_profit_factor} onChange={v => setFilters(f => ({ ...f, min_profit_factor: v }))} step="0.1" />
            </div>
          </div>

          {/* OOS pipeline filters — only meaningful after OOS task completes */}
          {run.oos_status === 'complete' && (
          <div className="p-3 rounded-lg border border-sky-200/60 dark:border-sky-800/40 bg-sky-50/60 dark:bg-sky-900/10">
            <div className="text-[10px] uppercase tracking-wider text-sky-600 dark:text-sky-400 mb-1">OOS filters</div>
            <div className="text-[9px] text-gray-500 dark:text-gray-500 mb-2">
              Degradation ≥ 0.5 means OOS performance ≥ 50% of IS.
            </div>
            <div className="space-y-2">
              <ThreshInput
                label="Min Degradation Ratio"
                value={filters.min_degradation_ratio}
                onChange={v => setFilters(f => ({ ...f, min_degradation_ratio: v }))}
                step="0.05"
              />
              {hasIdle && (
                <ThreshInput
                  label="Min OOS Blended Sharpe"
                  value={filters.min_oos_blended_sharpe}
                  onChange={v => setFilters(f => ({ ...f, min_oos_blended_sharpe: v }))}
                  step="0.1"
                />
              )}
            </div>
          </div>
          )}

          {Object.keys(paramOptions).length > 0 && (
            <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#161b22]">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Parameter values</div>
              <div className="space-y-2">
                {Object.entries(paramOptions).map(([param, vals]) => (
                  <div key={param}>
                    <div className="text-[10px] text-gray-600 dark:text-gray-400 mb-1 font-mono">{param}</div>
                    <div className="flex flex-wrap gap-1">
                      {vals.map(v => {
                        const set = paramFilters[param];
                        const allSelected = !set || set.size === 0 || set.size === vals.length;
                        const selectedHere = allSelected || set.has(v);
                        return (
                          <button
                            key={String(v)}
                            onClick={() => toggleParamValue(param, v)}
                            className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
                              selectedHere
                                ? 'border-brand-gold/60 bg-brand-gold/10 text-brand-gold'
                                : 'border-gray-300 dark:border-gray-700 text-gray-400 dark:text-gray-500'
                            }`}
                          >
                            {String(v)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between text-[11px]">
            <span className="text-gray-500 dark:text-gray-400 tabular-nums">
              {totalMatching.toLocaleString()} of {totalInRun.toLocaleString()} combos
            </span>
            <button onClick={resetFilters} className="text-brand-gold hover:underline">Reset</button>
          </div>
        </aside>

        <main>
          {/* ─── Sort + bulk-action bar ─────────────────────────────────
              Sort controls live in the column headers below (click any
              column to sort by it; click again to flip direction). The
              backend ORDER BY runs across the FULL grid (840K combos for
              opt #17) and returns the top 1000 — so sort is global, not
              just over visible rows. The "X of Y combos" counter shows
              the scope. */}
          <div className="flex flex-wrap items-center gap-2 mb-2 text-xs text-gray-600 dark:text-gray-400">
            <span className="tabular-nums">
              Showing top <span className="text-gray-900 dark:text-gray-100">{combos.length.toLocaleString()}</span>
              {totalMatching > combos.length && <span> of {totalMatching.toLocaleString()} matching</span>}
              <span className="text-gray-500 dark:text-gray-500"> · sorted by </span>
              <span className="text-brand-gold">{filters.sort.replace(/^-/, '').replace(/_/g, ' ')}</span>
              <span className="text-gray-500 dark:text-gray-500"> {filters.sort.startsWith('-') ? '↓' : '↑'} (full grid)</span>
            </span>
            {selected.size > 0 ? (
              <>
                <span className="ml-2 text-xs text-gray-600 dark:text-gray-400">· {selected.size} selected</span>
                <button onClick={clearSelection} className="text-[11px] text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Clear</button>
                {/* OOS validate selected combos — only shown when run has OOS reservation */}
                {run?.oos_split_pct > 0 && run.status === 'complete' && (
                  <button
                    onClick={() => validateOos(null)}
                    disabled={oosValidating}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded border border-purple-500/40 text-purple-600 dark:text-purple-400 hover:bg-purple-500/10 disabled:opacity-50"
                  >
                    {oosValidating ? '⟳ Validating…' : `OOS validate ${selected.size}`}
                  </button>
                )}
                <button
                  onClick={() => setShowSaveModal(true)}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-gold text-[#0d1117] rounded font-medium hover:bg-brand-gold-light"
                >
                  <Glyph name="check" className="w-3.5 h-3.5" />
                  Save {selected.size} to Analytics
                </button>
              </>
            ) : (
              <div className="ml-auto flex items-center gap-2">
                {/* Top-10 OOS validate quick action */}
                {run?.oos_split_pct > 0 && run.status === 'complete' && (
                  <button
                    onClick={() => validateOos(10)}
                    disabled={oosValidating}
                    className="text-[11px] px-2 py-1 rounded border border-purple-500/30 text-purple-500 dark:text-purple-400 hover:bg-purple-500/10 disabled:opacity-50"
                  >
                    {oosValidating ? '⟳ Validating…' : 'OOS validate top 10'}
                  </button>
                )}
                <button onClick={selectAllVisible} className="text-[11px] text-gray-500 hover:text-brand-gold">
                  Select all visible
                </button>
              </div>
            )}
            {oosResult && !oosResult.error && (
              <div className="w-full mt-1 text-[11px] text-purple-600 dark:text-purple-400">
                ✓ OOS validated {oosResult.validated} combo{oosResult.validated !== 1 ? 's' : ''} on {oosResult.oos_window?.start} → {oosResult.oos_window?.end} ({oosResult.oos_bars} bars)
                {oosResult.errors?.length > 0 && ` · ${oosResult.errors.length} failed`}
              </div>
            )}
            {oosResult?.error && (
              <div className="w-full mt-1 text-[11px] text-red-500">{oosResult.error}</div>
            )}
          </div>

          {error && (
            <div className="px-3 py-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded mb-2">
              {error}
            </div>
          )}

          {/* Hint above the table — discoverable indicator that there are
              more columns to the right. macOS auto-hides system scrollbars
              even with custom CSS, so without this many users miss the
              extra columns entirely. */}
          <div className="text-[10px] text-gray-500 dark:text-gray-500 mb-1 flex items-center gap-1">
            <Glyph name="chevron" className="w-3 h-3 rotate-180 opacity-40" />
            <span>scroll horizontally for more columns (Sortino, Calmar, Blended Sharpe/Return/DD)</span>
            <Glyph name="chevron" className="w-3 h-3 opacity-40" />
          </div>
          <div className="overflow-x-auto scrollbar-table border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-[#161b22]">
            {/* w-max sizes the table to its NATURAL content width (sum of
                column widths) instead of compressing to fit the container.
                Combined with parent overflow-x-auto, that guarantees a
                scrollbar appears whenever the natural table is wider than
                the container — works on any screen size, regardless of
                user's zoom or sidebar width. min-w-full keeps the table at
                least as wide as the container on very narrow screens (so
                it doesn't shrink awkwardly when there are few columns). */}
            <table className="w-max min-w-full text-[11px]">
              <thead className="bg-gray-50 dark:bg-gray-800/40 text-gray-500 uppercase tracking-wider sticky top-0 z-10 whitespace-nowrap">
                <tr>
                  <th className="px-2 py-2 w-8"></th>
                  <th className="px-2 py-2 text-left">#</th>
                  <th className="px-2 py-2 text-left">Params</th>
                  <SortableTh field="sharpe_ratio"  filters={filters} setFilters={setFilters}>Sharpe</SortableTh>
                  <SortableTh field="total_return"  filters={filters} setFilters={setFilters}>Return</SortableTh>
                  <SortableTh field="max_drawdown"  filters={filters} setFilters={setFilters} ascendingDefault>MaxDD</SortableTh>
                  <SortableTh field="num_trades"    filters={filters} setFilters={setFilters}>Trades</SortableTh>
                  <SortableTh field="win_rate"      filters={filters} setFilters={setFilters}>Win%</SortableTh>
                  <SortableTh field="profit_factor" filters={filters} setFilters={setFilters}>PF</SortableTh>
                  <SortableTh field="sortino_ratio" filters={filters} setFilters={setFilters}>Sortino</SortableTh>
                  <SortableTh field="calmar_ratio"  filters={filters} setFilters={setFilters}>Calmar</SortableTh>
                  <SortableTh field="blended_sharpe"       filters={filters} setFilters={setFilters} blue>Bl Sh</SortableTh>
                  <SortableTh field="blended_sortino"      filters={filters} setFilters={setFilters} blue>Bl Sort</SortableTh>
                  <SortableTh field="blended_calmar"       filters={filters} setFilters={setFilters} blue>Bl Cal</SortableTh>
                  <SortableTh field="blended_return"       filters={filters} setFilters={setFilters} blue>Bl Ret</SortableTh>
                  <SortableTh field="blended_max_drawdown" filters={filters} setFilters={setFilters} blue ascendingDefault>Bl DD</SortableTh>
                  {nsr.status === 'complete' && (
                    <>
                      <SortableTh field="nsr_score"         filters={filters} setFilters={setFilters}>NSR</SortableTh>
                      <SortableTh field="nsr_neighbor_std"  filters={filters} setFilters={setFilters} ascendingDefault>NbrStd</SortableTh>
                      <th className="px-2 py-2 text-right">Nbrs</th>
                    </>
                  )}
                  {/* OOS columns — shown when the run has OOS reservation AND at least
                      one combo on this page has been validated (oos_sharpe_ratio set). */}
                  {run?.oos_split_pct > 0 && combos.some(x => x.oos_sharpe_ratio != null) && (
                    <>
                      <SortableTh field="oos_sharpe_ratio"     filters={filters} setFilters={setFilters} blue>OOS Sh</SortableTh>
                      <SortableTh field="oos_total_return"     filters={filters} setFilters={setFilters} blue>OOS Ret</SortableTh>
                      <SortableTh field="oos_max_drawdown"     filters={filters} setFilters={setFilters} blue ascendingDefault>OOS DD</SortableTh>
                      <th className="px-2 py-2 text-right text-blue-400">OOS Tr</th>
                      {combos.some(x => x.oos_degradation_ratio != null) && (
                        <SortableTh field="oos_degradation_ratio" filters={filters} setFilters={setFilters} blue title="OOS÷IS blended Sharpe. ≥0.5 = OOS performance ≥50% of IS. Pipeline filter threshold.">Degrad</SortableTh>
                      )}
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800/70">
                {combos.map((c, i) => {
                  const idx = c._combo_index;
                  const isSelected = idx != null && selected.has(idx);
                  return (
                    <tr
                      key={idx ?? i}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer ${isSelected ? 'bg-brand-gold/5' : ''}`}
                      onClick={() => idx != null && toggleSelected(idx)}
                    >
                      <td className="px-2 py-1 text-center">
                        <input type="checkbox" checked={isSelected} readOnly className="accent-brand-gold cursor-pointer" />
                      </td>
                      <td className="px-2 py-1 text-gray-500 tabular-nums">{i + 1}</td>
                      <td className="px-2 py-1"><ParamChips params={c.parameters || {}} max={5} /></td>
                      <td className={`px-2 py-1 text-right tabular-nums font-mono ${valColor(c.sharpe_ratio)}`}>{fmtNum(c.sharpe_ratio)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-mono ${valColor(c.total_return)}`}>{fmtPct(c.total_return)}</td>
                      <td className="px-2 py-1 text-right tabular-nums font-mono text-red-400">{fmtPct(c.max_drawdown)}</td>
                      <td className="px-2 py-1 text-right tabular-nums font-mono text-gray-700 dark:text-gray-300">{c.num_trades ?? '—'}</td>
                      <td className="px-2 py-1 text-right tabular-nums font-mono text-gray-700 dark:text-gray-300">{c.win_rate != null ? c.win_rate.toFixed(0) : '—'}</td>
                      <td className="px-2 py-1 text-right tabular-nums font-mono text-gray-700 dark:text-gray-300">{c.profit_factor != null ? c.profit_factor.toFixed(2) : '—'}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-mono ${valColor(c.sortino_ratio)}`}>{fmtNum(c.sortino_ratio)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-mono ${valColor(c.calmar_ratio)}`}>{fmtNum(c.calmar_ratio)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-mono ${c.blended_sharpe != null ? valColor(c.blended_sharpe) : 'text-gray-500'}`}>{fmtNum(c.blended_sharpe)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-mono ${c.blended_sortino != null ? valColor(c.blended_sortino) : 'text-gray-500'}`}>{fmtNum(c.blended_sortino)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-mono ${c.blended_calmar != null ? valColor(c.blended_calmar) : 'text-gray-500'}`}>{fmtNum(c.blended_calmar)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-mono ${c.blended_return != null ? valColor(c.blended_return) : 'text-gray-500'}`}>{fmtPct(c.blended_return)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-mono ${c.blended_max_drawdown != null ? 'text-red-400' : 'text-gray-500'}`}>{fmtPct(c.blended_max_drawdown)}</td>
                      {nsr.status === 'complete' && (
                        <>
                          <td className={`px-2 py-1 text-right tabular-nums font-mono ${c.nsr_score != null ? valColor(c.nsr_score) : 'text-gray-500'}`}>{fmtNum(c.nsr_score)}</td>
                          <td className="px-2 py-1 text-right tabular-nums font-mono text-gray-700 dark:text-gray-300">{fmtNum(c.nsr_neighbor_std)}</td>
                          <td className="px-2 py-1 text-right tabular-nums font-mono text-gray-500">{c.nsr_neighbor_count ?? '—'}</td>
                        </>
                      )}
                      {/* OOS columns — cells shown only when corresponding headers are shown */}
                      {run?.oos_split_pct > 0 && combos.some(x => x.oos_sharpe_ratio != null) && (
                        <>
                          <td className={`px-2 py-1 text-right tabular-nums font-mono ${c.oos_sharpe_ratio != null ? valColor(c.oos_sharpe_ratio) : 'text-gray-400'}`}>{c.oos_sharpe_ratio != null ? fmtNum(c.oos_sharpe_ratio) : '—'}</td>
                          <td className={`px-2 py-1 text-right tabular-nums font-mono ${c.oos_total_return != null ? valColor(c.oos_total_return) : 'text-gray-400'}`}>{c.oos_total_return != null ? fmtPct(c.oos_total_return) : '—'}</td>
                          <td className={`px-2 py-1 text-right tabular-nums font-mono ${c.oos_max_drawdown != null ? 'text-red-400' : 'text-gray-400'}`}>{c.oos_max_drawdown != null ? fmtPct(c.oos_max_drawdown) : '—'}</td>
                          <td className="px-2 py-1 text-right tabular-nums font-mono text-gray-500">{c.oos_num_trades ?? '—'}</td>
                          {combos.some(x => x.oos_degradation_ratio != null) && (
                            <td
                              title={c.oos_degradation_ratio != null ? `OOS÷IS: ${c.oos_degradation_ratio.toFixed(3)}` : 'Degradation not yet computed'}
                              className={`px-2 py-1 text-right tabular-nums font-mono ${
                                c.oos_degradation_ratio == null ? 'text-gray-400'
                                : c.oos_degradation_ratio >= 0.5 ? 'text-emerald-500 dark:text-emerald-400'
                                : c.oos_degradation_ratio >= 0   ? 'text-amber-500 dark:text-amber-400'
                                : 'text-red-500 dark:text-red-400'
                              }`}
                            >
                              {c.oos_degradation_ratio != null ? c.oos_degradation_ratio.toFixed(2) : '—'}
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {loading && <div className="text-center py-3 text-xs text-gray-500">Filtering…</div>}
            {!loading && combos.length === 0 && (
              <div className="text-center py-6 text-xs text-gray-500">No combos match these filters.</div>
            )}
          </div>
        </main>
      </div>
      )}

      {showSaveModal && (
        <SaveCombosModal
          run={run}
          selectedIndexes={Array.from(selected)}
          onClose={() => setShowSaveModal(false)}
          onSaved={() => { setShowSaveModal(false); clearSelection(); }}
        />
      )}
    </div>
  );
};

const SaveCombosModal = ({ run, selectedIndexes, onClose, onSaved }) => {
  const [items, setItems] = useState(() =>
    selectedIndexes.map((idx) => {
      const c = (run.results || [])[idx] || {};
      return {
        run_id: run.id,
        combo_index: idx,
        name: `${run.symbol} ${run.strategy_name} #${run.id}-${idx}`,
        description: '',
        sharpe: c.sharpe_ratio,
        return_pct: c.total_return,
      };
    })
  );
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = items.map(({ sharpe: _s, return_pct: _r, ...rest }) => rest);
      const res = await tradingService.saveOptimizationCombos(payload);
      setResults(res.data);
      if (res.data.error_count === 0) setTimeout(onSaved, 1500);
    } catch (e) {
      setResults({ error: e.response?.data?.error || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="my-8 w-full max-w-2xl rounded-lg bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">Save {items.length} combo{items.length === 1 ? '' : 's'} to Analytics</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none">✕</button>
        </div>
        <div className="p-4 max-h-[60vh] overflow-y-auto scrollbar-thin">
          {results ? (
            <div className="space-y-2">
              {results.error && <div className="text-sm text-red-500">{results.error}</div>}
              {(results.saved || []).map((r, i) => (
                <div key={i} className={`text-xs px-2 py-1.5 rounded ${r.success ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-red-500/10 text-red-700 dark:text-red-400'}`}>
                  {r.success ? `✓ Saved "${r.name}" (id ${r.strategy_id})` : `✗ Combo ${r.combo_index}: ${r.error}`}
                </div>
              ))}
              {results.success_count > 0 && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Saved {results.success_count} of {items.length}. Visit the Analytics tab to see them.
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item, i) => (
                <div key={i} className="p-3 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                  <div className="flex items-center justify-between mb-2 text-[11px]">
                    <span className="text-gray-500 dark:text-gray-400">
                      Combo #{item.combo_index} · Sharpe {fmtNum(item.sharpe)} · Return {fmtPct(item.return_pct)}
                    </span>
                  </div>
                  <input
                    value={item.name}
                    onChange={(e) => setItems(arr => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                    placeholder="Name"
                    className="w-full px-2 py-1.5 mb-1 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                  />
                  <input
                    value={item.description}
                    onChange={(e) => setItems(arr => arr.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                    placeholder="Description (optional)"
                    className="w-full px-2 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {!results && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-700">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 text-xs bg-brand-gold text-gray-900 rounded font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : `Save ${items.length}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// LAYOUT — switches between list and detail; toast surface
// ─────────────────────────────────────────────────────────────────────────

const OptimizationsLayout = () => {
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2800); };

  return (
    <div>
      {selectedRunId
        ? <RunDetail runId={selectedRunId} onBack={() => setSelectedRunId(null)} />
        : <OptimizationsList
            onSelectRun={setSelectedRunId}
            showToast={showToast}
          />}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-lg bg-[#161b22] border border-brand-gold/40 text-brand-gold text-xs font-mono shadow-[0_8px_30px_rgba(201,169,110,0.18)] opt-bulk-in">
          {toast}
        </div>
      )}
    </div>
  );
};

export default OptimizationsLayout;
